import ServerImageSelectors from '../components/ServerImageSelectors';
import AccountServerImagesPanel from '../components/AccountServerImagesPanel';
import type { ServerImage } from '../types/serverImages';
import { uploadDuoInstaller, type DuoInstallerUploadResult as DuoInstallerStatusResponse } from '../utils/duoInstallerUpload';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost } from '@shared/api/httpClient';
import type { ClientRecord, ClientResponse, ClientsResponse, ClientUserRecord, ClientUsersResponse, CreateClientRequest, CreateClientResponse, CreateClientUserResponse, DeleteClientUserResponse, ClientStatusUpdateResponse, RunClientsResponse, ClientWorkflowState, WorkSpacesUserRecord, WorkSpacesUsersResponse, UpsertWorkSpacesUserResponse, DeleteWorkSpacesUserResponse, DeploymentOperationalStatus, DeploymentOperationalStatusResponse, DeploymentProxyStatus, DuoDeploymentConfiguration, ManagedAwsAccount, ManagedAwsAccountResponse, ManagedAwsAccountsResponse } from '@shared/types/api';
import { getRuntimeConfig } from '@shared/config/runtime';
import { getSignedInUserDisplayName } from '@shared/auth/cognito';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { friendlyLabel } from '@shared/utils/friendlyLabels';
import { displayNameForIdentity, notificationContactSummary, redactEmailsDeep } from '@shared/utils/privacy';
import { statusPillClassName } from '@/utils/statusPill';

function validDuoImageParameter(value: string): boolean {
  const parameter = value.trim();
  return parameter === '/aws/service/ami-windows-latest/Windows_Server-2025-English-Full-Base'
    || /^\/surelinc\/server-images\/duo-proxy\/[A-Za-z0-9][A-Za-z0-9_.-]*$/.test(parameter);
}

type SearchMode = 'exact' | 'contains' | 'beginsWith';
type ClientMode = 'create' | 'edit';
type OverrideChoice = 'inherit' | 'enabled' | 'disabled';
type DeploymentDefaultKey = keyof CmmcDeploymentProfileDefaults;
type VpcEndpointOverrideField =
  | 'vpcEndpointGatewayS3'
  | 'vpcEndpointGatewayDynamoDb'
  | 'vpcEndpointInterfaceSsm'
  | 'vpcEndpointInterfaceSsmMessages'
  | 'vpcEndpointInterfaceEc2Messages'
  | 'vpcEndpointInterfaceKms'
  | 'vpcEndpointInterfaceLogs'
  | 'vpcEndpointInterfaceMonitoring'
  | 'vpcEndpointInterfaceEc2'
  | 'vpcEndpointInterfaceSts'
  | 'vpcEndpointInterfaceSecretsManager'
  | 'vpcEndpointInterfaceEcrApi'
  | 'vpcEndpointInterfaceEcrDkr'
  | 'vpcEndpointInterfaceWorkSpaces'
  | 'vpcEndpointInterfaceInspector2Telemetry'
  | 'vpcEndpointInterfaceWorkSpacesWeb';
type VpcEndpointDefinition = {
  field: VpcEndpointOverrideField;
  defaultKey: DeploymentDefaultKey;
  label: string;
  help: string;
  requiredByIdentityProtection?: boolean;
};
type DirectoryTypeChoice = 'aws_managed_microsoft_ad' | 'simple_ad';
type DirectoryEditionChoice = 'standard' | 'enterprise';
type WorkSpacesIdentityProvider = 'duo' | 'okta' | 'microsoft_entra_id';
type WorkSpacesIdentityDeploymentStage = 'STATIC_ONLY' | 'PROXY_ONLY' | 'SAML_ACTIVATION';
type DuoEditionChoice = 'ESSENTIALS' | 'ADVANTAGE' | 'PREMIER';
type DuoMetadataSourceMode = 'managed_upload' | 's3_uri' | 'https_url';
type DuoInstallerSourceMode = 'managed_upload' | 's3_uri';
type DuoInstallerUploadStatus = '' | 'UPLOADING' | 'SCANNING' | 'APPROVED' | 'BLOCKED' | 'FAILED';
type IdentityReadinessStatus = 'PASS' | 'BLOCKED' | 'PENDING' | 'WARNING';
type IdentityReadinessCheck = {
  id: string;
  label: string;
  status: IdentityReadinessStatus;
  message: string;
};

const GATEWAY_ENDPOINT_DEFINITIONS: readonly VpcEndpointDefinition[] = [
  {
    field: 'vpcEndpointGatewayS3',
    defaultKey: 'endpointGatewayS3',
    label: 'Amazon S3',
    help: 'No endpoint hourly charge. Keeps S3 traffic on AWS routes and avoids NAT processing for attached route tables.',
  },
  {
    field: 'vpcEndpointGatewayDynamoDb',
    defaultKey: 'endpointGatewayDynamoDb',
    label: 'Amazon DynamoDB',
    help: 'No endpoint hourly charge. Enable for private workloads that access DynamoDB.',
  },
];

const INTERFACE_ENDPOINT_DEFINITIONS: readonly VpcEndpointDefinition[] = [
  { field: 'vpcEndpointInterfaceSsm', defaultKey: 'endpointInterfaceSsm', label: 'Systems Manager API', help: 'Enable with private SSM-managed hosts or WorkSpaces.' },
  { field: 'vpcEndpointInterfaceSsmMessages', defaultKey: 'endpointInterfaceSsmMessages', label: 'SSM Messages', help: 'Required for modern SSM Agent, Session Manager, and Run Command without NAT.' },
  { field: 'vpcEndpointInterfaceEc2Messages', defaultKey: 'endpointInterfaceEc2Messages', label: 'EC2 Messages', help: 'Legacy SSM Agent compatibility only; leave disabled when current agents are enforced.' },
  { field: 'vpcEndpointInterfaceKms', defaultKey: 'endpointInterfaceKms', label: 'AWS KMS', help: 'Enable only when private workloads must call KMS without NAT.' },
  { field: 'vpcEndpointInterfaceLogs', defaultKey: 'endpointInterfaceLogs', label: 'CloudWatch Logs', help: 'Enable only for private log delivery without NAT.' },
  { field: 'vpcEndpointInterfaceMonitoring', defaultKey: 'endpointInterfaceMonitoring', label: 'CloudWatch Metrics', help: 'Enable only for private PutMetricData and monitoring API calls without NAT.' },
  { field: 'vpcEndpointInterfaceEc2', defaultKey: 'endpointInterfaceEc2', label: 'Amazon EC2 API', help: 'Enable only for private automation that calls EC2 APIs without NAT.' },
  { field: 'vpcEndpointInterfaceSts', defaultKey: 'endpointInterfaceSts', label: 'AWS STS', help: 'Enable only when private workloads assume roles without NAT.' },
  { field: 'vpcEndpointInterfaceSecretsManager', defaultKey: 'endpointInterfaceSecretsManager', label: 'Secrets Manager', help: 'Enable only when private workloads retrieve secrets without NAT.' },
  { field: 'vpcEndpointInterfaceEcrApi', defaultKey: 'endpointInterfaceEcrApi', label: 'ECR API', help: 'Use with ECR Docker and S3 endpoints for private image pulls without NAT.' },
  { field: 'vpcEndpointInterfaceEcrDkr', defaultKey: 'endpointInterfaceEcrDkr', label: 'ECR Docker', help: 'Use with ECR API and S3 endpoints for private image pulls without NAT.' },
  { field: 'vpcEndpointInterfaceWorkSpaces', defaultKey: 'endpointInterfaceWorkSpaces', label: 'WorkSpaces API', help: 'For private WorkSpaces control-plane API calls; not required merely to run desktops.' },
  { field: 'vpcEndpointInterfaceInspector2Telemetry', defaultKey: 'endpointInterfaceInspector2Telemetry', label: 'Amazon Inspector telemetry', help: 'Required when the Identity Protection Package creates Duo Authentication Proxy instances so Inspector can report VM scan telemetry without NAT.', requiredByIdentityProtection: true },
  { field: 'vpcEndpointInterfaceWorkSpacesWeb', defaultKey: 'endpointInterfaceWorkSpacesWeb', label: 'WorkSpaces Secure Browser', help: 'Enable only when the separate WorkSpaces Secure Browser service is deployed.' },
];
type DuoSamlMetadataUploadResponse = {
  clientId?: string;
  accountId?: string;
  fileName?: string;
  s3Uri: string;
  stagedS3Uri?: string;
  sha256: string;
  versionId?: string;
  uploadId?: string;
  storage?: string;
  message?: string;
  metadataObjectVerified?: boolean;
  metadataObjectStatus?: string;
};
type DuoProxyInputDraft = {
  profileId: string;
  portalProxyName: string;
  authproxyConfig: string;
  generatedCommand: string;
};
type DuoProxyProfileStatus = {
  profileId: string;
  status: string;
  revision?: number;
  configuredAt?: string;
  configuredBy?: string;
  assignedInstanceId?: string;
  authproxyConfigFingerprint?: string;
  commandFingerprint?: string;
  authproxyConfigSections?: string[];
  portalProxyName?: string;
  pendingAction?: string;
};
type DuoProxyOnboardingResponse = {
  clientId?: string;
  configured: boolean;
  status: string;
  schemaVersion?: string;
  configuredAt?: string;
  configuredBy?: string;
  remoteIdentityKeyFingerprint?: string;
  authproxyConfigFingerprint?: string;
  authproxyConfigSections?: string[];
  commandFingerprint?: string;
  secretArn?: string;
  storage?: string;
  message?: string;
  proxyProfiles?: DuoProxyProfileStatus[];
  deploymentPlan?: DuoDeploymentPlan;
  retireProfileIds?: string[];
};

type DuoDeploymentPlan = {
  operation?: string;
  summary?: string;
  minimumCapacity?: number;
  desiredCapacity?: number;
  maximumCapacity?: number;
  concurrentProfileUpdates?: number;
  requiresFreshCommands?: boolean;
  freshCommandRequiredProfileIds?: string[];
  readyForDeployment?: boolean;
  readyForApproval?: boolean;
  atLeastOneProxyRemainsOperational?: boolean;
  stepCount?: number;
  freshInstall?: boolean;
  identityStackExists?: boolean | null;
};

const DUO_AUTHPROXY_CONFIG_TEMPLATE = `# SURELINC managed Duo Authentication Proxy configuration.
# Replace only the placeholder values below with the exact values copied from Duo.


[main]
# Run Duo connectivity checks whenever the proxy service starts.
# Keep true during initial deployment; set to false after validation if startup checks are no longer desired.
test_connectivity_on_startup=true

[sso]
# Remote Identity Key from Duo SSO > External Authentication Sources > Active Directory.
rikey=REPLACE_WITH_DUO_REMOTE_IDENTITY_KEY

[cloud]
# Directory Sync credentials downloaded from the Duo directory configuration.
ikey=REPLACE_WITH_DUO_DIRECTORY_SYNC_INTEGRATION_KEY
skey=REPLACE_WITH_DUO_DIRECTORY_SYNC_SECRET_KEY
api_host=REPLACE_WITH_DUO_API_HOSTNAME`;

const DUO_PROXY_INSTANCE_TYPES = [
  't3.small',
  't3.medium',
  't3.large',
  't3a.small',
  't3a.medium',
  't3a.large',
] as const;

function newDuoProxyInputDraft(
  index: number,
  authproxyConfig: string = DUO_AUTHPROXY_CONFIG_TEMPLATE,
): DuoProxyInputDraft {
  return {
    profileId: `proxy-${index}`,
    portalProxyName: `SURELINC WorkSpaces Authentication Proxy ${index}`,
    authproxyConfig,
    generatedCommand: '',
  };
}

type PendingDuoInstallerUpload = {
  clientId: string;
  accountId: string;
  uploadId: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  quarantineS3Uri?: string;
  status: DuoInstallerUploadStatus;
  savedAt: string;
};

const DUO_INSTALLER_SCAN_POLL_INTERVAL_MS = 5_000;
const DUO_INSTALLER_SCAN_POLL_ATTEMPTS = 120;
const DUO_INSTALLER_UPLOAD_SESSION_PREFIX = 'surelinc-duo-installer-upload:';

function duoInstallerUploadSessionKey(clientId: string, accountId: string): string {
  return `${DUO_INSTALLER_UPLOAD_SESSION_PREFIX}${clientId.trim()}:${accountId.trim()}`;
}

function storePendingDuoInstallerUpload(upload: PendingDuoInstallerUpload): void {
  try {
    window.sessionStorage.setItem(
      duoInstallerUploadSessionKey(upload.clientId, upload.accountId),
      JSON.stringify(upload),
    );
  } catch {
    // Session persistence is a convenience only. The server remains authoritative.
  }
}

function readPendingDuoInstallerUpload(clientId: string, accountId: string): PendingDuoInstallerUpload | null {
  try {
    const value = window.sessionStorage.getItem(duoInstallerUploadSessionKey(clientId, accountId));
    if (!value) return null;
    const parsed = JSON.parse(value) as PendingDuoInstallerUpload;
    if (!parsed || parsed.clientId !== clientId || parsed.accountId !== accountId || !parsed.uploadId || !parsed.sha256) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearPendingDuoInstallerUpload(clientId: string, accountId: string): void {
  try {
    window.sessionStorage.removeItem(duoInstallerUploadSessionKey(clientId, accountId));
  } catch {
    // Ignore storage cleanup failures.
  }
}
function accountRequestOptions(accountId: string) {
  const normalizedAccountId = accountId.trim();
  if (!/^\d{12}$/.test(normalizedAccountId)) {
    throw new Error('A valid 12-digit AWS account ID is required for this account-scoped operation.');
  }
  return { query: { accountId: normalizedAccountId } };
}

function clientAccountIdForRequest(client: ClientRecord | null | undefined): string {
  if (!client) return '';
  const clientConfig = (
    typeof client.clientConfig === 'object' && client.clientConfig !== null
      ? client.clientConfig
      : {}
  ) as Record<string, unknown>;
  return String(
    client.clientAccountId
      || client.primaryAccountId
      || clientConfig.client_account_id
      || clientConfig.clientAccountId
      || '',
  ).trim();
}

type ClientAccountRegistryRow = {
  client: ClientRecord;
  account: ManagedAwsAccount | null;
};

function clientAccountScopeKey(clientId: string, accountId: string): string {
  return `${clientId.trim()}:${accountId.trim()}`;
}

function clientRecordForAccountConfiguration(
  client: ClientRecord,
  account: ManagedAwsAccount,
): ClientRecord {
  return {
    ...client,
    clientAccountId: account.accountId,
    managementAccountId: account.managementAccountId,
    logArchiveAccountId: account.logArchiveAccountId,
    region: account.region,
    cmmcLevel: account.cmmcLevel,
    clientConfig: account.accountConfig ?? {},
    workspacesUsers: account.workspacesUsers ?? [],
    accountStatus: account.accountStatus,
    accountDisplayName: account.displayName,
  };
}

function compactOverrideTree(value: Record<string, unknown>): Record<string, unknown> {
  const compacted: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry === null || entry === undefined) continue;
    if (typeof entry === 'object' && entry !== null && !Array.isArray(entry)) {
      const nested = compactOverrideTree(entry as Record<string, unknown>);
      if (Object.keys(nested).length > 0) compacted[key] = nested;
      continue;
    }
    compacted[key] = entry;
  }
  return compacted;
}

function accountConfigFromForm(
  form: ClientFormState,
  currentConfig: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const config = structuredClone(currentConfig ?? {});
  const overrides = compactOverrideTree(buildOverrides(form));
  const currentOverrides = recordValue(currentConfig?.overrides);
  const backupWasManaged = nestedValue(currentOverrides, 'backup', 'managed') === true
    || nestedValue(currentOverrides, 'backup', 'enabled') === true;
  const backupEnabled = nestedValue(overrides, 'backup', 'enabled') === true;

  // Once Backup has been selected for management, keep the core recovery
  // resources under CloudFormation control when scheduling is later disabled.
  // This hidden lifecycle marker is intentionally not a separate portal field.
  if (backupWasManaged || backupEnabled) {
    setNested(overrides, ['backup', 'managed'], true);
  }

  return {
    ...config,
    client_id: form.clientId.trim(),
    client_account_id: form.clientAccountId.trim(),
    management_account_id: form.managementAccountId.trim(),
    log_archive_account_id: form.logArchiveAccountId.trim(),
    region: form.region.trim() || 'us-east-1',
    cmmc_level: form.cmmcLevel,
    overrides,
  };
}

type RemovalPolicyChoice = 'retain' | 'destroy';
type RunningModeChoice = 'AUTO_STOP' | 'ALWAYS_ON';
type ClientSecurityAlertDeliveryChannel = 'email' | 'webhook' | 'email_and_webhook';
type ClientSecurityAlertMinimumSeverity = 'HIGH' | 'CRITICAL';
type OrchestrationRunKind = 'full' | 'software' | 'evidence';

const ORCHESTRATION_RUN_OPTIONS: Record<OrchestrationRunKind, {
  requestedAction: string;
  workflowType: string;
  allLabel: string;
  queueingLabel: string;
  reasonAll: string;
  confirmAll: (activeCount: number) => string;
}> = {
  full: {
    requestedAction: 'RUN_EVIDENCE_WORKFLOW',
    workflowType: 'technical-baseline',
    allLabel: 'Full Workflow - All Active Accounts',
    queueingLabel: 'Queueing...',
    reasonAll: 'Admin requested full workflow run for all active AWS accounts from the Clients page.',
    confirmAll: (activeCount) => `Queue full workflow jobs for all active AWS accounts?${activeCount ? ` ${activeCount} active account(s) are visible on this page.` : ''} This runs software deployment first, then evidence package generation after deployment succeeds. Disabled and incomplete accounts will be skipped.`,
  },
  software: {
    requestedAction: 'RUN_CDK_DEPLOYMENT',
    workflowType: 'software-update',
    allLabel: 'Deploy Updates - All Accounts',
    queueingLabel: 'Deploying...',
    reasonAll: 'Admin requested software update deployment for all active AWS accounts from the Clients page.',
    confirmAll: (activeCount) => `Queue software update deployment jobs for all active AWS accounts?${activeCount ? ` ${activeCount} active account(s) are visible on this page.` : ''} This changes account infrastructure/software and will not build evidence packages. Disabled and incomplete accounts will be skipped.`,
  },
  evidence: {
    requestedAction: 'RUN_EVIDENCE_PACKAGE',
    workflowType: 'evidence-package',
    allLabel: 'Collect Evidence - All Accounts',
    queueingLabel: 'Collecting...',
    reasonAll: 'Admin requested evidence package only run for all active AWS accounts from the Clients page.',
    confirmAll: (activeCount) => `Queue evidence package-only jobs for all active AWS accounts?${activeCount ? ` ${activeCount} active account(s) are visible on this page.` : ''} No CDK/software deployment will run. Disabled and incomplete accounts will be skipped.`,
  },
};

type ClientUserFormState = {
  email: string;
  name: string;
  givenName: string;
  familyName: string;
  groupName: string;
};

type WorkSpacesProvisioningUserDraft = {
  userName: string;
  givenName: string;
  surname: string;
  email: string;
  bundleId: string;
  passwordSecretArn: string;
};


type WorkSpacesBundleOption = {
  value?: string;
  profile?: string;
  label: string;
  description?: string;
  bundleId?: string;
  computeType?: string;
  owner?: string;
  source?: string;
  operatingSystem?: string;
  protocol?: string;
  cpu?: string;
  memory?: string;
  gpu?: string;
  storage?: string;
  rootStorageGb?: number | string;
  userStorageGb?: number | string;
};

type WorkSpacesBundleCatalogState = {
  recommendedOptions: WorkSpacesBundleOption[];
  advancedOptions: WorkSpacesBundleOption[];
  cached?: boolean;
  lastRefreshedAt?: string;
  expiresAt?: string;
  loading: boolean;
  error: string | null;
  warning: string | null;
};

type WorkSpacesBundleOptionsResponse = {
  recommendedOptions?: WorkSpacesBundleOption[];
  advancedOptions?: WorkSpacesBundleOption[];
  options?: WorkSpacesBundleOption[];
  cached?: boolean;
  lastRefreshedAt?: string;
  expiresAt?: string;
  warning?: string;
  message?: string;
};

type ClientSearchState = {
  clientId: string;
  lifecycle: string;
  cmmcLevel: string;
  clientAccountId: string;
  search: string;
  searchMode: SearchMode;
  limit: number;
};

type ClientFormState = CreateClientRequest & {
  clientId: string;
  clientName: string;
  contactEmail: string;
  clientAccountId: string;
  managementAccountId: string;
  logArchiveAccountId: string;
  region: string;
  cmmcLevel: string;
  lifecycle: string;
  statusReason: string;
  deploymentModel: string;
  pipelineType: string;
  approvalModel: string;
  notificationEmails: string;
  clientUsersManagement: OverrideChoice;
  clientEvidenceWorkflow: OverrideChoice;
  reportGeneration: OverrideChoice;
  securityHubNotifications: OverrideChoice;
  securityHubNotificationEmail: string;
  securityHubNotificationDeliveryChannel: ClientSecurityAlertDeliveryChannel;
  securityHubNotificationWebhookSecretArn: string;
  securityHubNotificationNotifySurelinc: boolean;
  securityHubNotificationMinimumSeverity: ClientSecurityAlertMinimumSeverity;
  guardDuty: OverrideChoice;
  guardDutyRuntimeMonitoring: OverrideChoice;
  securityHub: OverrideChoice;
  inspector: OverrideChoice;
  vpc: OverrideChoice;
  vpcEndpointGatewayS3: OverrideChoice;
  vpcEndpointGatewayDynamoDb: OverrideChoice;
  vpcEndpointInterfaceSsm: OverrideChoice;
  vpcEndpointInterfaceSsmMessages: OverrideChoice;
  vpcEndpointInterfaceEc2Messages: OverrideChoice;
  vpcEndpointInterfaceKms: OverrideChoice;
  vpcEndpointInterfaceLogs: OverrideChoice;
  vpcEndpointInterfaceMonitoring: OverrideChoice;
  vpcEndpointInterfaceEc2: OverrideChoice;
  vpcEndpointInterfaceSts: OverrideChoice;
  vpcEndpointInterfaceSecretsManager: OverrideChoice;
  vpcEndpointInterfaceEcrApi: OverrideChoice;
  vpcEndpointInterfaceEcrDkr: OverrideChoice;
  vpcEndpointInterfaceWorkSpaces: OverrideChoice;
  vpcEndpointInterfaceInspector2Telemetry: OverrideChoice;
  vpcEndpointInterfaceWorkSpacesWeb: OverrideChoice;
  cuiStorage: OverrideChoice;
  cuiFilesAccess: OverrideChoice;
  cuiFileShare: OverrideChoice;
  cuiFileShareStorageCapacityGib: string;
  cuiFileShareThroughputCapacityMbps: string;
  cuiFileShareBackupRetentionDays: string;
  cuiFileShareDriveLetter: string;
  cuiFileShareShareName: string;
  cuiFileShareCreateSharedFolder: boolean;
  cuiFileShareCreateUserFolders: boolean;
  cuiFileShareRetainOnDelete: boolean;
  backup: OverrideChoice;
  ssm: OverrideChoice;
  ssmInventory: OverrideChoice;
  ssmPatchManager: OverrideChoice;
  directoryService: OverrideChoice;
  directoryType: DirectoryTypeChoice;
  directoryEdition: DirectoryEditionChoice;
  directoryName: string;
  directoryShortName: string;
  directoryRemovalPolicy: RemovalPolicyChoice;
  directoryAllowDestroy: boolean;
  directoryDestroyGuardEnabled: boolean;
  directoryFailDestroyIfRegistered: boolean;
  workspaces: OverrideChoice;
  workspacesRegistrationEnabled: boolean;
  workspacesProvisioning: OverrideChoice;
  workspacesProvisioningUsers: string;
  workspacesBundleType: string;
  workspacesRunningMode: RunningModeChoice;
  workspacesRootVolumeEncryption: boolean;
  workspacesUserVolumeEncryption: boolean;
  workspacesDirectoryTypeRequired: string;
  workspacesRemovalPolicy: RemovalPolicyChoice;
  workspacesAllowDestroy: boolean;
  workspacesIdentityFederation: OverrideChoice;
  workspacesIdentityDeploymentStage: WorkSpacesIdentityDeploymentStage;
  workspacesIdentityDeploymentEnabled: boolean;
  workspacesIdentityProviderConfigurationComplete: boolean;
  workspacesIdentityClientAuthorizationApproved: boolean;
  workspacesIdentityApprovalId: string;
  workspacesIdentityApprovedAt: string;
  workspacesIdentityApprovedBy: string;
  workspacesIdentityProvider: WorkSpacesIdentityProvider;
  workspacesIdentityIntegrationMode: 'saml_sso';
  workspacesIdentityAllowDirectoryLoginFallback: boolean;
  workspacesIdentityFallbackAcknowledged: boolean;
  workspacesIdentityTestUserName: string;
  duoEdition: DuoEditionChoice;
  duoLicensedUserCount: string;
  duoSubaccountId: string;
  duoApiHostname: string;
  duoApiHostnameRequired: boolean;
  duoUserAccessUrl: string;
  duoMetadataSourceMode: DuoMetadataSourceMode;
  duoMetadataUrl: string;
  duoMetadataS3Uri: string;
  duoMetadataStagedS3Uri: string;
  duoMetadataStagedVersionId: string;
  duoMetadataSha256: string;
  duoMetadataObjectVerified: boolean;
  duoMetadataObjectStatus: string;
  duoAuthenticationSourceConfigured: boolean;
  duoWorkspacesApplicationConfigured: boolean;
  duoUserAccessGranted: boolean;
  duoPermittedEmailDomains: string;
  duoEmailDomainVerified: boolean;
  duoProxyOnboardingConfigured: boolean;
  duoDeploymentPlanReady: boolean;
  duoDeploymentPlanMissingCommands: string;
  duoProxyInputs: DuoProxyInputDraft[];
  duoProxyEnrollmentSecretArn: string;
  duoServiceAccountSecretArn: string;
  duoInstallerSourceMode: DuoInstallerSourceMode;
  duoInstallerUploadStatus: DuoInstallerUploadStatus;
  duoInstallerMalwareScanStatus: string;
  duoInstallerUploadId: string;
  duoInstallerQuarantineS3Uri: string;
  duoInstallerS3Uri: string;
  duoInstallerSha256: string;
  duoInstallerVersionId: string;
  duoInstallerFileName: string;
  duoInstallerSizeBytes: string;
  duoMinimumProxyVersion: string;
  duoProxyInstanceType: string;
  duoProxyAmiSsmParameter: string;
  duoServerImage: string;
  duoImageInstallerEmbedded: boolean;
  duoImageInstallerSha256: string;
  duoImageInstallerVersion: string;
  managementServerImage: string;
  duoProxyRootVolumeGib: string;
  duoProxyMinimumCapacity: string;
  duoProxyDesiredCapacity: string;
  duoProxyMaximumCapacity: string;
};

type AdminPackageRecord = {
  clientId?: string;
  accountId?: string;
  packageId?: string;
  logicalPackageId?: string;
  latestPackageId?: string;
  packageName?: string;
  jobId?: string;
  packageType?: string;
  packageStage?: string;
  status?: string;
  packageLifecycleStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  generatedUtc?: string;
  passCount?: number | string;
  partialCount?: number | string;
  failCount?: number | string;
  notApplicableCount?: number | string;
  totalControls?: number | string;
  finalReadinessPassControls?: number | string;
  finalReadinessPartialControls?: number | string;
  finalReadinessFailControls?: number | string;
  finalReadinessNotApplicableControls?: number | string;
  finalReadinessTotalControls?: number | string;
  documentCount?: number | string;
  signedDocumentCount?: number | string;
  signedDocumentsUploaded?: number | string;
  validDocumentCount?: number | string;
  scoringEligibleDocumentCount?: number | string;
  clientActionRequiredCount?: number | string;
  actionRequiredCount?: number | string;
  humanProcessRequiredCount?: number | string;
  possibleNotApplicableCount?: number | string;
  finalAssessorReviewPendingCount?: number | string;
  downloadUrl?: string;
  finalAuditorPackageDownloadUrl?: string;
  presignedUrl?: string;
  s3Bucket?: string;
  s3Key?: string;
};

type AdminPackagesResponse = {
  packages?: AdminPackageRecord[];
  items?: AdminPackageRecord[];
  count?: number;
  matchedCount?: number;
  limit?: number;
  page?: number;
  pageCount?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
};

type PackageSearchState = {
  packageType: string;
  status: string;
  search: string;
  searchMode: SearchMode;
  limit: number;
};

type PackageMetadata = {
  count?: number;
  matchedCount?: number;
  limit?: number;
  page?: number;
  pageCount?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
};

type PackageDownloadUrlResponse = {
  downloadUrl: string;
};

type ClientPackageSummary = {
  packages: AdminPackageRecord[];
  latestTechnicalPackage?: AdminPackageRecord;
  finalAuditorPackage?: AdminPackageRecord;
  currentReadinessPackage?: AdminPackageRecord;
  lastRun?: string;
  signedDocumentCount: number;
  actionRequiredCount: number;
};

type DeploymentServiceStatus = {
  key: string;
  label: string;
  category: string;
  description: string;
  enabled: boolean;
  source: string;
  detail?: string;
};

const DEFAULT_SEARCH: ClientSearchState = {
  clientId: '',
  lifecycle: '',
  cmmcLevel: '',
  clientAccountId: '',
  search: '',
  searchMode: 'contains',
  limit: 25,
};

const FSX_CUI_FILE_SHARE_COST_WARNING = 'Paid add-on with monthly FSx storage, throughput, and backup charges.';

const emptyForm: ClientFormState = {
  email: '',
  companyName: '',
  clientId: '',
  clientName: '',
  contactEmail: '',
  clientAccountId: '',
  managementAccountId: '',
  logArchiveAccountId: '',
  region: 'us-east-1',
  cmmcLevel: 'L2',
  lifecycle: 'disabled',
  statusReason: '',
  deploymentModel: 'STEP_FUNCTIONS_CODEBUILD',
  pipelineType: 'CLIENT',
  approvalModel: 'CLIENT_PORTAL_APPROVAL',
  notificationEmails: '',
  clientUsersManagement: 'inherit',
  clientEvidenceWorkflow: 'inherit',
  address: '',
  name: '',
  website: '',
  phone_number: '',
  reportGeneration: 'inherit',
  securityHubNotifications: 'inherit',
  securityHubNotificationEmail: '',
  securityHubNotificationDeliveryChannel: 'email',
  securityHubNotificationWebhookSecretArn: '',
  securityHubNotificationNotifySurelinc: false,
  securityHubNotificationMinimumSeverity: 'HIGH',
  guardDuty: 'inherit',
  guardDutyRuntimeMonitoring: 'inherit',
  securityHub: 'inherit',
  inspector: 'inherit',
  vpc: 'inherit',
  vpcEndpointGatewayS3: 'inherit',
  vpcEndpointGatewayDynamoDb: 'inherit',
  vpcEndpointInterfaceSsm: 'inherit',
  vpcEndpointInterfaceSsmMessages: 'inherit',
  vpcEndpointInterfaceEc2Messages: 'inherit',
  vpcEndpointInterfaceKms: 'inherit',
  vpcEndpointInterfaceLogs: 'inherit',
  vpcEndpointInterfaceMonitoring: 'inherit',
  vpcEndpointInterfaceEc2: 'inherit',
  vpcEndpointInterfaceSts: 'inherit',
  vpcEndpointInterfaceSecretsManager: 'inherit',
  vpcEndpointInterfaceEcrApi: 'inherit',
  vpcEndpointInterfaceEcrDkr: 'inherit',
  vpcEndpointInterfaceWorkSpaces: 'inherit',
  vpcEndpointInterfaceInspector2Telemetry: 'inherit',
  vpcEndpointInterfaceWorkSpacesWeb: 'inherit',
  cuiStorage: 'inherit',
  cuiFilesAccess: 'inherit',
  cuiFileShare: 'disabled',
  cuiFileShareStorageCapacityGib: '32',
  cuiFileShareThroughputCapacityMbps: '8',
  cuiFileShareBackupRetentionDays: '7',
  cuiFileShareDriveLetter: 'Z:',
  cuiFileShareShareName: 'CUI',
  cuiFileShareCreateSharedFolder: true,
  cuiFileShareCreateUserFolders: true,
  cuiFileShareRetainOnDelete: true,
  backup: 'inherit',
  ssm: 'inherit',
  ssmInventory: 'inherit',
  ssmPatchManager: 'inherit',
  directoryService: 'inherit',
  directoryType: 'aws_managed_microsoft_ad',
  directoryEdition: 'standard',
  directoryName: 'cmmc.local',
  directoryShortName: 'CMMC',
  directoryRemovalPolicy: 'retain',
  directoryAllowDestroy: false,
  directoryDestroyGuardEnabled: true,
  directoryFailDestroyIfRegistered: true,
  workspaces: 'disabled',
  workspacesRegistrationEnabled: true,
  workspacesProvisioning: 'disabled',
  workspacesProvisioningUsers: '[]',
  workspacesBundleType: 'value',
  workspacesRunningMode: 'AUTO_STOP',
  workspacesRootVolumeEncryption: true,
  workspacesUserVolumeEncryption: true,
  workspacesDirectoryTypeRequired: 'aws_managed_microsoft_ad',
  workspacesRemovalPolicy: 'retain',
  workspacesAllowDestroy: false,
  workspacesIdentityFederation: 'disabled',
  workspacesIdentityDeploymentStage: 'STATIC_ONLY',
  workspacesIdentityDeploymentEnabled: false,
  workspacesIdentityProviderConfigurationComplete: false,
  workspacesIdentityClientAuthorizationApproved: false,
  workspacesIdentityApprovalId: '',
  workspacesIdentityApprovedAt: '',
  workspacesIdentityApprovedBy: '',
  workspacesIdentityProvider: 'duo',
  workspacesIdentityIntegrationMode: 'saml_sso',
  workspacesIdentityAllowDirectoryLoginFallback: false,
  workspacesIdentityFallbackAcknowledged: false,
  workspacesIdentityTestUserName: '',
  duoEdition: 'ADVANTAGE',
  duoLicensedUserCount: '0',
  duoSubaccountId: '',
  duoApiHostname: '',
  duoApiHostnameRequired: false,
  duoUserAccessUrl: '',
  duoMetadataSourceMode: 'managed_upload',
  duoMetadataUrl: '',
  duoMetadataS3Uri: '',
  duoMetadataStagedS3Uri: '',
  duoMetadataStagedVersionId: '',
  duoMetadataSha256: '',
  duoMetadataObjectVerified: false,
  duoMetadataObjectStatus: '',
  duoAuthenticationSourceConfigured: false,
  duoWorkspacesApplicationConfigured: false,
  duoUserAccessGranted: false,
  duoPermittedEmailDomains: '',
  duoEmailDomainVerified: false,
  duoProxyOnboardingConfigured: false,
  duoDeploymentPlanReady: true,
  duoDeploymentPlanMissingCommands: '',
  duoProxyInputs: [newDuoProxyInputDraft(1)],
  duoProxyEnrollmentSecretArn: '',
  duoServiceAccountSecretArn: '',
  duoInstallerSourceMode: 'managed_upload',
  duoInstallerUploadStatus: '',
  duoInstallerMalwareScanStatus: '',
  duoInstallerUploadId: '',
  duoInstallerQuarantineS3Uri: '',
  duoInstallerS3Uri: '',
  duoInstallerSha256: '',
  duoInstallerVersionId: '',
  duoInstallerFileName: '',
  duoInstallerSizeBytes: '',
  duoMinimumProxyVersion: '6.5.1',
  duoProxyInstanceType: 't3.small',
  duoProxyAmiSsmParameter: '/aws/service/ami-windows-latest/Windows_Server-2025-English-Full-Base',
  duoServerImage: '',
  duoImageInstallerEmbedded: false,
  duoImageInstallerSha256: '',
  duoImageInstallerVersion: '',
  managementServerImage: '',
  duoProxyRootVolumeGib: '40',
  duoProxyMinimumCapacity: '2',
  duoProxyDesiredCapacity: '2',
  duoProxyMaximumCapacity: '3',
};

const emptyWorkSpacesProvisioningUserDraft: WorkSpacesProvisioningUserDraft = {
  userName: '',
  givenName: '',
  surname: '',
  email: '',
  bundleId: '',
  passwordSecretArn: '',
};


const emptyWorkSpacesBundleCatalog: WorkSpacesBundleCatalogState = {
  recommendedOptions: [],
  advancedOptions: [],
  loading: false,
  error: null,
  warning: null,
};

const WORKSPACES_DEFAULT_BUNDLE_PROFILE = 'performance';

const LIFECYCLE_OPTIONS = ['active', 'disabled'];
const CMMC_LEVEL_OPTIONS = ['L1', 'L2', 'L3'];
const US_REGION_OPTIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'us-gov-east-1',
  'us-gov-west-1',
];
const INITIAL_CLIENT_USER_ROLE = 'CLIENT_ADMIN';
const DEFAULT_PACKAGE_SEARCH: PackageSearchState = {
  packageType: '',
  status: '',
  search: '',
  searchMode: 'contains',
  limit: 25,
};
const PACKAGE_TYPE_OPTIONS = ['TECHNICAL_EVIDENCE_PACKAGE', 'FINAL_AUDITOR_PACKAGE'];
const PACKAGE_STATUS_OPTIONS = ['PACKAGE_INDEXED', 'AUDITOR_PACKAGE_READY', 'CLIENT_ACTION_REQUIRED', 'FINAL_ASSESSOR_REVIEW_PENDING', 'FAILED'];
const PACKAGE_PAGE_SIZE_OPTIONS = [5, 25, 50, 100, 250];

function value(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}


function clientContactDisplayName(client: ClientRecord): string {
  const clientConfig = (typeof client.clientConfig === 'object' && client.clientConfig !== null ? client.clientConfig : {}) as Record<string, unknown>;
  return displayNameForIdentity(
    {
      contactName: client.contactName,
      name: client.name,
      contact_name: clientConfig.contact_name,
      email: client.contactEmail || client.clientEmail || clientConfig.contact_email,
    },
    'Primary contact',
  );
}

function clientNotificationContactSummary(client: ClientRecord): string {
  const clientConfig = (typeof client.clientConfig === 'object' && client.clientConfig !== null ? client.clientConfig : {}) as Record<string, unknown>;
  return notificationContactSummary(client.notificationEmails || clientConfig.notification_emails);
}

function clientUserDisplayName(user: ClientUserRecord): string {
  return displayNameForIdentity(user as unknown as Record<string, unknown>, 'Client user');
}

function workSpacesUserDisplayName(user: Record<string, unknown>): string {
  return displayNameForIdentity(
    {
      given_name: user.given_name,
      surname: user.surname,
      name: workSpacesUserFullName(user),
      username: user.user_name,
      email: user.email,
    },
    'WorkSpaces user',
  );
}

function welcomeDeliveryMessage(
  response: { message?: string; cognitoInviteSent?: boolean; welcomeEmailQueued?: boolean; welcomeEmailSent?: boolean; welcomeEmailStatus?: string },
  fallback: string,
): string {
  if (response.message) return response.message;
  const status = String(response.welcomeEmailStatus || '').trim().toUpperCase();
  if (response.welcomeEmailQueued) return `${fallback} Branded SURELINC welcome email queued.`;
  if (response.welcomeEmailSent) return `${fallback} Branded SURELINC welcome email sent.`;
  if (response.cognitoInviteSent) {
    return `${fallback} Cognito temporary-password invitation sent. Branded SURELINC welcome email status: ${status || 'UNKNOWN'}.`;
  }
  return fallback;
}

function numberValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function slugifyClientId(companyName: string): string {
  const slug = companyName
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 64)
    .replace(/^-|-$/g, '');
  return slug || 'client';
}

function packageTimestamp(pkg?: AdminPackageRecord): string {
  return String(pkg?.updatedAt || pkg?.createdAt || pkg?.generatedUtc || '');
}

function rawPackageStatus(pkg?: AdminPackageRecord): string {
  return value(pkg?.status || pkg?.packageLifecycleStatus || pkg?.packageStage);
}

function packageStatus(pkg?: AdminPackageRecord): string {
  return friendlyLabel(rawPackageStatus(pkg));
}

function packageDisplayName(pkg?: AdminPackageRecord): string {
  return value(pkg?.packageName || pkg?.logicalPackageId || pkg?.packageId || pkg?.latestPackageId);
}

function packageIdValue(pkg?: AdminPackageRecord): string {
  return String(pkg?.logicalPackageId || pkg?.packageId || pkg?.latestPackageId || pkg?.packageName || '').trim();
}

function packageRowKey(pkg: AdminPackageRecord, index: number): string {
  return `${pkg.clientId || 'client'}-${packageIdValue(pkg) || 'package'}-${pkg.packageType || 'package'}-${index}`;
}

function controlCounts(pkg?: AdminPackageRecord) {
  return {
    pass: numberValue(pkg?.passCount ?? pkg?.finalReadinessPassControls),
    partial: numberValue(pkg?.partialCount ?? pkg?.finalReadinessPartialControls),
    fail: numberValue(pkg?.failCount ?? pkg?.finalReadinessFailControls),
    notApplicable: numberValue(pkg?.notApplicableCount ?? pkg?.finalReadinessNotApplicableControls),
    total: numberValue(pkg?.totalControls ?? pkg?.finalReadinessTotalControls),
  };
}

function scoreSummary(pkg?: AdminPackageRecord): string {
  if (!pkg) return '—';
  const counts = controlCounts(pkg);
  return `P ${counts.pass} / Pa ${counts.partial} / F ${counts.fail} / N/A ${counts.notApplicable}${counts.total ? ` / T ${counts.total}` : ''}`;
}

function signedDocumentCount(pkg?: AdminPackageRecord): number {
  return numberValue(pkg?.signedDocumentCount ?? pkg?.signedDocumentsUploaded ?? pkg?.validDocumentCount ?? pkg?.scoringEligibleDocumentCount);
}

function actionRequiredCount(pkg?: AdminPackageRecord): number {
  return numberValue(
    pkg?.clientActionRequiredCount
      ?? pkg?.actionRequiredCount
      ?? pkg?.humanProcessRequiredCount
      ?? pkg?.possibleNotApplicableCount
      ?? pkg?.finalAssessorReviewPendingCount,
  );
}

function packageDownloadUrl(pkg?: AdminPackageRecord): string {
  return String(pkg?.finalAuditorPackageDownloadUrl || pkg?.downloadUrl || pkg?.presignedUrl || '').trim();
}

function buildClientPackageSummary(packages: AdminPackageRecord[]): ClientPackageSummary {
  const realPackages = packages.filter((pkg) => !String(pkg.packageId || '').startsWith('LATEST#'));
  const latestTechnicalPackage = realPackages
    .filter((pkg) => String(pkg.packageType || '').toUpperCase() === 'TECHNICAL_EVIDENCE_PACKAGE')
    .sort((a, b) => packageTimestamp(b).localeCompare(packageTimestamp(a)))[0];
  const finalAuditorPackage = realPackages
    .filter((pkg) => String(pkg.packageType || '').toUpperCase() === 'FINAL_AUDITOR_PACKAGE')
    .sort((a, b) => packageTimestamp(b).localeCompare(packageTimestamp(a)))[0];
  const currentReadinessPackage = finalAuditorPackage || latestTechnicalPackage;

  return {
    packages: realPackages,
    latestTechnicalPackage,
    finalAuditorPackage,
    currentReadinessPackage,
    lastRun: packageTimestamp(currentReadinessPackage),
    signedDocumentCount: signedDocumentCount(currentReadinessPackage),
    actionRequiredCount: actionRequiredCount(currentReadinessPackage),
  };
}

function compactForm(form: ClientFormState): Record<string, unknown> {
  const primaryEmail = (form.email || form.contactEmail).trim();
  const notificationEmails = form.notificationEmails
    .split(/[;,\n]+/)
    .map((email) => email.trim())
    .filter(Boolean);
  const resolvedNotificationEmails = notificationEmails.length > 0 ? notificationEmails : primaryEmail ? [primaryEmail] : [];
  const clientUsersManagementEnabled = effectiveEnabled(form.clientUsersManagement, true);

  const payload: Record<string, unknown> = {
    clientId: form.clientId.trim() || undefined,
    clientName: (form.clientName || form.companyName).trim(),
    companyName: (form.companyName || form.clientName).trim(),
    email: primaryEmail,
    contactEmail: (form.contactEmail || form.email).trim(),
    clientStatus: form.lifecycle.toLowerCase() === 'active' ? 'ACTIVE' : 'DISABLED',
    lifecycle: form.lifecycle,
    status: form.lifecycle.toLowerCase() === 'active' ? 'ACTIVE' : 'DISABLED',
    statusReason: form.statusReason.trim(),
    notificationEmails: resolvedNotificationEmails,
    notification_emails: resolvedNotificationEmails,
    clientUsersManagementEnabled,
    name: form.name?.trim(),
    address: form.address?.trim(),
    website: form.website?.trim(),
    phone_number: form.phone_number?.trim(),
  };

  return Object.fromEntries(Object.entries(payload).filter(([, formValue]) => {
    if (Array.isArray(formValue)) return formValue.length > 0;
    return formValue !== undefined && formValue !== null && formValue !== '';
  }));
}

function formatDate(dateValue: unknown): string {
  return formatLocalDateTime(dateValue);
}

function emailList(value: unknown): string {
  return Array.isArray(value) ? value.join(', ') : value ? String(value) : '';
}

const WORKSPACES_USER_ALLOWED_KEYS = new Set(['user_name', 'given_name', 'surname', 'email', 'bundle_id', 'password_secret_arn', 'reset_password_on_update']);
const WORKSPACES_USER_FORBIDDEN_KEYS = new Set(['password', 'initial_password', 'new_password', 'temporary_password', 'temporaryPassword']);

function parseWorkSpacesProvisioningUsers(rawValue: string): Record<string, unknown>[] {
  const text = rawValue.trim();
  if (!text) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error(`WorkSpaces provisioning users must be valid JSON: ${error instanceof Error ? error.message : 'parse failed'}`);
  }

  if (!Array.isArray(parsed)) {
    throw new Error('WorkSpaces provisioning users must be a JSON array.');
  }

  return parsed.map((entry, index) => {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      throw new Error(`WorkSpaces provisioning user ${index + 1} must be an object.`);
    }

    const source = entry as Record<string, unknown>;
    const normalized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(source)) {
      if (WORKSPACES_USER_FORBIDDEN_KEYS.has(key)) {
        throw new Error(`WorkSpaces provisioning user ${index + 1} must not include plaintext password field ${key}. Use password_secret_arn or allow the stack to generate a temporary password.`);
      }
      if (!WORKSPACES_USER_ALLOWED_KEYS.has(key)) {
        throw new Error(`WorkSpaces provisioning user ${index + 1} contains unsupported field ${key}.`);
      }
      if (key === 'reset_password_on_update') {
        if (typeof value === 'boolean') {
          normalized[key] = value;
        } else if (typeof value === 'string' && value.trim()) {
          const normalizedBoolean = value.trim().toLowerCase();
          if (['true', '1', 'yes', 'y'].includes(normalizedBoolean)) {
            normalized[key] = true;
          } else if (['false', '0', 'no', 'n'].includes(normalizedBoolean)) {
            normalized[key] = false;
          } else {
            throw new Error(`WorkSpaces provisioning user ${index + 1} field ${key} must be a boolean.`);
          }
        } else if (value !== null && value !== undefined && value !== '') {
          throw new Error(`WorkSpaces provisioning user ${index + 1} field ${key} must be a boolean.`);
        }
      } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) normalized[key] = trimmed;
      } else if (value !== null && value !== undefined) {
        throw new Error(`WorkSpaces provisioning user ${index + 1} field ${key} must be a string.`);
      }
    }

    if (!String(normalized.user_name || '').trim()) {
      throw new Error(`WorkSpaces provisioning user ${index + 1} requires user_name.`);
    }

    return normalized;
  });
}

function normalizeWorkSpacesUserRecord(user: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  const userName = String(user.user_name ?? user.userName ?? user.username ?? '').trim();
  const givenName = String(user.given_name ?? user.givenName ?? '').trim();
  const surname = String(user.surname ?? user.family_name ?? user.familyName ?? '').trim();
  const email = String(user.email ?? '').trim();
  const bundleId = String(user.bundle_id ?? user.bundleId ?? '').trim();
  const passwordSecretArn = String(user.password_secret_arn ?? user.passwordSecretArn ?? '').trim();

  if (userName) normalized.user_name = userName;
  if (givenName) normalized.given_name = givenName;
  if (surname) normalized.surname = surname;
  if (email) normalized.email = email;
  if (bundleId) normalized.bundle_id = bundleId;
  if (passwordSecretArn) normalized.password_secret_arn = passwordSecretArn;
  if (typeof user.reset_password_on_update === 'boolean') normalized.reset_password_on_update = user.reset_password_on_update;
  if (typeof user.resetPasswordOnUpdate === 'boolean') normalized.reset_password_on_update = user.resetPasswordOnUpdate;
  return normalized;
}

function workSpacesUsersFromClientRecord(client: ClientRecord): Record<string, unknown>[] {
  const separatedUsers = client.workspacesUsers ?? client.workspaceUsers ?? client.workSpacesUsers;
  if (Array.isArray(separatedUsers)) {
    return separatedUsers
      .map((entry) => normalizeWorkSpacesUserRecord(entry as Record<string, unknown>))
      .filter((entry) => String(entry.user_name || '').trim());
  }
  return [];
}

function workSpacesUserBundleId(user: Record<string, unknown>): string {
  return String(user.bundle_id || '').trim();
}

function validateWorkSpacesProvisioningUserBundleIds(users: Record<string, unknown>[]): string {
  for (const [index, user] of users.entries()) {
    const bundleId = workSpacesUserBundleId(user);
    if (!bundleId.startsWith('wsb-')) {
      const userName = String(user.user_name || '').trim();
      return userName
        ? `WorkSpaces provisioning user ${userName} requires a bundle ID that starts with wsb-.`
        : `WorkSpaces provisioning user ${index + 1} requires a bundle ID that starts with wsb-.`;
    }
  }
  return '';
}

function workSpacesProvisioningUsersForForm(form: Pick<ClientFormState, 'workspacesProvisioningUsers'>): Record<string, unknown>[] {
  return parseWorkSpacesProvisioningUsers(form.workspacesProvisioningUsers);
}

function workSpacesUserFullName(user: Record<string, unknown>): string {
  return [String(user.given_name || '').trim(), String(user.surname || '').trim()].filter(Boolean).join(' ');
}

function workSpacesUserRowKey(user: Record<string, unknown>, index: number): string {
  return `${String(user.user_name || 'workspace-user').trim() || 'workspace-user'}-${index}`;
}


function formatWorkSpacesProvisioningUsers(users: Record<string, unknown>[]): string {
  return JSON.stringify(users, null, 2);
}

function buildWorkSpacesProvisioningUser(draft: WorkSpacesProvisioningUserDraft): Record<string, unknown> {
  const user: Record<string, unknown> = {};
  const userName = draft.userName.trim();
  const givenName = draft.givenName.trim();
  const surname = draft.surname.trim();
  const email = draft.email.trim();
  const bundleId = draft.bundleId.trim();
  const passwordSecretArn = draft.passwordSecretArn.trim();

  if (!userName) {
    throw new Error('WorkSpaces provisioning user requires a user name.');
  }
  if (!bundleId.startsWith('wsb-')) {
    throw new Error('WorkSpaces provisioning user requires a bundle ID that starts with wsb-.');
  }

  user.user_name = userName;
  if (givenName) user.given_name = givenName;
  if (surname) user.surname = surname;
  if (email) user.email = email;
  if (bundleId) user.bundle_id = bundleId;
  if (passwordSecretArn) user.password_secret_arn = passwordSecretArn;
  return user;
}

function addWorkSpacesProvisioningUserToJson(rawValue: string, draft: WorkSpacesProvisioningUserDraft): string {
  const users = parseWorkSpacesProvisioningUsers(rawValue);
  const nextUser = buildWorkSpacesProvisioningUser(draft);
  const nextUserName = String(nextUser.user_name || '').toLowerCase();
  const updatedUsers = users.filter((user) => String(user.user_name || '').toLowerCase() !== nextUserName);
  updatedUsers.push(nextUser);
  return formatWorkSpacesProvisioningUsers(updatedUsers);
}


function workSpacesBundleOptionKey(option: WorkSpacesBundleOption): string {
  const profile = String(option.profile || '').trim();
  if (profile) return `profile:${profile}`;
  const bundleId = String(option.bundleId || '').trim();
  if (bundleId) return `bundle:${bundleId}`;
  return `label:${option.label}`;
}

function workSpacesBundleOptionDetail(option: WorkSpacesBundleOption): string {
  const bundleId = String(option.bundleId || '').trim();
  const protocol = String(option.protocol || '').trim();
  return [bundleId, protocol].filter(Boolean).join(' · ');
}

function selectWorkSpacesBundleValue(
  bundleId: string,
  recommendedOptions: WorkSpacesBundleOption[],
): string {
  const normalizedBundleId = bundleId.trim();
  if (!normalizedBundleId) return '';

  const recommendedMatch = recommendedOptions.find((option) => String(option.bundleId || '').trim() === normalizedBundleId);
  return recommendedMatch ? workSpacesBundleOptionKey(recommendedMatch) : `bundle:${normalizedBundleId}`;
}

function workSpacesBundleProfileLabel(option: WorkSpacesBundleOption): string {
  const detail = workSpacesBundleOptionDetail(option);
  return detail ? `${option.label} (${detail})` : option.label;
}

function workSpacesBundleSpecs(option?: WorkSpacesBundleOption): Array<[string, string]> {
  if (!option) return [];
  const storage = option.storage || [
    option.rootStorageGb ? `Root ${option.rootStorageGb} GB` : '',
    option.userStorageGb ? `User ${option.userStorageGb} GB` : '',
  ].filter(Boolean).join(' / ');
  const rows: Array<[string, string]> = [
    ['O/S', option.operatingSystem || 'Windows'],
    ['CPU', option.cpu || option.computeType || '—'],
    ['Memory', option.memory || '—'],
    ['Storage', storage || '—'],
    ['Graphics', option.gpu || '—'],
  ];
  return rows.filter(([, value]) => value !== '—');
}

function currentWorkSpacesBundleOption(bundleId: string, options: WorkSpacesBundleOption[]): WorkSpacesBundleOption | undefined {
  const normalizedBundleId = bundleId.trim();
  if (!normalizedBundleId) return undefined;
  return options.find((option) => String(option.bundleId || '').trim() === normalizedBundleId)
    ?? {
      value: normalizedBundleId,
      label: `Current bundle ${normalizedBundleId}`,
      bundleId: normalizedBundleId,
      description: 'Existing bundle assigned to this user. Refresh bundles or select another profile to change it.',
    };
}

function normalizeWorkSpacesBundleOptions(options?: WorkSpacesBundleOption[]): WorkSpacesBundleOption[] {
  if (!Array.isArray(options)) return [];
  const seen = new Set<string>();
  return options
    .map((option) => ({
      ...option,
      label: String(option.label || option.bundleId || '').trim(),
      bundleId: String(option.bundleId || '').trim(),
      profile: String(option.profile || '').trim(),
    }))
    .filter((option) => option.label)
    .filter((option) => {
      const key = workSpacesBundleOptionKey(option);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function recordValue(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function nestedValue(source: Record<string, unknown>, ...path: string[]): unknown {
  let current: unknown = source;
  for (const segment of path) {
    if (typeof current !== 'object' || current === null || Array.isArray(current)) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function overrideChoice(value: unknown, inheritedDefault: boolean): Exclude<OverrideChoice, 'inherit'> {
  if (value === true) return 'enabled';
  if (value === false) return 'disabled';
  return inheritedDefault ? 'enabled' : 'disabled';
}

function overrideBool(choice: OverrideChoice): boolean | undefined {
  if (choice === 'enabled') return true;
  if (choice === 'disabled') return false;
  return undefined;
}

function overridePayload(choice: OverrideChoice, inheritedDefault: boolean): boolean {
  return effectiveEnabled(choice, inheritedDefault);
}

function effectiveEnabled(choice: OverrideChoice, inheritedDefault: boolean): boolean {
  const override = overrideBool(choice);
  return override === undefined ? inheritedDefault : override;
}

function positiveIntegerString(value: string, fallback: string): string {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : fallback;
}

function nonNegativeIntegerString(value: string, fallback: string): string {
  const parsed = Number.parseInt(String(value ?? '').trim(), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : fallback;
}

function normalizedWorkSpacesIdentityDeploymentStage(
  value: unknown,
  legacyDeploymentEnabled = false,
): WorkSpacesIdentityDeploymentStage {
  const stage = String(value || '').trim().toUpperCase();
  if (stage === 'STATIC_ONLY' || stage === 'PROXY_ONLY' || stage === 'SAML_ACTIVATION') {
    return stage;
  }
  return legacyDeploymentEnabled ? 'PROXY_ONLY' : 'STATIC_ONLY';
}

function workSpacesIdentityProxyDeploymentRequested(form: ClientFormState): boolean {
  return form.workspacesIdentityDeploymentStage === 'PROXY_ONLY'
    || form.workspacesIdentityDeploymentStage === 'SAML_ACTIVATION';
}

function workSpacesIdentitySamlActivationRequested(form: ClientFormState): boolean {
  return form.workspacesIdentityDeploymentStage === 'SAML_ACTIVATION';
}

function effectiveWorkSpacesIdentityDeploymentStage(form: ClientFormState): WorkSpacesIdentityDeploymentStage {
  if (form.workspacesIdentityFederation !== 'enabled') {
    return 'STATIC_ONLY';
  }
  if (duoProviderConfigurationReady(form)) {
    return 'SAML_ACTIVATION';
  }
  if (duoProxyConfigurationReady(form)) {
    return 'PROXY_ONLY';
  }
  return 'STATIC_ONLY';
}

function normalizedEmailDomains(value: string): string[] {
  return Array.from(new Set(
    String(value || '')
      .split(/[\s,;]+/)
      .map((item) => item.trim().toLowerCase().replace(/^@/, ''))
      .filter((item) => /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(item) && item.includes('.')),
  )).sort();
}

function inferredDuoEmailDomains(form: ClientFormState): string[] {
  const domains = new Set(normalizedEmailDomains(form.duoPermittedEmailDomains));
  const addEmail = (value: unknown) => {
    const email = String(value || '').trim().toLowerCase();
    const at = email.lastIndexOf('@');
    if (at > 0 && at < email.length - 1) domains.add(email.slice(at + 1));
  };
  addEmail(form.contactEmail);
  try {
    for (const user of parseWorkSpacesProvisioningUsers(form.workspacesProvisioningUsers)) {
      addEmail(user.email);
    }
  } catch {
    // The main form validation reports malformed WorkSpaces users separately.
  }
  return Array.from(domains).sort();
}

function duoProxyConfigurationReady(form: ClientFormState): boolean {
  const installerConfigured = form.duoImageInstallerEmbedded && /^[0-9a-fA-F]{64}$/.test(form.duoImageInstallerSha256) || (form.duoInstallerSourceMode === 'managed_upload'
    ? form.duoInstallerUploadStatus === 'APPROVED'
      && form.duoInstallerMalwareScanStatus === 'NO_THREATS_FOUND'
      && form.duoInstallerS3Uri.trim().startsWith('s3://')
      && /^[0-9a-fA-F]{64}$/.test(form.duoInstallerSha256.trim())
    : form.duoInstallerS3Uri.trim().startsWith('s3://')
      && /^[0-9a-fA-F]{64}$/.test(form.duoInstallerSha256.trim()));
  return Boolean(form.duoProxyOnboardingConfigured && form.duoDeploymentPlanReady && installerConfigured);
}

function duoProviderConfigurationReady(form: ClientFormState): boolean {
  const metadataConfigured = form.duoMetadataSourceMode === 'https_url'
    ? form.duoMetadataUrl.trim().startsWith('https://')
    : (
      (form.duoMetadataS3Uri.trim().startsWith('s3://') && form.duoMetadataObjectVerified)
      || (
        form.duoMetadataStagedS3Uri.trim().startsWith('s3://')
        && form.duoMetadataObjectStatus.toUpperCase() === 'STAGED'
        && /^[0-9a-fA-F]{64}$/.test(form.duoMetadataSha256.trim())
      )
    );
  return Boolean(
    duoProxyConfigurationReady(form)
      && form.duoUserAccessUrl.trim().startsWith('https://')
      && metadataConfigured,
  );
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return window.btoa(binary);
}


function formatFileSize(sizeBytes: string | number): string {
  const size = Number(sizeBytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${size} bytes`;
}

function normalizeDriveLetter(value: string): string {
  const normalized = String(value || '').trim().toUpperCase();
  const match = normalized.match(/^([A-Z]):?$/);
  return match ? `${match[1]}:` : normalized;
}

function deploymentDefaultsForLevel(cmmcLevel: string): Readonly<CmmcDeploymentProfileDefaults> {
  const normalizedLevel = String(cmmcLevel || '').trim().toUpperCase();
  const defaults = __CMMC_DEPLOYMENT_PROFILE_DEFAULTS__[normalizedLevel];
  if (!defaults) {
    throw new Error(`No deployment defaults were built from cdk.json for CMMC level ${normalizedLevel || 'UNKNOWN'}.`);
  }
  return defaults;
}

function deploymentDefault(form: ClientFormState, key: DeploymentDefaultKey): boolean {
  return deploymentDefaultsForLevel(form.cmmcLevel)[key];
}

function displayedChoice(
  choice: OverrideChoice,
  inheritedDefault: boolean,
): Exclude<OverrideChoice, 'inherit'> {
  if (choice === 'enabled' || choice === 'disabled') return choice;
  return inheritedDefault ? 'enabled' : 'disabled';
}

function displayedDeploymentChoice(
  form: ClientFormState,
  key: DeploymentDefaultKey,
  choice: OverrideChoice,
): Exclude<OverrideChoice, 'inherit'> {
  return displayedChoice(choice, deploymentDefault(form, key));
}

function firstBooleanFromPaths(source: Record<string, unknown>, paths: string[][]): boolean | undefined {
  for (const path of paths) {
    const found = nestedValue(source, ...path);
    if (typeof found === 'boolean') return found;
  }
  return undefined;
}

function deploymentStatus(
  clientConfig: Record<string, unknown>,
  choice: OverrideChoice,
  inheritedDefault: boolean,
  paths: string[][],
): { enabled: boolean; source: string } {
  const configuredValue = overrideBool(choice);
  const resolvedConfig = firstBooleanFromPaths(clientConfig, paths);
  const enabled = resolvedConfig ?? configuredValue ?? inheritedDefault;
  return { enabled, source: enabled ? 'Account setting enabled' : 'Account setting disabled' };
}

function buildDeploymentServiceStatuses(clientConfig: Record<string, unknown>, form: ClientFormState): DeploymentServiceStatus[] {
  const defaults = deploymentDefaultsForLevel(form.cmmcLevel);
  const service = (
    key: string,
    label: string,
    category: string,
    description: string,
    choice: OverrideChoice,
    inheritedDefault: boolean,
    paths: string[][],
    detail?: string,
  ): DeploymentServiceStatus => {
    const status = deploymentStatus(clientConfig, choice, inheritedDefault, paths);
    return {
      key,
      label,
      category,
      description,
      detail,
      ...status,
    };
  };

  const directoryDetail = [friendlyLabel(form.directoryType), friendlyLabel(form.directoryEdition)]
    .filter((item) => item && item !== '—')
    .join(' · ');
  const provisioningEnabled = effectiveEnabled(form.workspacesProvisioning, deploymentDefault(form, 'workspacesProvisioning'));
  const workspaceUserCount = (() => {
    try {
      return workSpacesProvisioningUsersForForm(form).length;
    } catch {
      return 0;
    }
  })();
  const workspacesDetail = [
    workspaceUserCount ? `${workspaceUserCount} provisioning user(s)` : (form.workspacesBundleType ? `Bundle type ${form.workspacesBundleType}` : ''),
    form.workspacesRunningMode,
    provisioningEnabled ? 'Provisioning enabled' : 'Provisioning disabled',
  ]
    .filter(Boolean)
    .join(' · ');
  const notificationDetail = [
    form.securityHubNotificationEmail ? `Email: ${form.securityHubNotificationEmail}` : '',
    form.securityHubNotificationDeliveryChannel === 'email_and_webhook'
      ? 'Email + channel webhook'
      : form.securityHubNotificationDeliveryChannel === 'webhook'
        ? 'Channel webhook'
        : 'Email',
    `Workflow NEW · ${form.securityHubNotificationMinimumSeverity}+`,
    form.securityHubNotificationNotifySurelinc ? 'SURELINC copy on' : '',
  ].filter(Boolean).join(' · ');
  const cuiStorageDetail = 'CUI bucket and CUI log bucket are self-contained';
  const cuiFileShareDetail = [
    'Paid add-on',
    `FSx Windows · ${form.cuiFileShareDriveLetter || 'Z:'}`,
    `${positiveIntegerString(form.cuiFileShareStorageCapacityGib, '32')} GiB`,
    `${positiveIntegerString(form.cuiFileShareThroughputCapacityMbps, '8')} MB/s`,
  ].join(' · ');
  const identityFederationDetail = [
    'Paid add-on',
    form.workspacesIdentityProvider === 'duo' ? 'Cisco Duo' : friendlyLabel(form.workspacesIdentityProvider),
    form.workspacesIdentityIntegrationMode.toUpperCase(),
    form.workspacesIdentityDeploymentStage === 'SAML_ACTIVATION'
      ? 'Stage 3 · SAML activation'
      : form.workspacesIdentityDeploymentStage === 'PROXY_ONLY'
        ? 'Stage 2 · Proxy deployment'
        : 'Stage 1 · Static resources only',
  ].join(' · ');

  return [
    service(
      'vpc',
      'Enclave network',
      'Core',
      'Network foundation used by enclave resources and dependent services.',
      form.vpc,
      defaults.vpc,
      [['vpc', 'enabled']],
    ),
    service(
      'cui-storage',
      'CUI storage',
      'Core',
      'Dedicated CUI storage boundary and supporting access-log configuration.',
      form.cuiStorage,
      defaults.cuiStorage,
      [['cui_storage', 'enabled'], ['cuiStorage', 'enabled']],
      cuiStorageDetail,
    ),
    service(
      'cui-file-share',
      'CUI File Share',
      'Paid add-on',
      'Optional FSx for Windows File Server mapped-drive experience for WorkSpaces users.',
      form.cuiFileShare,
      defaults.cuiFileShare,
      [['cui_file_share', 'enabled'], ['cuiFileShare', 'enabled']],
      cuiFileShareDetail,
    ),
    service(
      'directory-service',
      'Directory Service',
      'Identity',
      'Managed directory used for identity-aware enclave access.',
      form.directoryService,
      defaults.directoryService,
      [['directory_service', 'enabled'], ['directoryService', 'enabled']],
      directoryDetail,
    ),
    service(
      'workspaces',
      'WorkSpaces',
      'Access',
      'Controlled desktop access into the enclave when enabled.',
      form.workspaces,
      defaults.workspaces,
      [['workspaces', 'enabled']],
      workspacesDetail,
    ),
    service(
      'workspaces-identity-federation',
      'WorkSpaces Identity Protection',
      'Paid add-on',
      'Provider-neutral SAML identity protection for WorkSpaces. Cisco Duo is supported now; Okta and Microsoft Entra ID are reserved for later adapters.',
      form.workspacesIdentityFederation,
      defaults.workspacesIdentityFederation,
      [['workspaces', 'identity_federation', 'enabled']],
      identityFederationDetail,
    ),
    service(
      'guardduty',
      'GuardDuty',
      'Security monitoring',
      'Threat-detection service for supported client account telemetry.',
      form.guardDuty,
      defaults.guardDuty,
      [['security_services', 'guardduty', 'enabled'], ['securityServices', 'guardDuty', 'enabled']],
    ),
    service(
      'guardduty-runtime',
      'GuardDuty runtime monitoring',
      'Security monitoring',
      'Runtime monitoring option for supported workloads.',
      form.guardDutyRuntimeMonitoring,
      defaults.guardDutyRuntimeMonitoring,
      [['security_services', 'guardduty', 'enable_runtime_monitoring'], ['securityServices', 'guardDuty', 'enableRuntimeMonitoring']],
    ),
    service(
      'security-hub',
      'Security Hub',
      'Security posture',
      'Security posture aggregation and compliance finding management.',
      form.securityHub,
      defaults.securityHub,
      [['security_services', 'security_hub', 'enabled'], ['securityServices', 'securityHub', 'enabled']],
    ),
    service(
      'inspector',
      'Inspector',
      'Vulnerability management',
      'Vulnerability scanning service for supported resources.',
      form.inspector,
      defaults.inspector,
      [['security_services', 'aws_inspector', 'enabled'], ['securityServices', 'awsInspector', 'enabled']],
    ),
    service(
      'security-hub-notifications',
      'Client Security Alerts',
      'Notifications',
      'Paid add-on for tenant-isolated Security Hub NEW HIGH/CRITICAL client alert digests.',
      form.securityHubNotifications,
      defaults.securityHubNotifications,
      [['security_services', 'security_hub_notifications', 'enabled'], ['securityServices', 'securityHubNotifications', 'enabled']],
      notificationDetail,
    ),
    service(
      'ssm',
      'Systems Manager',
      'Operations',
      'Operations management foundation for inventory and patch evidence.',
      form.ssm,
      defaults.ssm,
      [['ssm', 'enabled']],
    ),
    service(
      'ssm-inventory',
      'SSM Inventory',
      'Operations',
      'Inventory collection setting for managed instances.',
      form.ssmInventory,
      defaults.ssmInventory,
      [['ssm', 'inventory']],
    ),
    service(
      'ssm-patch-manager',
      'SSM Patch Manager',
      'Operations',
      'Patch-management evidence and operational patch state tracking.',
      form.ssmPatchManager,
      defaults.ssmPatchManager,
      [['ssm', 'patch_manager'], ['ssm', 'patchManager']],
    ),
    service(
      'backup',
      'Backup',
      'Resilience',
      'Backup service configuration for recovery posture evidence.',
      form.backup,
      defaults.backup,
      [['backup', 'enabled']],
    ),
    service(
      'client-user-management',
      'Client portal user management',
      'Portal',
      'Allows client admins to manage portal users and WorkSpaces user requests from the client portal.',
      form.clientUsersManagement,
      true,
      [
        ['client_user_management', 'enabled'],
        ['client_users_management', 'enabled'],
        ['client_portal', 'users_management_enabled'],
        ['client_portal', 'users', 'enabled'],
      ],
      'Client Admins only · Portal Users + WorkSpaces Users',
    ),
    service(
      'client-evidence-workflow',
      'Client-started evidence collection',
      'Readiness workflow',
      'Paid account feature that lets client account administrators request evidence collection from the client portal. Every request still requires approval or decline before collection begins.',
      form.clientEvidenceWorkflow,
      false,
      [
        ['client_evidence_workflow', 'enabled'],
        ['client_evidence_collection', 'enabled'],
        ['client_portal', 'evidence_workflow_enabled'],
        ['client_portal', 'evidence_collection', 'enabled'],
      ],
      'Paid feature · Account Admins only · Approval required',
    ),
    service(
      'report-generation',
      'Report generation',
      'Readiness workflow',
      'Automated readiness and evidence report generation.',
      form.reportGeneration,
      true,
      [['report_generation', 'enabled'], ['reportGeneration', 'enabled']],
    ),
  ];
}

function buildWorkSpacesIdentityReadinessChecks(form: ClientFormState): IdentityReadinessCheck[] {
  const defaults = deploymentDefaultsForLevel(form.cmmcLevel);
  const moduleEnabled = effectiveEnabled(form.workspacesIdentityFederation, deploymentDefault(form, 'workspacesIdentityFederation'));
  if (!moduleEnabled) {
    return [{
      id: 'MODULE_SELECTED',
      label: 'Identity protection package',
      status: 'PENDING',
      message: 'Optional package is not selected for this client.',
    }];
  }

  const vpcEnabled = effectiveEnabled(form.vpc, defaults.vpc);
  const directoryEnabled = effectiveEnabled(form.directoryService, defaults.directoryService);
  const workspacesEnabled = effectiveEnabled(form.workspaces, defaults.workspaces);
  const minimumCapacity = Number.parseInt(positiveIntegerString(form.duoProxyMinimumCapacity, '0'), 10);
  const desiredCapacity = Number.parseInt(positiveIntegerString(form.duoProxyDesiredCapacity, '0'), 10);
  const maximumCapacity = Number.parseInt(positiveIntegerString(form.duoProxyMaximumCapacity, '0'), 10);
  const rootVolumeGib = Number.parseInt(positiveIntegerString(form.duoProxyRootVolumeGib, '0'), 10);
  const supportedProxyInstanceType = DUO_PROXY_INSTANCE_TYPES.includes(
    form.duoProxyInstanceType.trim().toLowerCase() as (typeof DUO_PROXY_INSTANCE_TYPES)[number],
  );
  const proxyConfigurationComplete = duoProxyConfigurationReady(form);
  const samlConfigurationComplete = duoProviderConfigurationReady(form);
  const proxyDeploymentRequested = workSpacesIdentityProxyDeploymentRequested(form);
  const samlActivationRequested = workSpacesIdentitySamlActivationRequested(form);

  const checks: IdentityReadinessCheck[] = [
    {
      id: 'L2_PROFILE_ENABLED',
      label: 'CMMC L2 profile',
      status: form.cmmcLevel === 'L2' ? 'PASS' : 'BLOCKED',
      message: form.cmmcLevel === 'L2' ? 'L2 enclave profile selected.' : 'WorkSpaces identity protection requires an L2 enclave profile.',
    },
    {
      id: 'VPC_ENABLED',
      label: 'Enclave network',
      status: vpcEnabled ? 'PASS' : 'BLOCKED',
      message: vpcEnabled ? 'VPC/enclave networking is enabled.' : 'Enable the VPC/enclave network first.',
    },
    {
      id: 'DIRECTORY_SERVICE_ENABLED',
      label: 'AWS Directory Service',
      status: directoryEnabled ? 'PASS' : 'BLOCKED',
      message: directoryEnabled ? 'Directory Service is enabled.' : 'Enable Directory Service first.',
    },
    {
      id: 'DIRECTORY_TYPE_SUPPORTED',
      label: 'Directory type',
      status: form.directoryType === 'aws_managed_microsoft_ad' ? 'PASS' : 'BLOCKED',
      message: form.directoryType === 'aws_managed_microsoft_ad' ? 'AWS Managed Microsoft AD selected.' : 'The first release supports AWS Managed Microsoft AD only.',
    },
    {
      id: 'WORKSPACES_ENABLED',
      label: 'Amazon WorkSpaces',
      status: workspacesEnabled ? 'PASS' : 'BLOCKED',
      message: workspacesEnabled ? 'WorkSpaces is enabled.' : 'Enable WorkSpaces first.',
    },
    {
      id: 'WORKSPACES_DIRECTORY_REGISTERED',
      label: 'WorkSpaces directory registration',
      status: form.workspacesRegistrationEnabled ? 'PASS' : 'BLOCKED',
      message: form.workspacesRegistrationEnabled ? 'Directory registration is enabled.' : 'Enable WorkSpaces directory registration.',
    },
    {
      id: 'PROVIDER_SUPPORTED',
      label: 'Identity provider adapter',
      status: form.workspacesIdentityProvider === 'duo' ? 'PASS' : 'BLOCKED',
      message: form.workspacesIdentityProvider === 'duo' ? 'Cisco Duo adapter selected.' : 'This provider is reserved for a later release.',
    },
    {
      id: 'WINDOWS_SERVER_2025',
      label: 'Proxy operating system',
      status: validDuoImageParameter(form.duoProxyAmiSsmParameter) ? 'PASS' : 'BLOCKED',
      message: validDuoImageParameter(form.duoProxyAmiSsmParameter) ? 'Image source selected; deployment verifies ownership, encryption, profile, and installer checksum.' : 'Select the approved Windows Server 2025 public parameter or a versioned client-owned Duo image parameter.',
    },
    {
      id: 'PROXY_INSTANCE_SIZE',
      label: 'Proxy EC2 instance size',
      status: supportedProxyInstanceType ? 'PASS' : 'BLOCKED',
      message: supportedProxyInstanceType
        ? `${form.duoProxyInstanceType.trim()} selected.`
        : 'Select an approved t3 or t3a x86 burstable size.',
    },
    {
      id: 'PROXY_ROOT_VOLUME',
      label: 'Encrypted proxy storage',
      status: rootVolumeGib >= 40 ? 'PASS' : 'BLOCKED',
      message: rootVolumeGib >= 40 ? `${rootVolumeGib} GiB encrypted gp3 root volume configured.` : 'Configure at least 40 GiB for Windows, updates, and logs.',
    },
    {
      id: 'PROXY_AUTO_SCALING',
      label: 'Persistent proxy capacity',
      status: minimumCapacity >= 1
        && minimumCapacity <= desiredCapacity
        && desiredCapacity < maximumCapacity
        && maximumCapacity <= 4
        ? 'PASS'
        : 'BLOCKED',
      message: minimumCapacity >= 1
        && minimumCapacity <= desiredCapacity
        && desiredCapacity < maximumCapacity
        && maximumCapacity <= 4
        ? `${desiredCapacity} persistent proxy server${desiredCapacity === 1 ? '' : 's'} configured with temporary replacement headroom to ${maximumCapacity}.`
        : 'Set minimum at or below desired, and maximum at least one higher than desired (maximum 4).',
    },
    {
      id: 'DISTINCT_AVAILABILITY_ZONES',
      label: 'Two private subnets in separate AZs',
      status: 'PENDING',
      message: 'Live AWS preflight must confirm two eligible private subnets in distinct Availability Zones.',
    },
    {
      id: 'OPTIONAL_TEST_USER',
      label: 'Optional test user validation',
      status: form.workspacesIdentityTestUserName.trim() ? 'PENDING' : 'PASS',
      message: form.workspacesIdentityTestUserName.trim()
        ? `Live AWS/AD preflight will validate ${form.workspacesIdentityTestUserName.trim()}, including sAMAccountName and mail attributes.`
        : 'No test user selected. This is optional until post-installation authentication validation.',
    },
    {
      id: 'PROXY_DEPLOYMENT_CONFIGURATION',
      label: 'Stage 2 · Duo Authentication Proxy',
      status: proxyConfigurationComplete
        ? 'PASS'
        : proxyDeploymentRequested ? 'BLOCKED' : 'PENDING',
      message: proxyConfigurationComplete
        ? 'The approved installer and protected Duo proxy onboarding content are ready.'
        : proxyDeploymentRequested
          ? 'Select a verified Duo image or approve an installer, then securely store the RIKEY and generated proxy command.'
          : 'Not required for the static-resource stage. Complete this before selecting proxy deployment.',
    },
    {
      id: 'SAML_ACTIVATION_CONFIGURATION',
      label: 'Stage 3 · WorkSpaces SAML activation',
      status: samlConfigurationComplete
        ? 'PASS'
        : samlActivationRequested ? 'BLOCKED' : 'PENDING',
      message: samlConfigurationComplete
        ? 'The User Access URL and validated SAML metadata are ready. The workflow will promote staged XML and continue automatically.'
        : samlActivationRequested
          ? 'Provide the Duo User Access URL and upload or reference valid SAML metadata. External Duo access settings are verified operationally after deployment.'
          : 'Not required for proxy testing. Supply these values when available and the workflow will advance automatically.',
    },
  ];

  if (samlActivationRequested && form.workspacesIdentityAllowDirectoryLoginFallback) {
    checks.push({
      id: 'FALLBACK_SECURITY_ACKNOWLEDGEMENT',
      label: 'Directory-login fallback acknowledgement',
      status: form.workspacesIdentityFallbackAcknowledged ? 'PASS' : 'BLOCKED',
      message: form.workspacesIdentityFallbackAcknowledged
        ? 'Security exception acknowledged.'
        : 'Acknowledge that unsupported clients can bypass SAML/Duo when directory-login fallback is enabled.',
    });
  }

  return checks;
}


function validateServiceDependencies(form: ClientFormState): string {
  const defaults = deploymentDefaultsForLevel(form.cmmcLevel);
  const vpcEnabled = effectiveEnabled(form.vpc, defaults.vpc);
  const directoryEnabled = effectiveEnabled(form.directoryService, defaults.directoryService);
  const workspacesEnabled = effectiveEnabled(form.workspaces, defaults.workspaces);
  const workspacesProvisioningEnabled = effectiveEnabled(form.workspacesProvisioning, deploymentDefault(form, 'workspacesProvisioning'));
  const cuiFileShareEnabled = effectiveEnabled(form.cuiFileShare, deploymentDefault(form, 'cuiFileShare'));
  const securityHubEnabled = effectiveEnabled(form.securityHub, deploymentDefault(form, 'securityHub'));
  const clientSecurityAlertsEnabled = effectiveEnabled(form.securityHubNotifications, deploymentDefault(form, 'securityHubNotifications'));
  const identityFederationEnabled = effectiveEnabled(form.workspacesIdentityFederation, deploymentDefault(form, 'workspacesIdentityFederation'));
  const proxyDeploymentRequested = workSpacesIdentityProxyDeploymentRequested(form);
  const samlActivationRequested = workSpacesIdentitySamlActivationRequested(form);
  const workspaceDestroyRequested = form.workspaces === 'disabled' && form.workspacesRemovalPolicy === 'destroy' && form.workspacesAllowDestroy;
  const directoryDestroyRequested = form.directoryService === 'disabled' && form.directoryRemovalPolicy === 'destroy' && form.directoryAllowDestroy;

  let parsedWorkSpacesUsers: Record<string, unknown>[] = [];
  try {
    parsedWorkSpacesUsers = parseWorkSpacesProvisioningUsers(form.workspacesProvisioningUsers);
  } catch (error) {
    return error instanceof Error ? error.message : 'WorkSpaces provisioning users are invalid.';
  }

  if (workspacesEnabled && form.cmmcLevel !== 'L2') return 'WorkSpaces can only be enabled for an L2 enclave profile.';
  if (directoryEnabled && !vpcEnabled) return 'Directory Service requires the VPC/enclave network to be enabled or inherited from an L2 profile.';
  if (workspacesEnabled && !directoryEnabled) return 'WorkSpaces cannot be enabled unless Directory Service is enabled or inherited from an L2 profile.';
  if (workspacesEnabled && !form.workspacesRegistrationEnabled) return 'WorkSpaces requires Directory Registration to be enabled.';
  if (workspacesEnabled && form.workspacesDirectoryTypeRequired !== form.directoryType) return 'WorkSpaces directory type requirement must match the selected Directory Service type.';
  if (workspacesProvisioningEnabled && !workspacesEnabled) return 'WorkSpaces provisioning requires WorkSpaces to be enabled.';
  if (workspacesProvisioningEnabled && !form.workspacesRegistrationEnabled) return 'WorkSpaces provisioning requires Directory Registration to be enabled.';
  if (workspacesProvisioningEnabled && parsedWorkSpacesUsers.length === 0) return 'WorkSpaces provisioning requires at least one user.';
  if (workspacesProvisioningEnabled) {
    const bundleError = validateWorkSpacesProvisioningUserBundleIds(parsedWorkSpacesUsers);
    if (bundleError) return bundleError;
  }
  if (proxyDeploymentRequested && !identityFederationEnabled) return 'WorkSpaces identity deployment cannot advance beyond static resources until the optional identity protection package is selected.';
  if (identityFederationEnabled && form.cmmcLevel !== 'L2') return 'WorkSpaces identity protection can only be enabled for an L2 enclave profile.';
  if (identityFederationEnabled && !vpcEnabled) return 'WorkSpaces identity protection requires the VPC/enclave network to be enabled.';
  if (identityFederationEnabled && !directoryEnabled) return 'WorkSpaces identity protection requires Directory Service to be enabled.';
  if (identityFederationEnabled && form.directoryType !== 'aws_managed_microsoft_ad') return 'The first WorkSpaces identity protection release supports AWS Managed Microsoft AD only.';
  if (identityFederationEnabled && !workspacesEnabled) return 'WorkSpaces identity protection requires WorkSpaces to be enabled.';
  if (identityFederationEnabled && !form.workspacesRegistrationEnabled) return 'WorkSpaces identity protection requires WorkSpaces directory registration to be enabled.';
  if (identityFederationEnabled && form.workspacesIdentityProvider !== 'duo') return 'Only the Cisco Duo provider adapter is available in this release. Okta and Microsoft Entra ID are reserved for later releases.';
  if (samlActivationRequested && form.workspacesIdentityAllowDirectoryLoginFallback && !form.workspacesIdentityFallbackAcknowledged) return 'Acknowledge the security impact before allowing directory-login fallback for unsupported WorkSpaces clients.';
  if (identityFederationEnabled) {
    const minimumCapacity = Number.parseInt(positiveIntegerString(form.duoProxyMinimumCapacity, '0'), 10);
    const desiredCapacity = Number.parseInt(positiveIntegerString(form.duoProxyDesiredCapacity, '0'), 10);
    const maximumCapacity = Number.parseInt(positiveIntegerString(form.duoProxyMaximumCapacity, '0'), 10);
    const rootVolumeGib = Number.parseInt(positiveIntegerString(form.duoProxyRootVolumeGib, '0'), 10);
    if (!validDuoImageParameter(form.duoProxyAmiSsmParameter)) return 'Select the approved public Windows Server 2025 parameter or a versioned client-owned Duo image parameter.';
    if (!DUO_PROXY_INSTANCE_TYPES.includes(form.duoProxyInstanceType.trim().toLowerCase() as (typeof DUO_PROXY_INSTANCE_TYPES)[number])) return 'Select an approved x86 burstable instance type for the Duo Authentication Proxy.';
    if (rootVolumeGib < 40) return 'Duo Authentication Proxy instances require at least 40 GiB of encrypted gp3 root storage.';
    if (desiredCapacity < 1 || desiredCapacity > 3 || minimumCapacity !== desiredCapacity || maximumCapacity !== desiredCapacity + 1) return 'Duo proxy capacity requires minimum = desired, desired between 1 and 3, and maximum = desired + 1.';
    // Proxy profiles are protected state in the tooling staging secret or the client runtime secret.
    // Browser draft rows are intentionally transient and may contain only the next profile being edited.
    // The add/retire APIs and the deployment coordinator validate the authoritative profile topology.
  }
  if (proxyDeploymentRequested) {
    if (!duoProxyConfigurationReady(form)) return 'Select an image with verified Duo or upload and approve the installer, then store the Duo-generated proxy onboarding content before authorizing proxy deployment.';
    if (!form.duoProxyOnboardingConfigured) return 'Confirm that Duo-generated proxy onboarding content has been stored in the managed secret or ARN override.';
    if (!form.duoDeploymentPlanReady) return `Store a fresh Duo-generated command for every profile required by the deployment plan${form.duoDeploymentPlanMissingCommands ? `: ${form.duoDeploymentPlanMissingCommands}` : ''}.`;
    if (form.duoProxyEnrollmentSecretArn.trim() && !/^arn:[^:]+:secretsmanager:[^:]+:\d{12}:secret:.+/.test(form.duoProxyEnrollmentSecretArn.trim())) return 'Proxy secret override must be a valid Secrets Manager ARN, or leave it blank to use the stack-managed secret.';
    if (!form.duoImageInstallerEmbedded && form.duoInstallerSourceMode === 'managed_upload') {
      if (form.duoInstallerUploadStatus !== 'APPROVED' || form.duoInstallerMalwareScanStatus !== 'NO_THREATS_FOUND') return 'Upload the Duo Authentication Proxy installer and wait for malware scanning and approval before deployment.';
    }
    if (!form.duoImageInstallerEmbedded && !form.duoInstallerS3Uri.trim().startsWith('s3://')) return 'Duo deployment readiness requires an approved installer object.';
    if (!form.duoImageInstallerEmbedded && !/^[0-9a-fA-F]{64}$/.test(form.duoInstallerSha256.trim())) return 'Duo deployment readiness requires a verified installer SHA-256 digest.';
  }
  if (samlActivationRequested && !duoProviderConfigurationReady(form)) {
    return 'WorkSpaces SAML activation requires a valid Duo User Access URL and SAML metadata upload or approved metadata reference.';
  }
  if (cuiFileShareEnabled && form.cmmcLevel !== 'L2') return 'CUI File Share can only be enabled for an L2 enclave profile.';
  if (cuiFileShareEnabled && !vpcEnabled) return 'CUI File Share requires the VPC/enclave network to be enabled or inherited from the L2 profile.';
  if (cuiFileShareEnabled && !directoryEnabled) return 'CUI File Share requires Directory Service to be enabled or inherited from the L2 profile.';
  if (cuiFileShareEnabled && form.directoryType !== 'aws_managed_microsoft_ad') return 'CUI File Share requires AWS Managed Microsoft AD for FSx for Windows File Server.';
  if (cuiFileShareEnabled && !workspacesEnabled) return 'CUI File Share requires WorkSpaces to be enabled.';
  if (cuiFileShareEnabled && !workspacesProvisioningEnabled) return 'CUI File Share requires Desktop Provisioning to be enabled so mapped drives and user folders can be prepared.';
  if (cuiFileShareEnabled && parsedWorkSpacesUsers.length === 0) return 'CUI File Share requires at least one WorkSpaces provisioning user.';
  if (cuiFileShareEnabled && !form.cuiFileShareRetainOnDelete) return 'CUI File Share must retain FSx on delete from the Admin Portal. Use an approved teardown workflow with allow_destroy=true before destructive deletion.';
  if (cuiFileShareEnabled && Number.parseInt(positiveIntegerString(form.cuiFileShareStorageCapacityGib, '32'), 10) < 32) return 'CUI File Share storage size must be at least 32 GiB.';
  if (cuiFileShareEnabled && Number.parseInt(positiveIntegerString(form.cuiFileShareThroughputCapacityMbps, '8'), 10) < 8) return 'CUI File Share throughput must be at least 8 MB/s.';
  if (cuiFileShareEnabled) {
    const backupRetentionDays = Number.parseInt(positiveIntegerString(form.cuiFileShareBackupRetentionDays, '7'), 10);
    if (backupRetentionDays < 1 || backupRetentionDays > 90) return 'CUI File Share backup retention days must be between 1 and 90 so automatic FSx backups remain enabled.';
  }
  if (clientSecurityAlertsEnabled && !securityHubEnabled) return 'Client Security Alerts require Security Hub to be enabled or inherited from the L2 profile.';
  if (clientSecurityAlertsEnabled && ['email', 'email_and_webhook'].includes(form.securityHubNotificationDeliveryChannel) && !form.securityHubNotificationEmail.trim() && !form.notificationEmails.trim()) return 'Client Security Alerts require a target client email or client notification email.';
  if (clientSecurityAlertsEnabled && ['webhook', 'email_and_webhook'].includes(form.securityHubNotificationDeliveryChannel) && !form.securityHubNotificationWebhookSecretArn.trim()) return 'Client Security Alerts webhook delivery requires a Secrets Manager webhook secret ARN.';
  if (directoryDestroyRequested && !workspaceDestroyRequested) return 'Directory Service deletion requires WorkSpaces to be disabled with removal policy destroy and allow destroy enabled first.';
  return '';
}

function setNested(target: Record<string, unknown>, path: string[], fieldValue: unknown): void {
  let current = target;
  for (const segment of path.slice(0, -1)) {
    const existing = current[segment];
    if (typeof existing !== 'object' || existing === null || Array.isArray(existing)) {
      current[segment] = {};
    }
    current = current[segment] as Record<string, unknown>;
  }
  current[path[path.length - 1]] = fieldValue;
}

function implicitAdminDeploymentAuthorization(form: ClientFormState): { approvalId: string; approvedAt: string; approvedBy: string } {
  return {
    approvalId: form.workspacesIdentityApprovalId.trim() || 'admin-portal-deployment-implicit',
    approvedAt: form.workspacesIdentityApprovedAt.trim() || new Date().toISOString(),
    approvedBy: form.workspacesIdentityApprovedBy.trim() || getSignedInUserDisplayName('SURELINC administrator'),
  };
}

function buildOverrides(form: ClientFormState): Record<string, unknown> {
  const overrides: Record<string, unknown> = {};
  for (const [profile, value] of [['duo-proxy', form.duoServerImage], ['windows-management', form.managementServerImage]]) {
    if (!value) continue;
    setNested(overrides, ['server_images', profile], value === 'BASE' || value === 'DEFAULT' ? { mode: value } : { mode: 'EXPLICIT', image_id: value });
  }
  const approvalEvidence = implicitAdminDeploymentAuthorization(form);
  const identityProtectionEnabled = effectiveEnabled(
    form.workspacesIdentityFederation,
    deploymentDefault(form, 'workspacesIdentityFederation'),
  );
  const effectiveStage = effectiveWorkSpacesIdentityDeploymentStage(form);
  const proxyDeploymentRequested = effectiveStage === 'PROXY_ONLY' || effectiveStage === 'SAML_ACTIVATION';
  const samlActivationRequested = effectiveStage === 'SAML_ACTIVATION';
  // Stage 2 requires the approved installer and protected proxy onboarding input.
  // Full Duo SAML configuration is required only for Stage 3.
  const providerConfigurationComplete = effectiveStage === 'SAML_ACTIVATION'
    ? duoProviderConfigurationReady(form)
    : effectiveStage === 'PROXY_ONLY' && duoProxyConfigurationReady(form);
  setNested(overrides, ['report_generation', 'enabled'], overridePayload(form.reportGeneration, true));
  setNested(overrides, ['client_user_management', 'enabled'], overridePayload(form.clientUsersManagement, true));
  setNested(overrides, ['client_evidence_workflow', 'enabled'], overridePayload(form.clientEvidenceWorkflow, false));
  setNested(overrides, ['security_services', 'guardduty', 'enabled'], overridePayload(form.guardDuty, deploymentDefault(form, 'guardDuty')));
  setNested(overrides, ['security_services', 'guardduty', 'enable_runtime_monitoring'], overridePayload(form.guardDutyRuntimeMonitoring, deploymentDefault(form, 'guardDutyRuntimeMonitoring')));
  setNested(overrides, ['security_services', 'security_hub', 'enabled'], overridePayload(form.securityHub, deploymentDefault(form, 'securityHub')));
  setNested(overrides, ['security_services', 'aws_inspector', 'enabled'], overridePayload(form.inspector, deploymentDefault(form, 'inspector')));
  setNested(overrides, ['security_services', 'security_hub_notifications', 'enabled'], overridePayload(form.securityHubNotifications, deploymentDefault(form, 'securityHubNotifications')));
  setNested(overrides, ['security_services', 'security_hub_notifications', 'email'], form.securityHubNotificationEmail.trim() || null);
  setNested(overrides, ['security_services', 'security_hub_notifications', 'delivery_channel'], form.securityHubNotificationDeliveryChannel);
  setNested(overrides, ['security_services', 'security_hub_notifications', 'webhook_secret_arn'], form.securityHubNotificationWebhookSecretArn.trim() || null);
  setNested(overrides, ['security_services', 'security_hub_notifications', 'notify_surelinc'], form.securityHubNotificationNotifySurelinc);
  setNested(overrides, ['security_services', 'security_hub_notifications', 'minimum_severity'], form.securityHubNotificationMinimumSeverity);
  setNested(overrides, ['security_services', 'security_hub_notifications', 'workflow_statuses'], ['NEW']);
  setNested(overrides, ['security_services', 'security_hub_notifications', 'source'], 'securityhub');
  setNested(overrides, ['vpc', 'enabled'], overridePayload(form.vpc, deploymentDefault(form, 'vpc')));
  setNested(overrides, ['vpc', 'endpoints', 'gateway', 's3', 'enabled'], overridePayload(form.vpcEndpointGatewayS3, deploymentDefault(form, 'endpointGatewayS3')));
  setNested(overrides, ['vpc', 'endpoints', 'gateway', 'dynamodb', 'enabled'], overridePayload(form.vpcEndpointGatewayDynamoDb, deploymentDefault(form, 'endpointGatewayDynamoDb')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'ssm', 'enabled'], overridePayload(form.vpcEndpointInterfaceSsm, deploymentDefault(form, 'endpointInterfaceSsm')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'ssmmessages', 'enabled'], overridePayload(form.vpcEndpointInterfaceSsmMessages, deploymentDefault(form, 'endpointInterfaceSsmMessages')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'ec2messages', 'enabled'], overridePayload(form.vpcEndpointInterfaceEc2Messages, deploymentDefault(form, 'endpointInterfaceEc2Messages')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'kms', 'enabled'], overridePayload(form.vpcEndpointInterfaceKms, deploymentDefault(form, 'endpointInterfaceKms')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'logs', 'enabled'], overridePayload(form.vpcEndpointInterfaceLogs, deploymentDefault(form, 'endpointInterfaceLogs')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'monitoring', 'enabled'], overridePayload(form.vpcEndpointInterfaceMonitoring, deploymentDefault(form, 'endpointInterfaceMonitoring')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'ec2', 'enabled'], overridePayload(form.vpcEndpointInterfaceEc2, deploymentDefault(form, 'endpointInterfaceEc2')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'sts', 'enabled'], overridePayload(form.vpcEndpointInterfaceSts, deploymentDefault(form, 'endpointInterfaceSts')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'secretsmanager', 'enabled'], overridePayload(form.vpcEndpointInterfaceSecretsManager, deploymentDefault(form, 'endpointInterfaceSecretsManager')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'ecr_api', 'enabled'], overridePayload(form.vpcEndpointInterfaceEcrApi, deploymentDefault(form, 'endpointInterfaceEcrApi')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'ecr_dkr', 'enabled'], overridePayload(form.vpcEndpointInterfaceEcrDkr, deploymentDefault(form, 'endpointInterfaceEcrDkr')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'workspaces', 'enabled'], overridePayload(form.vpcEndpointInterfaceWorkSpaces, deploymentDefault(form, 'endpointInterfaceWorkSpaces')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'inspector2-telemetry', 'enabled'], identityProtectionEnabled ? true : overridePayload(form.vpcEndpointInterfaceInspector2Telemetry, deploymentDefault(form, 'endpointInterfaceInspector2Telemetry')));
  setNested(overrides, ['vpc', 'endpoints', 'interface', 'workspaces_web', 'enabled'], overridePayload(form.vpcEndpointInterfaceWorkSpacesWeb, deploymentDefault(form, 'endpointInterfaceWorkSpacesWeb')));
  setNested(overrides, ['directory_service', 'enabled'], overridePayload(form.directoryService, deploymentDefault(form, 'directoryService')));
  setNested(overrides, ['directory_service', 'type'], form.directoryType);
  setNested(overrides, ['directory_service', 'edition'], form.directoryEdition);
  setNested(overrides, ['directory_service', 'allowed_types'], ['aws_managed_microsoft_ad', 'simple_ad']);
  setNested(overrides, ['directory_service', 'allowed_editions'], ['standard', 'enterprise']);
  setNested(overrides, ['directory_service', 'default_type'], 'aws_managed_microsoft_ad');
  setNested(overrides, ['directory_service', 'default_edition'], 'standard');
  setNested(overrides, ['directory_service', 'name'], form.directoryName.trim() || 'cmmc.local');
  setNested(overrides, ['directory_service', 'short_name'], form.directoryShortName.trim() || 'CMMC');
  setNested(overrides, ['directory_service', 'removal_policy'], form.directoryRemovalPolicy);
  setNested(overrides, ['directory_service', 'allow_destroy'], form.directoryAllowDestroy);
  setNested(overrides, ['directory_service', 'destroy_guard_enabled'], form.directoryDestroyGuardEnabled);
  setNested(overrides, ['directory_service', 'fail_destroy_if_registered_with_workspaces'], form.directoryFailDestroyIfRegistered);
  const parsedWorkSpacesProvisioningUsers = parseWorkSpacesProvisioningUsers(form.workspacesProvisioningUsers);
  setNested(overrides, ['workspaces', 'enabled'], overridePayload(form.workspaces, deploymentDefault(form, 'workspaces')));
  setNested(overrides, ['workspaces', 'registration_enabled'], form.workspacesRegistrationEnabled);
  setNested(overrides, ['workspaces', 'bundle_type'], form.workspacesBundleType.trim() || 'value');
  setNested(overrides, ['workspaces', 'running_mode'], form.workspacesRunningMode);
  setNested(overrides, ['workspaces', 'root_volume_encryption_enabled'], form.workspacesRootVolumeEncryption);
  setNested(overrides, ['workspaces', 'user_volume_encryption_enabled'], form.workspacesUserVolumeEncryption);
  setNested(overrides, ['workspaces', 'directory_type_required'], form.workspacesDirectoryTypeRequired.trim() || 'aws_managed_microsoft_ad');
  setNested(overrides, ['workspaces', 'removal_policy'], form.workspacesRemovalPolicy);
  setNested(overrides, ['workspaces', 'allow_destroy'], form.workspacesAllowDestroy);
  setNested(overrides, ['workspaces', 'provisioning', 'enabled'], overridePayload(form.workspacesProvisioning, deploymentDefault(form, 'workspacesProvisioning')));
  setNested(overrides, ['workspaces', 'provisioning', 'users'], parsedWorkSpacesProvisioningUsers);
  setNested(overrides, ['workspaces', 'identity_federation', 'enabled'], overridePayload(form.workspacesIdentityFederation, deploymentDefault(form, 'workspacesIdentityFederation')));
  setNested(overrides, ['workspaces', 'identity_federation', 'paid_add_on'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment_enabled'], proxyDeploymentRequested);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'static_resources_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'stack_name'], 'Surelinc-CMMC-Client-WorkSpaces-Identity-Deployment-Stack');
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'stage'], effectiveStage);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'proxy_deployment_requested'], proxyDeploymentRequested);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'saml_activation_requested'], samlActivationRequested);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'provider_configuration_complete'], providerConfigurationComplete);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approved'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approval_id'], approvalEvidence.approvalId);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approved_at'], approvalEvidence.approvedAt);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approved_by'], approvalEvidence.approvedBy);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'client_authorization', 'authorization_mode'], 'ADMIN_PORTAL_DEPLOYMENT_IMPLICIT');
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'workflow', 'deployment_state_machine_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'workflow', 'rollback_state_machine_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'workflow', 'rollback_on_failure'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'workflow', 'retain_logs_on_failure'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'deployment', 'workflow', 'enable_saml_only_after_proxy_validation'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider'], 'duo');
  setNested(overrides, ['workspaces', 'identity_federation', 'integration_mode'], 'saml_sso');
  setNested(overrides, ['workspaces', 'identity_federation', 'status'], samlActivationRequested
    ? 'SAML_ACTIVATION_REQUESTED'
    : proxyDeploymentRequested
      ? 'PROXY_DEPLOYMENT_REQUESTED'
      : providerConfigurationComplete
        ? 'READY_FOR_SAML_ACTIVATION'
        : effectiveEnabled(form.workspacesIdentityFederation, deploymentDefault(form, 'workspacesIdentityFederation')) ? 'STATIC_RESOURCES_ONLY' : 'NOT_SELECTED');
  setNested(overrides, ['workspaces', 'identity_federation', 'supported_providers'], ['duo']);
  setNested(overrides, ['workspaces', 'identity_federation', 'roadmap_providers'], ['okta', 'microsoft_entra_id']);
  setNested(overrides, ['workspaces', 'identity_federation', 'dependencies', 'require_l2_profile'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'dependencies', 'require_vpc'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'dependencies', 'require_workspaces'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'dependencies', 'require_workspaces_directory_registration'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'dependencies', 'require_directory_service'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'dependencies', 'allowed_directory_types'], ['aws_managed_microsoft_ad']);
  setNested(overrides, ['workspaces', 'identity_federation', 'dependencies', 'require_two_private_subnets_in_distinct_availability_zones'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'saml', 'status'], samlActivationRequested ? 'ACTIVATION_REQUESTED' : 'DISABLED');
  setNested(overrides, ['workspaces', 'identity_federation', 'saml', 'allow_directory_login_fallback'], form.workspacesIdentityAllowDirectoryLoginFallback);
  setNested(overrides, ['workspaces', 'identity_federation', 'saml', 'fallback_security_acknowledged'], form.workspacesIdentityFallbackAcknowledged);
  setNested(overrides, ['workspaces', 'identity_federation', 'saml', 'relay_state_parameter_name'], 'RelayState');
  setNested(overrides, ['workspaces', 'identity_federation', 'saml', 'name_id_attribute'], 'sAMAccountName');
  setNested(overrides, ['workspaces', 'identity_federation', 'saml', 'name_id_format'], 'persistent');
  setNested(overrides, ['workspaces', 'identity_federation', 'saml', 'test_user_name'], form.workspacesIdentityTestUserName.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'configuration_validation_required'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'live_aws_validation_required'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'provider_readiness_validation_required'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'block_deployment_on_error'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'stack_name'], 'Surelinc-CMMC-Client-WorkSpaces-Identity-Preflight-Stack');
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'require_test_user'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'require_existing_workspace'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'memory_size_mb'], 256);
  setNested(overrides, ['workspaces', 'identity_federation', 'preflight', 'timeout_seconds'], 90);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'status'], providerConfigurationComplete ? 'READY_FOR_SAML_ACTIVATION' : duoProxyConfigurationReady(form) ? 'PROXY_READY' : 'CONFIGURING');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'edition'], form.duoEdition);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'licensed_user_count'], Number.parseInt(nonNegativeIntegerString(form.duoLicensedUserCount, '0'), 10));
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'mode'], 'MSP_SUBACCOUNT');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'subaccount_action'], form.duoSubaccountId.trim() ? 'USE_EXISTING' : 'CREATE');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'subaccount_id'], form.duoSubaccountId.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'api_hostname'], form.duoApiHostname.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'api_hostname_required'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'provisioning_mode'], 'CATALOG_MANUAL');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_source_mode'], form.duoMetadataSourceMode.toUpperCase());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_url'], form.duoMetadataSourceMode === 'https_url' ? form.duoMetadataUrl.trim() : '');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_s3_uri'], form.duoMetadataSourceMode === 'https_url' ? '' : form.duoMetadataS3Uri.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_staged_s3_uri'], form.duoMetadataSourceMode === 'managed_upload' ? form.duoMetadataStagedS3Uri.trim() : '');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_staged_version_id'], form.duoMetadataSourceMode === 'managed_upload' ? form.duoMetadataStagedVersionId.trim() : '');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_staging_status'], form.duoMetadataSourceMode === 'managed_upload' && form.duoMetadataStagedS3Uri.trim() ? 'STAGED' : form.duoMetadataObjectStatus);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_sha256'], form.duoMetadataSha256.trim().toLowerCase());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_object_verified'], form.duoMetadataSourceMode === 'https_url' ? true : form.duoMetadataObjectVerified);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_object_status'], form.duoMetadataSourceMode === 'https_url' ? 'HTTPS_SOURCE' : form.duoMetadataObjectStatus);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'user_access_url'], form.duoUserAccessUrl.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'configured'], Boolean(form.duoUserAccessUrl.trim().startsWith('https://') && (form.duoMetadataSourceMode === 'https_url' ? form.duoMetadataUrl.trim().startsWith('https://') : (form.duoMetadataS3Uri.trim().startsWith('s3://') || form.duoMetadataStagedS3Uri.trim().startsWith('s3://')))));
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'user_access_granted'], form.duoUserAccessGranted);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'readiness_mode'], 'AUTOMATIC_RUNTIME_VALIDATION');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'permitted_email_domains'], inferredDuoEmailDomains(form));
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'permitted_email_domain_verified'], form.duoEmailDomainVerified);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'type'], 'AWS_MANAGED_MICROSOFT_AD');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'email_attribute'], 'mail');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'duo_username_attribute'], 'sAMAccountName');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'authentication_type'], 'INTEGRATED');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'transport'], 'CLEAR');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'configured'], form.duoAuthenticationSourceConfigured);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'readiness_mode'], 'AUTOMATIC_RUNTIME_VALIDATION');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'proxy_secret_mode'], form.duoProxyEnrollmentSecretArn.trim() ? 'OVERRIDE' : 'MANAGED');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'proxy_onboarding_configured'], form.duoProxyOnboardingConfigured);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'proxy_enrollment_secret_arn'], form.duoProxyEnrollmentSecretArn.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'service_account_required'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'service_account_secret_arn'], form.duoServiceAccountSecretArn.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'operating_system'], 'WINDOWS_SERVER_2025');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'windows_ami_ssm_parameter'], form.duoProxyAmiSsmParameter.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'instance_type'], form.duoProxyInstanceType.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'root_volume_size_gib'], Number.parseInt(positiveIntegerString(form.duoProxyRootVolumeGib, '40'), 10));
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'root_volume_type'], 'gp3');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'root_volume_encrypted'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'assign_public_ip'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'ssm_only_administration'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'minimum_capacity'], Number.parseInt(positiveIntegerString(form.duoProxyMinimumCapacity, '2'), 10));
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'desired_capacity'], Number.parseInt(positiveIntegerString(form.duoProxyDesiredCapacity, '2'), 10));
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'maximum_capacity'], Number.parseInt(positiveIntegerString(form.duoProxyMaximumCapacity, '3'), 10));
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'require_distinct_availability_zones'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'instance_refresh_enabled'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'lifecycle_validation_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'patch_management_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'cloudwatch_logging_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'health_alarms_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'retain_on_disable'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'allow_destroy'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'static_resources_enabled'], true);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'source_mode'], form.duoInstallerSourceMode.toUpperCase());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'upload_status'], form.duoInstallerUploadStatus);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'malware_scan_status'], form.duoInstallerMalwareScanStatus);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'upload_id'], form.duoInstallerUploadId);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'quarantine_s3_uri'], form.duoInstallerQuarantineS3Uri);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 's3_uri'], form.duoInstallerS3Uri.trim());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'sha256'], form.duoInstallerSha256.trim().toLowerCase());
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 's3_version_id'], form.duoInstallerVersionId);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'file_name'], form.duoInstallerFileName);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'size_bytes'], Number.parseInt(form.duoInstallerSizeBytes || '0', 10) || 0);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'minimum_version'], form.duoMinimumProxyVersion.trim() || '6.5.1');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'okta', 'enabled'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'okta', 'status'], 'ROADMAP');
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'microsoft_entra_id', 'enabled'], false);
  setNested(overrides, ['workspaces', 'identity_federation', 'provider_settings', 'microsoft_entra_id', 'status'], 'ROADMAP');
  setNested(overrides, ['backup', 'enabled'], overridePayload(form.backup, deploymentDefault(form, 'backup')));
  setNested(overrides, ['ssm', 'enabled'], overridePayload(form.ssm, deploymentDefault(form, 'ssm')));
  setNested(overrides, ['ssm', 'inventory'], overridePayload(form.ssmInventory, deploymentDefault(form, 'ssmInventory')));
  setNested(overrides, ['ssm', 'patch_manager'], overridePayload(form.ssmPatchManager, deploymentDefault(form, 'ssmPatchManager')));
  setNested(overrides, ['cui_storage', 'enabled'], overridePayload(form.cuiStorage, deploymentDefault(form, 'cuiStorage')));
  setNested(overrides, ['cui_files_access', 'enabled'], false);
  setNested(overrides, ['cui_file_share', 'enabled'], overridePayload(form.cuiFileShare, deploymentDefault(form, 'cuiFileShare')));
  setNested(overrides, ['cui_file_share', 'provider'], 'fsx_windows');
  setNested(overrides, ['cui_file_share', 'share_name'], form.cuiFileShareShareName.trim() || 'CUI');
  setNested(overrides, ['cui_file_share', 'drive_letter'], normalizeDriveLetter(form.cuiFileShareDriveLetter || 'Z:'));
  setNested(overrides, ['cui_file_share', 'storage_capacity_gib'], Number.parseInt(positiveIntegerString(form.cuiFileShareStorageCapacityGib, '32'), 10));
  setNested(overrides, ['cui_file_share', 'throughput_capacity_mbps'], Number.parseInt(positiveIntegerString(form.cuiFileShareThroughputCapacityMbps, '8'), 10));
  setNested(overrides, ['cui_file_share', 'deployment_type'], 'SINGLE_AZ_2');
  setNested(overrides, ['cui_file_share', 'backup_retention_days'], Number.parseInt(nonNegativeIntegerString(form.cuiFileShareBackupRetentionDays, '7'), 10));
  setNested(overrides, ['cui_file_share', 'retain_on_delete'], form.cuiFileShareRetainOnDelete);
  setNested(overrides, ['cui_file_share', 'allow_destroy'], false);
  setNested(overrides, ['cui_file_share', 'create_shared_folder'], form.cuiFileShareCreateSharedFolder);
  setNested(overrides, ['cui_file_share', 'create_user_folders'], form.cuiFileShareCreateUserFolders);
  setNested(overrides, ['cui_file_share', 'map_drive_for_workspaces'], true);
  return overrides;
}

function clientStatus(client: ClientRecord): string {
  return value(client.clientStatus || client.lifecycleStatus || client.status || client.lifecycle);
}

function isActiveClient(client: ClientRecord): boolean {
  return clientStatus(client).toUpperCase() === 'ACTIVE';
}

function clientWorkflowState(client: ClientRecord): ClientWorkflowState | null {
  const state = client.workflowState;
  if (state && typeof state === 'object' && !Array.isArray(state)) return state as ClientWorkflowState;
  return null;
}

function accountWorkflowState(client: ClientRecord, accountId: string): ClientWorkflowState | null {
  const states = client.accountWorkflowStates;
  if (!Array.isArray(states)) return null;
  const match = states.find((state) => (
    state && typeof state === 'object' && String((state as ClientWorkflowState).accountId || '') === accountId
  ));
  return match && typeof match === 'object' ? match as ClientWorkflowState : null;
}

function workflowBadges(client: ClientRecord): string[] {
  const state = clientWorkflowState(client);
  if (!state) return [];
  if (Array.isArray(state.badges) && state.badges.length) {
    return state.badges.map((badge) => String(badge)).filter(Boolean);
  }
  const badges: string[] = [];
  if (state.approvalPending) badges.push('Approval Pending');
  if (state.approved) badges.push('Approved');
  if (state.declined) badges.push('Declined');
  if (state.evidenceRunning) badges.push('Evidence Running');
  if (state.deploymentRunning) badges.push('Deployment Running');
  if (state.fullWorkflowRunning) badges.push('Full Workflow Running');
  if (state.statusReadError) badges.push('Workflow Status Unknown');
  return badges;
}

function workflowSummary(client: ClientRecord): string {
  const state = clientWorkflowState(client);
  if (state?.statusReadError) return state.statusReadError;
  const badges = workflowBadges(client);
  return badges.length ? badges.join(', ') : 'No active workflow';
}

function workflowBadgeClassName(label: string): string {
  const normalized = label.trim().toUpperCase();
  if (normalized.includes('DECLINED')) return 'status-pill status-pill-error';
  if (normalized.includes('PENDING')) return 'status-pill status-pill-warning';
  if (normalized.includes('RUNNING')) return 'status-pill status-pill-info';
  if (normalized.includes('APPROVED')) return 'status-pill status-pill-success';
  if (normalized.includes('UNKNOWN')) return 'status-pill status-pill-muted';
  return statusPillClassName(label);
}

function WorkflowStatusBadges({ client }: { client: ClientRecord }) {
  const badges = workflowBadges(client);
  if (!badges.length) {
    return <span className="status-pill status-pill-muted">No active workflow</span>;
  }
  return (
    <div className="workflow-status-badge-row" title={workflowSummary(client)}>
      {badges.map((badge) => (
        <span key={badge} className={workflowBadgeClassName(badge)}>{badge}</span>
      ))}
    </div>
  );
}

function nextClientStatus(client: ClientRecord): 'ACTIVE' | 'DISABLED' {
  return isActiveClient(client) ? 'DISABLED' : 'ACTIVE';
}

function statusActionLabel(client: ClientRecord): string {
  return isActiveClient(client) ? 'Disable Organization' : 'Re-enable Organization';
}

function formatRunSummary(response: RunClientsResponse): string {
  const skipped = response.skippedCount ? `, skipped ${response.skippedCount}` : '';
  const skippedReasons = Array.isArray(response.skippedClients)
    ? response.skippedClients
        .slice(0, 3)
        .map((item) => {
          const clientId = String(item.clientId || 'client');
          const reason = String(item.reason || '').trim();
          return reason ? `${clientId}: ${reason}` : '';
        })
        .filter(Boolean)
    : [];
  const reasonText = skippedReasons.length ? ` Skipped reason${skippedReasons.length === 1 ? '' : 's'}: ${skippedReasons.join(' | ')}` : '';
  return `${response.message || 'Queued workflow request.'} Batch ID: ${response.batchId}. Queued ${response.queuedCount}${skipped}.${reasonText}`;
}

function rowKey(client: ClientRecord, index: number): string {
  return `${client.clientId || 'client'}-${client.recordType || index}`;
}

function formFromClient(client: ClientRecord): ClientFormState {
  const clientConfig = (typeof client.clientConfig === 'object' && client.clientConfig !== null ? client.clientConfig : {}) as Record<string, unknown>;
  const overrides = recordValue(clientConfig.overrides);
  const cmmcLevel = String(client.cmmcLevel || clientConfig.cmmc_level || 'L2');
  const defaults = deploymentDefaultsForLevel(cmmcLevel);
  const inheritedWorkSpacesUsers = (() => {
    const separatedUsers = workSpacesUsersFromClientRecord(client);
    if (separatedUsers.length) {
      return formatWorkSpacesProvisioningUsers(separatedUsers);
    }
    try {
      return formatWorkSpacesProvisioningUsers(parseWorkSpacesProvisioningUsers(JSON.stringify(nestedValue(overrides, 'workspaces', 'provisioning', 'users') || [])));
    } catch {
      return JSON.stringify(nestedValue(overrides, 'workspaces', 'provisioning', 'users') || [], null, 2);
    }
  })();
  return {
    ...emptyForm,
    clientId: String(client.clientId || ''),
    companyName: String(client.companyName || client.clientName || clientConfig.client_name || ''),
    clientName: String(client.clientName || client.companyName || clientConfig.client_name || ''),
    email: String(client.clientEmail || client.contactEmail || clientConfig.contact_email || ''),
    contactEmail: String(client.contactEmail || client.clientEmail || clientConfig.contact_email || ''),
    clientAccountId: String(client.clientAccountId || clientConfig.client_account_id || ''),
    managementAccountId: String(client.managementAccountId || clientConfig.management_account_id || ''),
    logArchiveAccountId: String(client.logArchiveAccountId || clientConfig.log_archive_account_id || ''),
    region: String(client.region || clientConfig.region || 'us-east-1'),
    cmmcLevel,
    lifecycle: String(client.accountStatus || clientStatus(client)).toUpperCase() === 'ACTIVE' ? 'active' : 'disabled',
    statusReason: String(client.statusReason || ''),
    deploymentModel: String(client.deploymentModel || 'STEP_FUNCTIONS_CODEBUILD'),
    pipelineType: String(client.pipelineType || 'CLIENT'),
    approvalModel: String(client.approvalModel || 'CLIENT_PORTAL_APPROVAL'),
    notificationEmails: emailList(client.notificationEmails || clientConfig.notification_emails),
    clientUsersManagement: overrideChoice(
      nestedValue(overrides, 'client_user_management', 'enabled')
        ?? nestedValue(overrides, 'client_users_management', 'enabled')
        ?? nestedValue(overrides, 'client_portal', 'users_management_enabled')
        ?? nestedValue(overrides, 'client_portal', 'users', 'enabled'),
      true,
    ),
    name: String(client.contactName || client.name || ''),
    address: String(client.address || ''),
    website: String(client.website || ''),
    phone_number: String(client.phoneNumber || client.phone_number || ''),
    reportGeneration: overrideChoice(nestedValue(overrides, 'report_generation', 'enabled'), true),
    clientEvidenceWorkflow: overrideChoice(
      nestedValue(overrides, 'client_evidence_workflow', 'enabled')
        ?? nestedValue(overrides, 'client_evidence_collection', 'enabled')
        ?? nestedValue(overrides, 'client_portal', 'evidence_workflow_enabled')
        ?? nestedValue(overrides, 'client_portal', 'evidence_collection', 'enabled'),
      false,
    ),
    securityHubNotifications: overrideChoice(nestedValue(overrides, 'security_services', 'security_hub_notifications', 'enabled'), defaults.securityHubNotifications),
    securityHubNotificationEmail: String(nestedValue(overrides, 'security_services', 'security_hub_notifications', 'email') || ''),
    securityHubNotificationDeliveryChannel: ['email', 'webhook', 'email_and_webhook'].includes(String(nestedValue(overrides, 'security_services', 'security_hub_notifications', 'delivery_channel') || '')) ? String(nestedValue(overrides, 'security_services', 'security_hub_notifications', 'delivery_channel')) as ClientSecurityAlertDeliveryChannel : 'email',
    securityHubNotificationWebhookSecretArn: String(nestedValue(overrides, 'security_services', 'security_hub_notifications', 'webhook_secret_arn') || ''),
    securityHubNotificationNotifySurelinc: nestedValue(overrides, 'security_services', 'security_hub_notifications', 'notify_surelinc') === true,
    securityHubNotificationMinimumSeverity: String(nestedValue(overrides, 'security_services', 'security_hub_notifications', 'minimum_severity') || 'HIGH') === 'CRITICAL' ? 'CRITICAL' : 'HIGH',
    guardDuty: overrideChoice(nestedValue(overrides, 'security_services', 'guardduty', 'enabled'), defaults.guardDuty),
    guardDutyRuntimeMonitoring: overrideChoice(nestedValue(overrides, 'security_services', 'guardduty', 'enable_runtime_monitoring'), defaults.guardDutyRuntimeMonitoring),
    securityHub: overrideChoice(nestedValue(overrides, 'security_services', 'security_hub', 'enabled'), defaults.securityHub),
    inspector: overrideChoice(nestedValue(overrides, 'security_services', 'aws_inspector', 'enabled'), defaults.inspector),
    vpc: overrideChoice(nestedValue(overrides, 'vpc', 'enabled'), defaults.vpc),
    vpcEndpointGatewayS3: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'gateway', 's3', 'enabled'), defaults.endpointGatewayS3),
    vpcEndpointGatewayDynamoDb: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'gateway', 'dynamodb', 'enabled'), defaults.endpointGatewayDynamoDb),
    vpcEndpointInterfaceSsm: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'ssm', 'enabled'), defaults.endpointInterfaceSsm),
    vpcEndpointInterfaceSsmMessages: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'ssmmessages', 'enabled'), defaults.endpointInterfaceSsmMessages),
    vpcEndpointInterfaceEc2Messages: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'ec2messages', 'enabled'), defaults.endpointInterfaceEc2Messages),
    vpcEndpointInterfaceKms: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'kms', 'enabled'), defaults.endpointInterfaceKms),
    vpcEndpointInterfaceLogs: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'logs', 'enabled'), defaults.endpointInterfaceLogs),
    vpcEndpointInterfaceMonitoring: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'monitoring', 'enabled'), defaults.endpointInterfaceMonitoring),
    vpcEndpointInterfaceEc2: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'ec2', 'enabled'), defaults.endpointInterfaceEc2),
    vpcEndpointInterfaceSts: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'sts', 'enabled'), defaults.endpointInterfaceSts),
    vpcEndpointInterfaceSecretsManager: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'secretsmanager', 'enabled'), defaults.endpointInterfaceSecretsManager),
    vpcEndpointInterfaceEcrApi: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'ecr_api', 'enabled'), defaults.endpointInterfaceEcrApi),
    vpcEndpointInterfaceEcrDkr: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'ecr_dkr', 'enabled'), defaults.endpointInterfaceEcrDkr),
    vpcEndpointInterfaceWorkSpaces: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'workspaces', 'enabled'), defaults.endpointInterfaceWorkSpaces),
    vpcEndpointInterfaceInspector2Telemetry: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'inspector2-telemetry', 'enabled'), defaults.endpointInterfaceInspector2Telemetry),
    vpcEndpointInterfaceWorkSpacesWeb: overrideChoice(nestedValue(overrides, 'vpc', 'endpoints', 'interface', 'workspaces_web', 'enabled'), defaults.endpointInterfaceWorkSpacesWeb),
    cuiStorage: overrideChoice(nestedValue(overrides, 'cui_storage', 'enabled'), defaults.cuiStorage),
    cuiFilesAccess: 'disabled',
    cuiFileShare: overrideChoice(nestedValue(overrides, 'cui_file_share', 'enabled'), defaults.cuiFileShare),
    cuiFileShareStorageCapacityGib: String(nestedValue(overrides, 'cui_file_share', 'storage_capacity_gib') || '32'),
    cuiFileShareThroughputCapacityMbps: String(nestedValue(overrides, 'cui_file_share', 'throughput_capacity_mbps') || '8'),
    cuiFileShareBackupRetentionDays: String(nestedValue(overrides, 'cui_file_share', 'backup_retention_days') ?? '7'),
    cuiFileShareDriveLetter: String(nestedValue(overrides, 'cui_file_share', 'drive_letter') || 'Z:'),
    cuiFileShareShareName: String(nestedValue(overrides, 'cui_file_share', 'share_name') || 'CUI'),
    cuiFileShareCreateSharedFolder: nestedValue(overrides, 'cui_file_share', 'create_shared_folder') !== false,
    cuiFileShareCreateUserFolders: nestedValue(overrides, 'cui_file_share', 'create_user_folders') !== false,
    cuiFileShareRetainOnDelete: nestedValue(overrides, 'cui_file_share', 'retain_on_delete') !== false,
    backup: overrideChoice(nestedValue(overrides, 'backup', 'enabled'), defaults.backup),
    ssm: overrideChoice(nestedValue(overrides, 'ssm', 'enabled'), defaults.ssm),
    ssmInventory: overrideChoice(nestedValue(overrides, 'ssm', 'inventory'), defaults.ssmInventory),
    ssmPatchManager: overrideChoice(nestedValue(overrides, 'ssm', 'patch_manager'), defaults.ssmPatchManager),
    directoryService: overrideChoice(nestedValue(overrides, 'directory_service', 'enabled'), defaults.directoryService),
    directoryType: ['aws_managed_microsoft_ad', 'simple_ad'].includes(String(nestedValue(overrides, 'directory_service', 'type') || '')) ? String(nestedValue(overrides, 'directory_service', 'type')) as DirectoryTypeChoice : 'aws_managed_microsoft_ad',
    directoryEdition: ['standard', 'enterprise'].includes(String(nestedValue(overrides, 'directory_service', 'edition') || '')) ? String(nestedValue(overrides, 'directory_service', 'edition')) as DirectoryEditionChoice : 'standard',
    directoryName: String(nestedValue(overrides, 'directory_service', 'name') || 'cmmc.local'),
    directoryShortName: String(nestedValue(overrides, 'directory_service', 'short_name') || 'CMMC'),
    directoryRemovalPolicy: String(nestedValue(overrides, 'directory_service', 'removal_policy') || 'retain') === 'destroy' ? 'destroy' : 'retain',
    directoryAllowDestroy: nestedValue(overrides, 'directory_service', 'allow_destroy') === true,
    directoryDestroyGuardEnabled: nestedValue(overrides, 'directory_service', 'destroy_guard_enabled') !== false,
    directoryFailDestroyIfRegistered: nestedValue(overrides, 'directory_service', 'fail_destroy_if_registered_with_workspaces') !== false,
    workspaces: overrideChoice(nestedValue(overrides, 'workspaces', 'enabled'), defaults.workspaces),
    workspacesRegistrationEnabled: nestedValue(overrides, 'workspaces', 'registration_enabled') !== false,
    workspacesProvisioning: overrideChoice(nestedValue(overrides, 'workspaces', 'provisioning', 'enabled'), defaults.workspacesProvisioning),
    workspacesProvisioningUsers: inheritedWorkSpacesUsers,
    workspacesBundleType: String(nestedValue(overrides, 'workspaces', 'bundle_type') || 'value'),
    workspacesRunningMode: String(nestedValue(overrides, 'workspaces', 'running_mode') || 'AUTO_STOP') === 'ALWAYS_ON' ? 'ALWAYS_ON' : 'AUTO_STOP',
    workspacesRootVolumeEncryption: nestedValue(overrides, 'workspaces', 'root_volume_encryption_enabled') !== false,
    workspacesUserVolumeEncryption: nestedValue(overrides, 'workspaces', 'user_volume_encryption_enabled') !== false,
    workspacesDirectoryTypeRequired: String(nestedValue(overrides, 'workspaces', 'directory_type_required') || 'aws_managed_microsoft_ad'),
    workspacesRemovalPolicy: String(nestedValue(overrides, 'workspaces', 'removal_policy') || 'retain') === 'destroy' ? 'destroy' : 'retain',
    workspacesAllowDestroy: nestedValue(overrides, 'workspaces', 'allow_destroy') === true,
    workspacesIdentityFederation: overrideChoice(nestedValue(overrides, 'workspaces', 'identity_federation', 'enabled'), defaults.workspacesIdentityFederation),
    workspacesIdentityDeploymentStage: normalizedWorkSpacesIdentityDeploymentStage(
      nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'stage'),
      nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment_enabled') === true,
    ),
    workspacesIdentityDeploymentEnabled: normalizedWorkSpacesIdentityDeploymentStage(
      nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'stage'),
      nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment_enabled') === true,
    ) !== 'STATIC_ONLY',
    workspacesIdentityProviderConfigurationComplete: nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'provider_configuration_complete') === true,
    workspacesIdentityClientAuthorizationApproved: nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approved') === true,
    workspacesIdentityApprovalId: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approval_id') || ''),
    workspacesIdentityApprovedAt: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approved_at') || ''),
    workspacesIdentityApprovedBy: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'client_authorization', 'approved_by') || ''),
    workspacesIdentityProvider: 'duo',
    workspacesIdentityIntegrationMode: 'saml_sso',
    workspacesIdentityAllowDirectoryLoginFallback: nestedValue(overrides, 'workspaces', 'identity_federation', 'saml', 'allow_directory_login_fallback') === true,
    workspacesIdentityFallbackAcknowledged: nestedValue(overrides, 'workspaces', 'identity_federation', 'saml', 'fallback_security_acknowledged') === true,
    workspacesIdentityTestUserName: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'saml', 'test_user_name') || ''),
    duoEdition: ['ESSENTIALS', 'ADVANTAGE', 'PREMIER'].includes(String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'edition') || ''))
      ? String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'edition')) as DuoEditionChoice
      : 'ADVANTAGE',
    duoLicensedUserCount: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'licensed_user_count') ?? '0'),
    duoSubaccountId: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'subaccount_id') || ''),
    duoApiHostname: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'api_hostname') || ''),
    duoApiHostnameRequired: nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'tenant', 'api_hostname_required') === true,
    duoUserAccessUrl: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'user_access_url') || ''),
    duoMetadataSourceMode: ['MANAGED_UPLOAD', 'S3_URI', 'HTTPS_URL'].includes(String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_source_mode') || '').toUpperCase())
      ? String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_source_mode')).toLowerCase() as DuoMetadataSourceMode
      : nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_url') ? 'https_url' : 'managed_upload',
    duoMetadataUrl: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_url') || ''),
    duoMetadataS3Uri: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_s3_uri') || ''),
    duoMetadataStagedS3Uri: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_staged_s3_uri') || ''),
    duoMetadataStagedVersionId: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_staged_version_id') || ''),
    duoMetadataSha256: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_sha256') || ''),
    duoMetadataObjectVerified: Boolean(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_object_verified')),
    duoMetadataObjectStatus: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'metadata_object_status') || ''),
    duoAuthenticationSourceConfigured: nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'configured') === true,
    duoWorkspacesApplicationConfigured: nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'configured') === true,
    duoUserAccessGranted: nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'user_access_granted') === true,
    duoPermittedEmailDomains: Array.isArray(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'permitted_email_domains'))
      ? (nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'permitted_email_domains') as unknown[]).map(String).join(', ')
      : '',
    duoEmailDomainVerified: nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'sso_application', 'permitted_email_domain_verified') === true,
    duoProxyOnboardingConfigured: nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'proxy_onboarding_configured') === true,
    duoDeploymentPlanReady: (nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'proxy_update_plan', 'readyForDeployment') ?? nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'proxy_update_plan', 'readyForApproval')) !== false,
    duoDeploymentPlanMissingCommands: Array.isArray(nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'proxy_update_plan', 'freshCommandRequiredProfileIds'))
      ? (nestedValue(overrides, 'workspaces', 'identity_federation', 'deployment', 'proxy_update_plan', 'freshCommandRequiredProfileIds') as unknown[]).map(String).join(', ')
      : '',
    duoProxyInputs: [newDuoProxyInputDraft(1)],
    duoProxyEnrollmentSecretArn: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'proxy_enrollment_secret_arn') || ''),
    duoServiceAccountSecretArn: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'authentication_source', 'service_account_secret_arn') || ''),
    duoInstallerSourceMode: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'source_mode') || 'MANAGED_UPLOAD').toUpperCase() === 'S3_URI' ? 's3_uri' : 'managed_upload',
    duoInstallerUploadStatus: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'upload_status') || '').toUpperCase() as DuoInstallerUploadStatus,
    duoInstallerMalwareScanStatus: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'malware_scan_status') || ''),
    duoInstallerUploadId: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'upload_id') || ''),
    duoInstallerQuarantineS3Uri: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'quarantine_s3_uri') || ''),
    duoInstallerS3Uri: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 's3_uri') || ''),
    duoInstallerSha256: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'sha256') || ''),
    duoInstallerVersionId: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 's3_version_id') || ''),
    duoInstallerFileName: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'file_name') || ''),
    duoInstallerSizeBytes: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'size_bytes') || ''),
    duoMinimumProxyVersion: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'installer', 'minimum_version') || '6.5.1'),
    duoProxyInstanceType: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'instance_type') || 't3.small'),
    duoProxyAmiSsmParameter: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'windows_ami_ssm_parameter') || '/aws/service/ami-windows-latest/Windows_Server-2025-English-Full-Base'),
    duoImageInstallerEmbedded: nestedValue(overrides, 'server_images', 'duo-proxy', 'installer_embedded') === true,
    duoImageInstallerSha256: String(nestedValue(overrides, 'server_images', 'duo-proxy', 'installer_sha256') || ''),
    duoImageInstallerVersion: String(nestedValue(overrides, 'server_images', 'duo-proxy', 'installer_version') || ''),
    duoServerImage: nestedValue(overrides, 'server_images', 'duo-proxy', 'mode') === 'EXPLICIT' ? String(nestedValue(overrides, 'server_images', 'duo-proxy', 'image_id')) : String(nestedValue(overrides, 'server_images', 'duo-proxy', 'mode') || ''),
    managementServerImage: nestedValue(overrides, 'server_images', 'windows-management', 'mode') === 'EXPLICIT' ? String(nestedValue(overrides, 'server_images', 'windows-management', 'image_id')) : String(nestedValue(overrides, 'server_images', 'windows-management', 'mode') || ''),
    duoProxyRootVolumeGib: String(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'root_volume_size_gib') ?? '40'),
    duoProxyMinimumCapacity: String(Number(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'desired_capacity') ?? 2)),
    duoProxyDesiredCapacity: String(Number(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'desired_capacity') ?? 2)),
    duoProxyMaximumCapacity: String(Math.min(4, Number(nestedValue(overrides, 'workspaces', 'identity_federation', 'provider_settings', 'duo', 'proxy_infrastructure', 'auto_scaling', 'desired_capacity') ?? 2) + 1)),
  };
}

function detailRow(label: string, detailValue: unknown) {
  return (
    <div className="detail-row" key={label}>
      <dt>{label}</dt>
      <dd>{value(detailValue)}</dd>
    </div>
  );
}

function ClientUserManagement({ client, onClose }: { client: ClientRecord; onClose: () => void }) {
  const runtimeConfig = getRuntimeConfig();
  const clientId = String(client.clientId || '').trim();
  const groupOptions = useMemo(() => [
    { value: runtimeConfig.clientAdminGroupName, label: `Client Administrator (${runtimeConfig.clientAdminGroupName})` },
    { value: runtimeConfig.clientReadonlyGroupName, label: `Client Read Only (${runtimeConfig.clientReadonlyGroupName})` },
  ], [runtimeConfig.clientAdminGroupName, runtimeConfig.clientReadonlyGroupName]);

  const emptyUserForm = useMemo<ClientUserFormState>(() => ({
    email: '',
    name: '',
    givenName: '',
    familyName: '',
    groupName: runtimeConfig.clientReadonlyGroupName,
  }), [runtimeConfig.clientReadonlyGroupName]);

  const [users, setUsers] = useState<ClientUserRecord[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [invitingUser, setInvitingUser] = useState(false);
  const [deletingUsername, setDeletingUsername] = useState<string | null>(null);
  const [userForm, setUserForm] = useState<ClientUserFormState>(emptyUserForm);
  const [userMessage, setUserMessage] = useState<string | null>(null);
  const [userError, setUserError] = useState<string | null>(null);

  useEffect(() => {
    setUserForm(emptyUserForm);
  }, [emptyUserForm, clientId]);

  async function loadUsers() {
    if (!clientId) return;
    setLoadingUsers(true);
    setUserError(null);
    try {
      const response = await apiGet<ClientUsersResponse>(`/admin/clients/${encodeURIComponent(clientId)}/users`);
      setUsers(response.users ?? []);
    } catch (err) {
      setUserError(err instanceof Error ? err.message : 'Unable to load client users.');
    } finally {
      setLoadingUsers(false);
    }
  }

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function handleInviteUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!clientId) return;
    setInvitingUser(true);
    setUserMessage(null);
    setUserError(null);
    try {
      const response = await apiPost<CreateClientUserResponse>(`/admin/clients/${encodeURIComponent(clientId)}/users`, {
        email: userForm.email.trim(),
        name: userForm.name.trim() || undefined,
        givenName: userForm.givenName.trim() || undefined,
        familyName: userForm.familyName.trim() || undefined,
        groupName: userForm.groupName,
      });
      setUserMessage(welcomeDeliveryMessage(response, `Invitation created for ${clientUserDisplayName(response.user ?? ({ name: userForm.name, givenName: userForm.givenName, familyName: userForm.familyName } as ClientUserRecord))}.`));
      setUserForm(emptyUserForm);
      await loadUsers();
    } catch (err) {
      setUserError(err instanceof Error ? err.message : 'Unable to invite client user.');
    } finally {
      setInvitingUser(false);
    }
  }

  async function handleDeleteUser(user: ClientUserRecord) {
    const username = String(user.username || user.email || '').trim();
    if (!clientId || !username) return;
    const label = clientUserDisplayName(user);
    const confirmed = window.confirm(`Delete ${label} from the portal for ${client.companyName || client.clientName || clientId}? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingUsername(username);
    setUserMessage(null);
    setUserError(null);
    try {
      const response = await apiDelete<DeleteClientUserResponse>(`/admin/clients/${encodeURIComponent(clientId)}/users/${encodeURIComponent(username)}`);
      setUserMessage(response.message ?? `${label} was deleted.`);
      await loadUsers();
    } catch (err) {
      setUserError(err instanceof Error ? err.message : 'Unable to delete client user.');
    } finally {
      setDeletingUsername(null);
    }
  }

  const clientName = client.companyName || client.clientName || client.clientId || 'client';

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <article className="card modal-card client-users-modal" role="dialog" aria-modal="true" aria-labelledby="client-users-heading" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header grid-header-row">
          <div>
            <h3 id="client-users-heading">Invite Client User</h3>
            <p className="muted-text">{clientName} · {client.clientId}</p>
          </div>
          <div className="button-row modal-header-actions">
            <button className="secondary-button" type="button" onClick={() => void loadUsers()} disabled={loadingUsers}>{loadingUsers ? 'Refreshing...' : 'Refresh Users'}</button>
            <button className="secondary-button" type="button" onClick={onClose} disabled={invitingUser || deletingUsername !== null}>Close</button>
          </div>
        </div>

        <p className="muted-text">Invite additional users for this organization and assign each user a portal role. The platform sends a branded SURELINC welcome email plus the separate Cognito invitation with a temporary password and requires a password change at first sign-in.</p>
        <p className="muted-text">Tell users to expect the Cognito invitation from no-reply@verificationemail.com with subject “SURELINC Readiness Platform access”; it includes the temporary password and may land in Junk or Spam.</p>

      <form className="form-grid jobs-search-grid" onSubmit={handleInviteUser}>
        <label>
          <span className="field-label-title">User email <span className="required-asterisk" aria-label="required">*</span></span>
          <input type="email" required value={userForm.email} onChange={(event) => setUserForm((current) => ({ ...current, email: event.target.value }))} placeholder="user@example.com" />
        </label>
        <label>
          Group
          <select value={userForm.groupName} onChange={(event) => setUserForm((current) => ({ ...current, groupName: event.target.value }))}>
            {groupOptions.map((group) => <option key={group.value} value={group.value}>{group.label}</option>)}
          </select>
        </label>
        <label>
          Display name
          <input value={userForm.name} onChange={(event) => setUserForm((current) => ({ ...current, name: event.target.value }))} placeholder="Jane Doe" />
        </label>
        <label>
          Given name
          <input value={userForm.givenName} onChange={(event) => setUserForm((current) => ({ ...current, givenName: event.target.value }))} placeholder="Jane" />
        </label>
        <label>
          Family name
          <input value={userForm.familyName} onChange={(event) => setUserForm((current) => ({ ...current, familyName: event.target.value }))} placeholder="Doe" />
        </label>
        <div className="form-actions form-wide button-row search-action-row">
          <button className="primary-button" type="submit" disabled={invitingUser}>{invitingUser ? 'Sending invite...' : 'Invite User'}</button>
        </div>
      </form>

      {userMessage && <p className="success-text">{userMessage}</p>}
      {userError && <p className="error-text">{userError}</p>}
      {loadingUsers && <p className="muted-text">Loading users...</p>}
      {!loadingUsers && users.length === 0 && <p className="muted-text">No portal users are currently assigned to this organization.</p>}
      {users.length > 0 && (
        <div className="table-wrap client-users-table-wrap">
          <table className="interactive-table package-summary-table client-users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Group</th>
                <th>Status</th>
                <th>Enabled</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const username = String(user.username || user.email || '');
                return (
                  <tr key={username || user.email}>
                    <td>{clientUserDisplayName(user)}</td>
                    <td>{value(user.groupName || user.groups)}</td>
                    <td><span className={statusPillClassName(user.userStatus)}>{value(user.userStatus)}</span></td>
                    <td>{user.enabled === false ? 'No' : 'Yes'}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <button
                        className="danger-button table-action-button"
                        type="button"
                        disabled={deletingUsername === username}
                        onClick={() => void handleDeleteUser(user)}
                      >
                        {deletingUsername === username ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </article>
    </div>
  );
}

function operationalStatusPillClassName(status: unknown): string {
  const normalized = String(status || '').trim().toUpperCase();
  if (['HEALTHY', 'VERIFIED', 'ONLINE', 'ACTIVE', 'SENT', 'READY'].includes(normalized)) {
    return 'status-pill status-pill-success';
  }
  if (['DEGRADED', 'SSM_OFFLINE', 'UNHEALTHY', 'HASH_MISMATCH', 'READ_FAILED', 'INCOMPLETE'].includes(normalized)) {
    return 'status-pill status-pill-warning';
  }
  if (['FAILED', 'MISSING', 'CONFIGURATION_ERROR', 'REMEDIATION_REQUIRED'].includes(normalized)) {
    return 'status-pill status-pill-error';
  }
  if (['NOT_RECORDED', 'NOT_STARTED', 'NOT_DEPLOYED', 'NOT_CONFIGURED', 'NOT_REQUIRED'].includes(normalized)) {
    return 'status-pill status-pill-muted';
  }
  return statusPillClassName(status);
}

function deploymentStatusDate(value: unknown): string {
  const formatted = formatDate(value);
  return formatted === '—' ? 'Not recorded' : formatted;
}

function DeploymentProxyCard({ proxy }: { proxy: DeploymentProxyStatus }) {
  return (
    <div className="deployment-proxy-status-card">
      <div className="deployment-status-card-header">
        <div>
          <span className="deployment-service-category">{proxy.profileId}</span>
          <strong>{proxy.portalProxyName || friendlyLabel(proxy.profileId)}</strong>
        </div>
        <span className={operationalStatusPillClassName(proxy.status)}>{value(proxy.status)}</span>
      </div>
      <dl>
        {detailRow('Instance', proxy.instanceId || 'Not assigned')}
        {detailRow('EC2 state', proxy.instanceState)}
        {detailRow('SSM', proxy.ssmPingStatus)}
        {detailRow('Auto Scaling', `${value(proxy.lifecycleState)} · ${value(proxy.healthStatus)}`)}
        {detailRow('Assignment', proxy.assignmentStatus)}
        {detailRow('Security disposition', proxy.securityStatus)}
        {detailRow('Security validated', deploymentStatusDate(proxy.securityValidatedAt))}
        {detailRow('Revision', proxy.revision)}
        {proxy.pendingAction && detailRow('Pending action', proxy.pendingAction)}
        {proxy.candidateInstanceId && detailRow('Candidate', proxy.candidateInstanceId)}
        {detailRow('Last applied', deploymentStatusDate(proxy.lastAppliedAt))}
      </dl>
    </div>
  );
}

function duoConfigurationChoiceLabel(rawValue: unknown): string {
  const normalized = String(rawValue || '').trim().toUpperCase();
  const labels: Record<string, string> = {
    INTEGRATED: 'Integrated',
    NTLMV2: 'NTLMv2',
    PLAIN: 'Plain',
    CLEAR: 'Clear - unencrypted',
    LDAPS: 'LDAPS',
    STARTTLS: 'STARTTLS',
  };
  return labels[normalized] || String(rawValue || '').trim();
}

function DeploymentCopyButton({ label, copyValue }: { label: string; copyValue: string }) {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const clipboardAvailable = typeof navigator !== 'undefined' && Boolean(navigator.clipboard?.writeText);

  useEffect(() => {
    if (copyStatus !== 'copied') return undefined;
    const timer = window.setTimeout(() => setCopyStatus('idle'), 1600);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  const copy = async () => {
    if (!clipboardAvailable || !copyValue) return;
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
  };

  return (
    <button
      className="secondary-button duo-configuration-copy-button"
      type="button"
      onClick={() => void copy()}
      disabled={!clipboardAvailable || !copyValue}
      title={clipboardAvailable ? `Copy ${label}` : 'Clipboard access is unavailable in this browser.'}
      aria-live="polite"
    >
      {copyStatus === 'copied' ? 'Copied' : copyStatus === 'failed' ? 'Copy failed' : 'Copy'}
    </button>
  );
}

function DuoConfigurationValue({
  label,
  rawValue,
  displayValue,
  copyValue,
}: {
  label: string;
  rawValue: unknown;
  displayValue?: string;
  copyValue?: string;
}) {
  const normalizedValue = displayValue ?? value(rawValue);
  const available = normalizedValue !== '—' && normalizedValue !== '';
  const normalizedCopyValue = copyValue ?? (available ? normalizedValue : '');

  return (
    <div className="duo-configuration-value-row">
      <dt>{label}</dt>
      <dd>
        <span className={available ? 'mono-cell' : 'muted-text'}>{available ? normalizedValue : 'Not available'}</span>
        {available && <DeploymentCopyButton label={label} copyValue={normalizedCopyValue} />}
      </dd>
    </div>
  );
}

function DuoConfigurationPanel({ configuration }: { configuration: DuoDeploymentConfiguration }) {
  const authenticationSource = configuration.authenticationSource ?? {};
  const workspacesApplication = configuration.workspacesApplication ?? {};
  const roleAttributes = configuration.roleAttributes ?? {};
  const domainControllers = authenticationSource.domainControllers ?? [];
  const baseDns = authenticationSource.baseDns ?? [];
  const emailAttributes = authenticationSource.emailAttributes ?? [];
  const advancedValuesAvailable = Boolean(workspacesApplication.providerArn || workspacesApplication.roleArn);

  return (
    <div className="duo-configuration-section">
      <div className="deployment-status-subheader duo-configuration-header">
        <div>
          <h5>Duo SSO Configuration Values</h5>
          <p className="muted-text">Live AWS values and deterministic SURELINC deployment-contract values for completing the Duo Active Directory source and Amazon WorkSpaces application.</p>
        </div>
        <span className={operationalStatusPillClassName(configuration.status)}>{value(configuration.status)}</span>
      </div>

      <p className="duo-configuration-message">{configuration.message || 'Duo configuration status is unavailable.'}</p>

      <div className="duo-configuration-grid">
        <article className="duo-configuration-card">
          <div className="duo-configuration-card-header">
            <span className="deployment-service-category">External Authentication Source</span>
            <strong>Active Directory server configuration</strong>
          </div>
          <dl>
            <DuoConfigurationValue label="Directory ID" rawValue={authenticationSource.directoryId} />
            <DuoConfigurationValue label="Directory DNS name" rawValue={authenticationSource.directoryDnsName} />
            {domainControllers.flatMap((controller, index) => [
              <DuoConfigurationValue
                key={`${controller.hostnameOrIp || 'controller'}-${index + 1}-address`}
                label={`Domain controller ${index + 1}`}
                rawValue={controller.hostnameOrIp}
              />,
              <DuoConfigurationValue
                key={`${controller.hostnameOrIp || 'controller'}-${index + 1}-port`}
                label={`Port ${index + 1}`}
                rawValue={controller.port}
              />,
            ])}
            {domainControllers.length === 0 && (
              <DuoConfigurationValue label="Domain controllers" rawValue="" />
            )}
            <DuoConfigurationValue label="Base DN(s)" rawValue={baseDns} copyValue={baseDns.join('\n')} />
            <DuoConfigurationValue
              label="Authentication type"
              rawValue={authenticationSource.authenticationType}
              displayValue={duoConfigurationChoiceLabel(authenticationSource.authenticationType)}
            />
            <DuoConfigurationValue
              label="Transport type"
              rawValue={authenticationSource.transportType}
              displayValue={duoConfigurationChoiceLabel(authenticationSource.transportType)}
            />
            <DuoConfigurationValue label="Email attributes" rawValue={emailAttributes} copyValue={emailAttributes.join('\n')} />
            <DuoConfigurationValue label="Duo username attribute" rawValue={authenticationSource.duoUsernameAttribute} />
          </dl>
          <p className="muted-text duo-configuration-note">Authentication type, transport, port, and attribute values come from the saved SURELINC account deployment configuration. The Duo display name and Duo-only policy choices remain administrator selections in Duo.</p>
        </article>

        <article className="duo-configuration-card">
          <div className="duo-configuration-card-header">
            <span className="deployment-service-category">Applications</span>
            <strong>Amazon WorkSpaces service provider</strong>
          </div>
          <dl>
            <DuoConfigurationValue label="Account number" rawValue={workspacesApplication.accountNumber} />
            <DuoConfigurationValue label="Registration Code" rawValue={workspacesApplication.registrationCode} />
            <DuoConfigurationValue label="Region ID" rawValue={workspacesApplication.regionId} />
            <DuoConfigurationValue label="Provider name" rawValue={workspacesApplication.providerName} />
            <DuoConfigurationValue label="AWS role" rawValue={workspacesApplication.awsRole} />
          </dl>
          <p className="muted-text duo-configuration-note">Use the provider and role names, not their ARNs, in the Duo Amazon WorkSpaces application. These deterministic names are available before SAML activation; the ARNs appear only after the corresponding AWS resources are created.</p>
        </article>

        <article className="duo-configuration-card">
          <div className="duo-configuration-card-header">
            <span className="deployment-service-category">Role Attributes</span>
            <strong>Duo group-to-role mapping</strong>
          </div>
          <dl>
            <DuoConfigurationValue label="AWS role" rawValue={roleAttributes.awsRole} />
            <DuoConfigurationValue label="Duo group" rawValue={roleAttributes.duoGroup} />
          </dl>
          <p className="muted-text duo-configuration-note">Confirm that Duo Directory Sync manages this Active Directory group before selecting it in the role mapping.</p>
        </article>
      </div>

      {advancedValuesAvailable && (
        <details className="duo-configuration-advanced">
          <summary>Advanced AWS details</summary>
          <dl>
            <DuoConfigurationValue label="SAML provider ARN" rawValue={workspacesApplication.providerArn} />
            <DuoConfigurationValue label="IAM role ARN" rawValue={workspacesApplication.roleArn} />
          </dl>
        </details>
      )}

      {Boolean(configuration.missingFields?.length) && (
        <p className="duo-configuration-missing-fields">
          <strong>Unavailable values:</strong> {configuration.missingFields?.join(', ')}
        </p>
      )}
    </div>
  );
}

function DeploymentOverridesSummary({ client }: { client: ClientRecord }) {
  const clientConfig = (typeof client.clientConfig === 'object' && client.clientConfig !== null
    ? client.clientConfig
    : {}) as Record<string, unknown>;
  const services = buildDeploymentServiceStatuses(clientConfig, formFromClient(client));

  return (
    <section className="deployment-overrides-section detail-section-wide">
      <div className="deployment-overrides-header">
        <div>
          <h4>AWS Account Deployment Settings</h4>
          <p className="muted-text">Resolved service configuration for this AWS account. Use Configure AWS Account to change a setting.</p>
        </div>
        <span className="result-count">{services.filter((service) => service.enabled).length} enabled · {services.filter((service) => !service.enabled).length} disabled</span>
      </div>
      <div className="deployment-service-grid">
        {services.map((service) => (
          <article
            className={`deployment-service-card ${service.enabled ? 'deployment-service-card-active' : 'deployment-service-card-disabled'}`}
            key={service.key}
          >
            <div className="deployment-service-card-header">
              <div>
                <span className="deployment-service-category">{service.category}</span>
                <strong>{service.label}</strong>
              </div>
              <span className={statusPillClassName(service.enabled ? 'ENABLED' : 'DISABLED')}>
                {service.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p>{service.description}</p>
            <div className="deployment-service-meta">
              <span>{service.source}</span>
              {service.detail && <span>{service.detail}</span>}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function DeploymentStatusPanel({ client }: { client: ClientRecord }) {
  const accountIsActive = String(client.accountStatus || '').trim().toUpperCase() === 'ACTIVE';
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentOperationalStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(accountIsActive);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [statusError, setStatusError] = useState('');

  useEffect(() => {
    let mounted = true;
    let requestInFlight = false;

    setDeploymentStatus(null);
    setStatusError('');
    setRefreshingStatus(false);
    if (!accountIsActive) {
      setLoadingStatus(false);
      return () => {
        mounted = false;
      };
    }

    const loadDeploymentStatus = async (initial: boolean) => {
      if (requestInFlight) return;
      requestInFlight = true;
      if (initial) setLoadingStatus(true);
      else setRefreshingStatus(true);
      try {
        const response = await apiGet<DeploymentOperationalStatusResponse>(
          `/admin/clients/${encodeURIComponent(client.clientId)}/workspaces-identity/deployment-status`,
          accountRequestOptions(String(client.clientAccountId || '')),
        );
        if (!mounted) return;
        setDeploymentStatus(response.deploymentStatus);
        setStatusError('');
      } catch (error) {
        if (!mounted) return;
        setStatusError(error instanceof Error ? error.message : 'Unable to read deployment status.');
      } finally {
        requestInFlight = false;
        if (mounted) {
          setLoadingStatus(false);
          setRefreshingStatus(false);
        }
      }
    };

    void loadDeploymentStatus(true);
    const timer = window.setInterval(() => void loadDeploymentStatus(false), 30_000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [accountIsActive, client.clientAccountId, client.clientId]);

  const refresh = async () => {
    if (!accountIsActive) return;
    setRefreshingStatus(true);
    try {
      const response = await apiGet<DeploymentOperationalStatusResponse>(
        `/admin/clients/${encodeURIComponent(client.clientId)}/workspaces-identity/deployment-status`,
        accountRequestOptions(String(client.clientAccountId || '')),
      );
      setDeploymentStatus(response.deploymentStatus);
      setStatusError('');
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : 'Unable to read deployment status.');
    } finally {
      setRefreshingStatus(false);
    }
  };

  return (
    <section className="deployment-operational-status-section detail-section-wide">
      <div className="deployment-overrides-header">
        <div>
          <h4>Deployment Status</h4>
          <p className="muted-text">
            {accountIsActive
              ? 'Live operational state from the tooling job record and client AWS resources. Refreshes every 30 seconds.'
              : 'Live AWS deployment reads are paused while this account is disabled.'}
          </p>
        </div>
        <div className="deployment-status-header-actions">
          {deploymentStatus && <span className={operationalStatusPillClassName(deploymentStatus.overallStatus)}>{value(deploymentStatus.overallStatus)}</span>}
          {!accountIsActive && <span className={statusPillClassName('DISABLED')}>DISABLED</span>}
          <button className="secondary-button" type="button" onClick={() => void refresh()} disabled={!accountIsActive || refreshingStatus}>
            {refreshingStatus ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {!accountIsActive && (
        <p className="muted-text">Enable this AWS account before requesting live deployment status.</p>
      )}
      {loadingStatus && <p className="muted-text">Reading deployment state...</p>}
      {statusError && <p className="error-text">{statusError}</p>}

      {deploymentStatus && (
        <>
          <div className="deployment-status-observed-row">
            <span>Observed {deploymentStatusDate(deploymentStatus.observedAt)}</span>
            {Boolean(deploymentStatus.currentJob?.jobId) && <span className="mono-cell">Job {String(deploymentStatus.currentJob?.jobId)}</span>}
          </div>
          {deploymentStatus.currentJob?.disableRollback === true && (
            <p className="error-text">CloudFormation rollback is disabled for this job. After failure, inspect available resources before retrying or rolling back. Resources left running may incur charges.</p>
          )}

          <div className="deployment-operational-grid">
            <div className="deployment-operational-card">
              <span>Deployment stage</span>
              <strong>{friendlyLabel(deploymentStatus.deploymentStage || 'UNKNOWN')}</strong>
              <p>{deploymentStatus.currentOperation || 'No current operation was reported.'}</p>
            </div>
            <div className="deployment-operational-card">
              <span>WorkSpaces deployment</span>
              <strong><span className={operationalStatusPillClassName(deploymentStatus.workspacesStack?.status)}>{value(deploymentStatus.workspacesStack?.status)}</span></strong>
              <p>{deploymentStatus.workspacesStack?.message || 'WorkSpaces stack status unavailable.'}</p>
            </div>
            <div className="deployment-operational-card">
              <span>SAML metadata status</span>
              <strong><span className={operationalStatusPillClassName(deploymentStatus.samlMetadata?.status)}>{value(deploymentStatus.samlMetadata?.status)}</span></strong>
              <p>{deploymentStatus.samlMetadata?.message || 'Metadata status unavailable.'}</p>
            </div>
            <div className="deployment-operational-card">
              <span>WorkSpaces SAML status</span>
              <strong><span className={operationalStatusPillClassName(deploymentStatus.workspacesSaml?.status)}>{value(deploymentStatus.workspacesSaml?.status)}</span></strong>
              <p>{deploymentStatus.workspacesSaml?.message || 'WorkSpaces SAML status unavailable.'}</p>
            </div>
            <div className="deployment-operational-card">
              <span>Last successful deployment</span>
              <strong>{deploymentStatusDate(deploymentStatus.lastSuccessfulDeployment?.updatedAt)}</strong>
              <p>{deploymentStatus.lastSuccessfulDeployment?.operation || deploymentStatus.lastSuccessfulDeployment?.message || 'Not recorded.'}</p>
            </div>
            <div className="deployment-operational-card">
              <span>Last validation</span>
              <strong><span className={operationalStatusPillClassName(deploymentStatus.lastValidation?.status)}>{value(deploymentStatus.lastValidation?.status)}</span></strong>
              <p>{deploymentStatusDate(deploymentStatus.lastValidation?.updatedAt)}</p>
            </div>
            <div className="deployment-operational-card">
              <span>Notification status</span>
              <strong><span className={operationalStatusPillClassName(deploymentStatus.notification?.status)}>{value(deploymentStatus.notification?.status)}</span></strong>
              <p>{deploymentStatus.notification?.message || 'Notification result unavailable.'}</p>
            </div>
            <div className="deployment-operational-card">
              <span>Rollback status</span>
              <strong><span className={operationalStatusPillClassName(deploymentStatus.rollback?.status)}>{value(deploymentStatus.rollback?.status)}</span></strong>
              <p>{deploymentStatus.rollback?.message || 'Rollback result unavailable.'}</p>
            </div>
            <div className="deployment-operational-card">
              <span>Auto Scaling capacity</span>
              <strong>
                {deploymentStatus.identityProtectionEnabled === false
                  ? 'Not configured'
                  : `${deploymentStatus.autoScaling?.minimumCapacity ?? '—'} / ${deploymentStatus.autoScaling?.desiredCapacity ?? '—'} / ${deploymentStatus.autoScaling?.maximumCapacity ?? '—'}`}
              </strong>
              <p>
                {deploymentStatus.identityProtectionEnabled === false
                  ? 'Duo identity protection is disabled.'
                  : 'Minimum / desired / maximum'}
              </p>
            </div>
          </div>

          {deploymentStatus.duoConfiguration && (
            <DuoConfigurationPanel configuration={deploymentStatus.duoConfiguration} />
          )}

          <div className="deployment-proxy-status-section">
            <div className="deployment-status-subheader">
              <h5>Duo Authentication Proxies</h5>
              <span className="result-count">{deploymentStatus.proxies?.length || 0} configured</span>
            </div>
            {deploymentStatus.proxies?.length ? (
              <div className="deployment-proxy-status-grid">
                {deploymentStatus.proxies.map((proxy) => <DeploymentProxyCard key={proxy.profileId} proxy={proxy} />)}
              </div>
            ) : (
              <p className="muted-text">
                {deploymentStatus.identityProtectionEnabled === false
                  ? 'Duo identity protection is disabled; no Authentication Proxy profiles are expected.'
                  : 'No deployed proxy profiles were returned by the client runtime secret.'}
              </p>
            )}
          </div>

          {deploymentStatus.failureReason && (
            <div className="deployment-status-failure">
              <strong>Failure reason</strong>
              <p>{deploymentStatus.failureReason}</p>
            </div>
          )}

          {Boolean(deploymentStatus.statusReadErrors?.length) && (
            <div className="deployment-status-read-errors">
              <strong>Operational status read errors</strong>
              {deploymentStatus.statusReadErrors?.map((message) => <p key={message}>{message}</p>)}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ClientDetails({ client, packageSummary, packageSummaryLoading, packageSummaryError, onEdit, onConfigureAccount, onSetStatus, statusUpdating }: { client: ClientRecord; packageSummary?: ClientPackageSummary; packageSummaryLoading: boolean; packageSummaryError: string | null; onEdit: (client: ClientRecord) => void; onConfigureAccount: (client: ClientRecord, accountId: string) => void; onSetStatus: (client: ClientRecord) => void; statusUpdating: boolean }) {
  const primaryAccountId = String(client.primaryAccountId || '').trim();
  const selectedAccountId = clientAccountIdForRequest(client);
  const hasSelectedAccount = /^\d{12}$/.test(selectedAccountId);
  return (
    <article className="card detail-panel">
      <div className="detail-panel-header">
        <div>
          <h3>{hasSelectedAccount ? 'Organization and AWS Account Details' : 'Organization Details'}</h3>
          <p className="muted-text">{client.companyName || client.clientName || client.clientId} · {client.clientId}{hasSelectedAccount ? ` · AWS ${selectedAccountId}` : ''}</p>
        </div>
        <div className="button-row detail-header-actions">
          <span className={statusPillClassName(clientStatus(client))}>Organization {clientStatus(client)}</span>
          {hasSelectedAccount && <span className={statusPillClassName(client.accountStatus)}>Account {value(client.accountStatus)}</span>}
          <button className={isActiveClient(client) ? 'danger-button' : 'success-button'} type="button" onClick={() => onSetStatus(client)} disabled={statusUpdating}>
            {statusUpdating ? 'Updating...' : statusActionLabel(client)}
          </button>
          <Link className="secondary-button" to={`/clients/${encodeURIComponent(client.clientId)}/accounts`}>
            {primaryAccountId ? 'Manage AWS Accounts' : 'Register First AWS Account'}
          </Link>
          {hasSelectedAccount && (
            <button className="primary-button" type="button" onClick={() => onConfigureAccount(client, selectedAccountId)}>Configure AWS Account</button>
          )}
          <button className="secondary-button" type="button" onClick={() => onEdit(client)}>Edit Organization</button>
        </div>
      </div>

      {hasSelectedAccount && <AccountServerImagesPanel clientId={client.clientId} accountId={selectedAccountId}
        active={String(client.accountStatus || '').trim().toUpperCase() === 'ACTIVE'} />}

      <div className="detail-grid">
        <section>
          <h4>Identity</h4>
          <dl>
            {detailRow('Organization UUID', client.organizationId)}
            {detailRow('Organization routing slug', client.organizationSlug || client.clientId)}
            {detailRow('Company', client.companyName || client.clientName)}
            <div className="detail-row"><span>Organization Status</span><strong><span className={statusPillClassName(clientStatus(client))}>{clientStatus(client)}</span></strong></div>
            {detailRow('Status Updated', formatDate(client.statusUpdatedAt))}
            {detailRow('Status Updated By', client.statusUpdatedBy)}
            {detailRow('Status Reason', client.statusReason)}
            {detailRow('Created', formatDate(client.createdAt))}
            {detailRow('Updated', formatDate(client.updatedAt))}
          </dl>
        </section>

        <section>
          <h4>AWS Account Onboarding</h4>
          <dl>
            {hasSelectedAccount && detailRow('Selected AWS account', selectedAccountId)}
            {hasSelectedAccount && detailRow('Selected account name', client.accountDisplayName)}
            {hasSelectedAccount && <div className="detail-row"><span>Selected account status</span><strong><span className={statusPillClassName(client.accountStatus)}>{value(client.accountStatus)}</span></strong></div>}
            {detailRow('Primary AWS account', primaryAccountId || 'Not registered')}
            {detailRow('Account configuration owner', primaryAccountId ? 'Managed AWS account record' : 'Not applicable')}
            {detailRow('Deployment configuration on organization', 'None')}
          </dl>
          <p className="muted-text">AWS account identity, CMMC level, region, deployment settings, evidence, and workflows are managed only on account records.</p>
        </section>

        <section>
          <h4>Contact and Notifications</h4>
          <dl>
            {detailRow('Contact', clientContactDisplayName(client))}
            {detailRow('Invitation email', client.clientEmail || client.contactEmail)}
            {detailRow('Notification Contacts', clientNotificationContactSummary(client))}
            {detailRow('Phone', client.phoneNumber || client.phone_number)}
            {detailRow('Website', client.website)}
          </dl>
        </section>

        {hasSelectedAccount && (
          <section className="package-visibility-section">
            <h4>Selected AWS Account Readiness</h4>
            {packageSummaryLoading && <p className="muted-text">Loading readiness data for AWS {selectedAccountId}…</p>}
            {!packageSummaryLoading && packageSummaryError && <p className="error-text">{packageSummaryError}</p>}
            {!packageSummaryLoading && !packageSummaryError && (
              <dl>
                {detailRow('Latest package', packageDisplayName(packageSummary?.currentReadinessPackage))}
                {detailRow('Last run', formatDate(packageSummary?.lastRun))}
                {detailRow('Control summary', scoreSummary(packageSummary?.currentReadinessPackage))}
                {detailRow('Final auditor status', packageStatus(packageSummary?.finalAuditorPackage))}
              </dl>
            )}
          </section>
        )}
      </div>

      {hasSelectedAccount && <DeploymentOverridesSummary client={client} />}
      {hasSelectedAccount && <DeploymentStatusPanel client={client} />}

      <details className="raw-details">
        <summary>Raw organization record (sensitive fields redacted)</summary>
        <pre>{JSON.stringify(redactEmailsDeep(client), null, 2)}</pre>
      </details>
    </article>
  );
}

type ClientPackageRegistryDialogProps = {
  client: ClientRecord;
  packages: AdminPackageRecord[];
  search: PackageSearchState;
  metadata: PackageMetadata | null;
  loading: boolean;
  error: string | null;
  downloadingPackageKey: string | null;
  onSearchChange: (nextSearch: PackageSearchState) => void;
  onSearch: (event: FormEvent<HTMLFormElement>) => void;
  onClear: () => void;
  onClose: () => void;
  onPageChange: (nextPage: number) => void;
  onPackageDownload: (pkg: AdminPackageRecord) => void;
};

function ClientPackageRegistryDialog({
  client,
  packages,
  search,
  metadata,
  loading,
  error,
  downloadingPackageKey,
  onSearchChange,
  onSearch,
  onClear,
  onClose,
  onPageChange,
  onPackageDownload,
}: ClientPackageRegistryDialogProps) {
  const clientName = client.companyName || client.clientName || client.clientId || 'client';
  const pageNumber = metadata?.page ?? 1;
  const pageCount = metadata?.pageCount ?? 0;
  const matchedCount = metadata?.matchedCount ?? metadata?.count ?? packages.length;

  function PaginationControls() {
    if (!matchedCount) return null;
    return (
      <div className="pagination-row">
        <button className="secondary-button" type="button" disabled={!metadata?.hasPreviousPage || loading} onClick={() => onPageChange(Math.max(1, pageNumber - 1))}>Previous</button>
        <span className="muted-text">Page {pageCount ? pageNumber : 0}{pageCount ? ` of ${pageCount}` : ''}</span>
        <button className="secondary-button" type="button" disabled={!metadata?.hasNextPage || loading} onClick={() => onPageChange(pageNumber + 1)}>Next</button>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <article className="card modal-card package-registry-modal" role="dialog" aria-modal="true" aria-labelledby="client-packages-heading" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-header grid-header-row">
          <div>
            <h3 id="client-packages-heading">Packages</h3>
            <p className="muted-text">{clientName} · {client.clientId}</p>
          </div>
          <button className="secondary-button" type="button" onClick={onClose}>Close</button>
        </div>

        <form className="form-grid package-dialog-search-grid" onSubmit={onSearch}>
          <label>
            Package Type
            <select value={search.packageType} onChange={(event) => onSearchChange({ ...search, packageType: event.target.value })}>
              <option value="">All package types</option>
              {PACKAGE_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{friendlyLabel(option)}</option>)}
            </select>
          </label>
          <label>
            Status
            <select value={search.status} onChange={(event) => onSearchChange({ ...search, status: event.target.value })}>
              <option value="">All statuses</option>
              {PACKAGE_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{friendlyLabel(option)}</option>)}
            </select>
          </label>
          <label className="form-wide">
            Search
            <input value={search.search} onChange={(event) => onSearchChange({ ...search, search: event.target.value })} placeholder="Package ID, package name, status, S3 key, or workflow ID" />
          </label>
          <label>
            Search Mode
            <select value={search.searchMode} onChange={(event) => onSearchChange({ ...search, searchMode: event.target.value as SearchMode })}>
              <option value="contains">Contains</option>
              <option value="beginsWith">Begins with</option>
              <option value="exact">Exact token</option>
            </select>
          </label>
          <label>
            Limit
            <select value={search.limit} onChange={(event) => onSearchChange({ ...search, limit: Number(event.target.value) || 25 })}>
              {PACKAGE_PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <div className="form-actions form-wide button-row search-action-row">
            <button className="primary-button" type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
            <button className="secondary-button" type="button" onClick={onClear} disabled={loading}>Clear</button>
          </div>
        </form>

        <div className="grid-header-row package-result-header">
          <span className="result-count">Showing {packages.length} of {matchedCount} package(s)</span>
          <PaginationControls />
        </div>

        {loading && <p>Loading packages...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && packages.length === 0 && <p className="muted-text">No packages found for the selected AWS account.</p>}

        {packages.length > 0 && (
          <div className="table-wrap package-dialog-table-wrap">
            <table className="interactive-table package-summary-table">
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Controls</th>
                  <th>Signed Docs</th>
                  <th>Client Actions</th>
                  <th>Download</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg, index) => {
                  const packageClientId = String(pkg.clientId || client.clientId || '').trim();
                  const packageAccountId = String(pkg.accountId || clientAccountIdForRequest(client)).trim();
                  const packageKey = `${packageClientId}#${packageIdValue(pkg)}`;
                  return (
                    <tr key={packageRowKey(pkg, index)}>
                      <td className="mono-cell">{packageDisplayName(pkg)}</td>
                      <td>{friendlyLabel(pkg.packageType)}</td>
                      <td><span className={statusPillClassName(rawPackageStatus(pkg))}>{packageStatus(pkg)}</span></td>
                      <td>{formatDate(packageTimestamp(pkg))}</td>
                      <td>{scoreSummary(pkg)}</td>
                      <td>{signedDocumentCount(pkg)}</td>
                      <td>{actionRequiredCount(pkg)}</td>
                      <td>
                        <button
                          className="primary-button table-action-button"
                          type="button"
                          disabled={!packageIdValue(pkg) || !packageAccountId || downloadingPackageKey === packageKey}
                          onClick={() => onPackageDownload({
                            ...pkg,
                            clientId: packageClientId,
                            accountId: packageAccountId,
                          })}
                        >
                          {downloadingPackageKey === packageKey ? 'Preparing...' : 'Download'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <PaginationControls />
      </article>
    </div>
  );
}

export default function ClientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [clientAccounts, setClientAccounts] = useState<Record<string, ManagedAwsAccount[]>>({});
  const [clientAccountsLoading, setClientAccountsLoading] = useState(false);
  const [clientAccountsError, setClientAccountsError] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<ClientRecord | null>(null);
  const [editingAccount, setEditingAccount] = useState<ManagedAwsAccount | null>(null);
  const [form, setForm] = useState<ClientFormState>(emptyForm);
  const [workspacesUserDraft, setWorkspacesUserDraft] = useState<WorkSpacesProvisioningUserDraft>(emptyWorkSpacesProvisioningUserDraft);
  const [mode, setMode] = useState<ClientMode>('create');
  const [search, setSearch] = useState<ClientSearchState>(DEFAULT_SEARCH);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [queueingAll, setQueueingAll] = useState<OrchestrationRunKind | null>(null);
  const [statusUpdatingClientId, setStatusUpdatingClientId] = useState<string | null>(null);
  const [lastRunResult, setLastRunResult] = useState<RunClientsResponse | null>(null);
  const [downloadingPackageKey, setDownloadingPackageKey] = useState<string | null>(null);
  const [packageSummaries, setPackageSummaries] = useState<Record<string, ClientPackageSummary>>({});
  const [packageSummaryLoading, setPackageSummaryLoading] = useState(false);
  const [packageSummaryError, setPackageSummaryError] = useState<string | null>(null);
  const [isClientFormOpen, setIsClientFormOpen] = useState(false);
  const [usersClient, setUsersClient] = useState<ClientRecord | null>(null);
  const [packagesClient, setPackagesClient] = useState<ClientRecord | null>(null);
  const [clientPackages, setClientPackages] = useState<AdminPackageRecord[]>([]);
  const [packageSearch, setPackageSearch] = useState<PackageSearchState>(DEFAULT_PACKAGE_SEARCH);
  const [packagePage, setPackagePage] = useState(1);
  const [packageMetadata, setPackageMetadata] = useState<PackageMetadata | null>(null);
  const [clientPackagesLoading, setClientPackagesLoading] = useState(false);
  const [clientPackagesError, setClientPackagesError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<{ count?: number; matchedCount?: number; limit?: number; page?: number; pageCount?: number; hasPreviousPage?: boolean; hasNextPage?: boolean } | null>(null);
  const [page, setPage] = useState(1);
  const [workspacesBundleCatalog, setWorkspacesBundleCatalog] = useState<WorkSpacesBundleCatalogState>(emptyWorkSpacesBundleCatalog);
  const [selectedWorkSpacesUsers, setSelectedWorkSpacesUsers] = useState<Record<string, WorkSpacesUserRecord[]>>({});
  const [duoMetadataUploading, setDuoMetadataUploading] = useState(false);
  const [duoMetadataUploadMessage, setDuoMetadataUploadMessage] = useState<string | null>(null);
  const [duoInstallerUploading, setDuoInstallerUploading] = useState(false);
  const [duoInstallerUploadMessage, setDuoInstallerUploadMessage] = useState<string | null>(null);
  const resolveDuoImage = useCallback((image?: ServerImage) => {
    const embedded = image?.installerEmbedded === true;
    const checksum = image?.installerSha256 || '';
    const version = image?.installerVersion || '';
    setForm(current => {
      const parameter = image?.version ? `/surelinc/server-images/duo-proxy/${image.version}` : current.duoProxyAmiSsmParameter;
      return current.duoImageInstallerEmbedded === embedded && current.duoImageInstallerSha256 === checksum && current.duoImageInstallerVersion === version && current.duoProxyAmiSsmParameter === parameter
        ? current : { ...current, duoImageInstallerEmbedded: embedded, duoImageInstallerSha256: checksum, duoImageInstallerVersion: version, duoProxyAmiSsmParameter: parameter };
    });
  }, []);

  const [duoProxyOnboardingSaving, setDuoProxyOnboardingSaving] = useState(false);
  const [duoProxyOnboardingMessage, setDuoProxyOnboardingMessage] = useState<string | null>(null);
  const [duoProxyProfileStatuses, setDuoProxyProfileStatuses] = useState<DuoProxyProfileStatus[]>([]);
  const [duoDeploymentPlan, setDuoDeploymentPlan] = useState<DuoDeploymentPlan | null>(null);
  const [runningAccountActionKey, setRunningAccountActionKey] = useState<string | null>(null);
  const handledConfigureRequest = useRef('');

  const selectedClientId = selectedClient?.clientId;
  const selectedClientAccountId = String(selectedClient?.clientAccountId || '').trim();
  const accountRows = useMemo<ClientAccountRegistryRow[]>(() => (
    clients.flatMap((client) => {
      const accounts = clientAccounts[String(client.clientId || '')] ?? [];
      return accounts.length > 0
        ? accounts.map((account): ClientAccountRegistryRow => ({ client, account }))
        : [{ client, account: null } as ClientAccountRegistryRow];
    })
  ), [clientAccounts, clients]);

  async function loadClients(criteria: ClientSearchState = search, pageNumber = page) {
    setLoading(true);
    setError(null);
    setClientAccountsLoading(true);
    setClientAccountsError(null);
    setClientAccounts({});
    try {
      const response = await apiGet<ClientsResponse>('/clients', {
        query: {
          limit: criteria.limit,
          page: pageNumber,
          clientId: criteria.clientId || undefined,
          lifecycle: criteria.lifecycle || undefined,
          cmmcLevel: criteria.cmmcLevel || undefined,
          clientAccountId: criteria.clientAccountId || undefined,
          search: criteria.search || undefined,
          searchMode: criteria.searchMode,
        },
      });
      setClients(response.clients ?? []);
      setMetadata({ count: response.count, matchedCount: response.matchedCount, limit: response.limit, page: response.page, pageCount: response.pageCount, hasPreviousPage: response.hasPreviousPage, hasNextPage: response.hasNextPage });
      setPage(response.page ?? pageNumber);
      const accountEntries = await Promise.all(
        (response.clients ?? []).map(async (client) => {
          const clientId = String(client.clientId || '').trim();
          if (!clientId) return ['', []] as const;
          const accountsResponse = await apiGet<ManagedAwsAccountsResponse>(
            `/admin/clients/${encodeURIComponent(clientId)}/accounts`,
          );
          return [clientId, accountsResponse.accounts ?? []] as const;
        }),
      );
      const nextClientAccounts: Record<string, ManagedAwsAccount[]> = Object.fromEntries(
        accountEntries.filter(([clientId]) => Boolean(clientId)),
      );
      setClientAccounts(nextClientAccounts);
      setSelectedClient((current) => {
        if (!current) return null;
        const refreshedClient = (response.clients ?? []).find((client) => client.clientId === current.clientId) ?? null;
        if (!refreshedClient) return null;
        const currentAccountId = String(current.clientAccountId || '').trim();
        if (!currentAccountId) return refreshedClient;
        const refreshedAccount = (nextClientAccounts[current.clientId] ?? [])
          .find((account) => account.accountId === currentAccountId);
        return refreshedAccount
          ? clientRecordForAccountConfiguration(refreshedClient, refreshedAccount)
          : refreshedClient;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load clients.');
      setClientAccountsError(err instanceof Error ? err.message : 'Unable to load managed AWS accounts.');
    } finally {
      setLoading(false);
      setClientAccountsLoading(false);
    }
  }

  useEffect(() => {
    void loadClients(DEFAULT_SEARCH, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const configureClientId = String(searchParams.get('configureClientId') || '').trim();
    const configureAccountId = String(searchParams.get('accountId') || '').trim();
    if (!configureClientId || !configureAccountId || clients.length === 0) return;
    const requestKey = clientAccountScopeKey(configureClientId, configureAccountId);
    if (handledConfigureRequest.current === requestKey) return;
    const client = clients.find((item) => String(item.clientId || '').trim() === configureClientId);
    if (!client) return;
    handledConfigureRequest.current = requestKey;
    void configureAccount(client, configureAccountId).finally(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('configureClientId');
      next.delete('accountId');
      setSearchParams(next, { replace: true });
    });
  }, [clients, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedClientId || !selectedClientAccountId) return;
    const activeClientId = selectedClientId;
    const activeAccountId = selectedClientAccountId;
    let cancelled = false;

    async function loadSelectedWorkSpacesUsers() {
      try {
        const response = await apiGet<WorkSpacesUsersResponse>(
          `/admin/clients/${encodeURIComponent(activeClientId)}/workspace-users`,
          accountRequestOptions(activeAccountId),
        );
        if (cancelled) return;
        setSelectedWorkSpacesUsers((current) => ({ ...current, [activeClientId]: response.users ?? [] }));
        setSelectedClient((current) => {
          if (!current || current.clientId !== activeClientId) return current;
          return { ...current, workspacesUsers: response.users ?? [] };
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load WorkSpaces users for the selected AWS account.');
        }
      }
    }

    void loadSelectedWorkSpacesUsers();
    return () => {
      cancelled = true;
    };
  }, [selectedClientAccountId, selectedClientId]);

  useEffect(() => {
    if (!selectedClientId || !selectedClientAccountId) {
      setPackageSummaryLoading(false);
      setPackageSummaryError(null);
      return;
    }

    const activeClientId = String(selectedClientId).trim();
    const activeAccountId = String(selectedClientAccountId).trim();
    const scopeKey = clientAccountScopeKey(activeClientId, activeAccountId);
    let cancelled = false;

    async function loadSelectedAccountPackageSummary() {
      setPackageSummaryLoading(true);
      setPackageSummaryError(null);
      try {
        const response = await apiGet<AdminPackagesResponse>(`/admin/clients/${encodeURIComponent(activeClientId)}/packages`, {
          query: {
            ...accountRequestOptions(activeAccountId).query,
            limit: 25,
          },
        });
        const packages = response.packages ?? response.items ?? [];
        if (!cancelled) {
          setPackageSummaries((current) => ({
            ...current,
            [scopeKey]: buildClientPackageSummary(packages),
          }));
        }
      } catch (err) {
        if (!cancelled) {
          setPackageSummaries((current) => {
            const next = { ...current };
            delete next[scopeKey];
            return next;
          });
          setPackageSummaryError(err instanceof Error ? err.message : 'Unable to load readiness data for the selected AWS account.');
        }
      } finally {
        if (!cancelled) setPackageSummaryLoading(false);
      }
    }

    void loadSelectedAccountPackageSummary();

    return () => {
      cancelled = true;
    };
  }, [selectedClientAccountId, selectedClientId]);


  useEffect(() => {
    if (!isClientFormOpen) return;

    let cancelled = false;
    const clientIdForLookup = mode === 'edit' ? form.clientId.trim() : '';
    const regionForLookup = form.region.trim() || 'us-east-1';

    async function loadBundleCatalog() {
      setWorkspacesBundleCatalog((current) => ({ ...current, loading: true, error: null }));
      try {
        const response = await apiGet<WorkSpacesBundleOptionsResponse>('/admin/workspaces-bundles', {
          query: {
            clientId: clientIdForLookup || undefined,
            accountId: mode === 'edit' ? form.clientAccountId.trim() || undefined : undefined,
            region: regionForLookup,
            includeAdvanced: false,
          },
        });
        if (cancelled) return;
        setWorkspacesBundleCatalog({
          recommendedOptions: normalizeWorkSpacesBundleOptions(response.recommendedOptions ?? response.options),
          advancedOptions: normalizeWorkSpacesBundleOptions(response.advancedOptions),
          cached: response.cached,
          lastRefreshedAt: response.lastRefreshedAt,
          expiresAt: response.expiresAt,
          loading: false,
          error: null,
          warning: response.warning || response.message || null,
        });
      } catch (err) {
        if (!cancelled) {
          setWorkspacesBundleCatalog((current) => ({
            ...current,
            loading: false,
            error: err instanceof Error ? err.message : 'Unable to load WorkSpaces bundle options.',
          }));
        }
      }
    }

    void loadBundleCatalog();

    return () => {
      cancelled = true;
    };
  }, [isClientFormOpen, editingAccount, mode, form.clientId, form.region]);

  useEffect(() => {
    if (!isClientFormOpen) return;
    if (workspacesUserDraft.bundleId.trim()) return;
    const defaultOption = workspacesBundleCatalog.recommendedOptions.find((option) => option.profile === WORKSPACES_DEFAULT_BUNDLE_PROFILE)
      ?? workspacesBundleCatalog.recommendedOptions[0];
    if (!defaultOption?.bundleId) return;
    setWorkspacesUserDraft((current) => current.bundleId.trim() ? current : { ...current, bundleId: defaultOption.bundleId || '' });
  }, [isClientFormOpen, workspacesUserDraft.bundleId, workspacesBundleCatalog.recommendedOptions]);

  const generatedClientId = useMemo(() => slugifyClientId(form.companyName || form.clientName), [form.companyName, form.clientName]);
  const formTitle = useMemo(() => {
    if (editingAccount) return `Configure AWS Account: ${editingAccount.accountId}`;
    return mode === 'edit' ? `Edit Organization: ${form.clientId}` : 'Create Organization';
  }, [editingAccount, mode, form.clientId]);
  const existingCuiFileShareEnabled = useMemo(() => {
    if (mode !== 'edit' || !selectedClient) return false;
    return effectiveEnabled(formFromClient(selectedClient).cuiFileShare, false);
  }, [mode, selectedClient]);
  const workspacesProvisioningUserGrid = useMemo(() => {
    try {
      return { users: workSpacesProvisioningUsersForForm(form), error: '' };
    } catch (err) {
      return { users: [] as Record<string, unknown>[], error: err instanceof Error ? err.message : 'WorkSpaces provisioning users are invalid.' };
    }
  }, [form.workspacesProvisioningUsers]);

  const workspacesIdentityReadiness = useMemo(() => buildWorkSpacesIdentityReadinessChecks(form), [form]);
  const workspacesIdentitySelected = effectiveEnabled(form.workspacesIdentityFederation, deploymentDefault(form, 'workspacesIdentityFederation'));
  const workspacesIdentityProxyReady = useMemo(() => duoProxyConfigurationReady(form), [form]);
  const workspacesIdentityProviderReady = useMemo(() => duoProviderConfigurationReady(form), [form]);
  const workspacesIdentitySelectedStageConfigurationReady = form.workspacesIdentityDeploymentStage === 'STATIC_ONLY'
    || (form.workspacesIdentityDeploymentStage === 'PROXY_ONLY' && workspacesIdentityProxyReady)
    || (form.workspacesIdentityDeploymentStage === 'SAML_ACTIVATION' && workspacesIdentityProviderReady);
  const workspacesIdentityBlockedCount = workspacesIdentityReadiness.filter((check) => check.status === 'BLOCKED').length;
  const workspacesIdentityPendingCount = workspacesIdentityReadiness.filter((check) => check.status === 'PENDING').length;
  const workspacesIdentityWarningCount = workspacesIdentityReadiness.filter((check) => check.status === 'WARNING').length;
  const workspacesIdentitySelectedStageReady = workspacesIdentitySelectedStageConfigurationReady && workspacesIdentityBlockedCount === 0;

  const workspacesBundleSelectValue = useMemo(
    () => selectWorkSpacesBundleValue(
      workspacesUserDraft.bundleId,
      workspacesBundleCatalog.recommendedOptions,
    ),
    [workspacesUserDraft.bundleId, workspacesBundleCatalog.recommendedOptions],
  );
  const selectedWorkSpacesBundleOption = useMemo(
    () => currentWorkSpacesBundleOption(workspacesUserDraft.bundleId, workspacesBundleCatalog.recommendedOptions),
    [workspacesUserDraft.bundleId, workspacesBundleCatalog.recommendedOptions],
  );
  function resetForm() {
    setForm(emptyForm);
    setEditingAccount(null);
    setDuoMetadataUploading(false);
    setDuoMetadataUploadMessage(null);
    setDuoInstallerUploading(false);
    setDuoInstallerUploadMessage(null);
    setDuoProxyProfileStatuses([]);
    setWorkspacesUserDraft(emptyWorkSpacesProvisioningUserDraft);
    setWorkspacesBundleCatalog(emptyWorkSpacesBundleCatalog);
    setMode('create');
    setSuccess(null);
    setError(null);
  }

  function openCreateClient() {
    resetForm();
    setIsClientFormOpen(true);
  }

  function closeClientForm() {
    if (!saving) {
      setIsClientFormOpen(false);
      setEditingAccount(null);
    }
  }

  function openEditForm(client: ClientRecord, account: ManagedAwsAccount | null = null) {
    const separatedUsers = account ? account.workspacesUsers : selectedWorkSpacesUsers[client.clientId];
    const clientForForm = separatedUsers ? { ...client, workspacesUsers: separatedUsers } : client;
    setSelectedClient(clientForForm);
    setEditingAccount(account);
    const clientForm = formFromClient(clientForForm);
    const pendingInstaller = readPendingDuoInstallerUpload(client.clientId, clientForm.clientAccountId);
    setForm(
      pendingInstaller && clientForm.duoInstallerUploadStatus !== 'APPROVED'
        ? {
            ...clientForm,
            duoInstallerSourceMode: 'managed_upload',
            duoInstallerUploadStatus: pendingInstaller.status,
            duoInstallerUploadId: pendingInstaller.uploadId,
            duoInstallerQuarantineS3Uri: pendingInstaller.quarantineS3Uri || '',
            duoInstallerSha256: pendingInstaller.sha256,
            duoInstallerFileName: pendingInstaller.fileName,
            duoInstallerSizeBytes: String(pendingInstaller.sizeBytes),
          }
        : clientForm,
    );
    setDuoMetadataUploading(false);
    setDuoMetadataUploadMessage(null);
    setDuoInstallerUploading(false);
    setDuoInstallerUploadMessage(null);
    setDuoProxyProfileStatuses([]);
    setWorkspacesUserDraft(emptyWorkSpacesProvisioningUserDraft);
    setMode('edit');
    setSuccess(null);
    setError(null);
    setIsClientFormOpen(true);
  }

  function editClient(client: ClientRecord) {
    openEditForm(client, null);
  }

  async function configureAccount(client: ClientRecord, accountId: string) {
    const clientId = String(client.clientId || '').trim();
    const normalizedAccountId = String(accountId || '').trim();
    if (!clientId || !/^\d{12}$/.test(normalizedAccountId)) {
      setError('A client ID and valid 12-digit AWS account ID are required to configure an account.');
      return;
    }
    setError(null);
    try {
      const response = await apiGet<ManagedAwsAccountResponse>(
        `/admin/clients/${encodeURIComponent(clientId)}/accounts/${encodeURIComponent(normalizedAccountId)}`,
      );
      const account = response.account;
      if (!account.accountConfig || typeof account.accountConfig !== 'object') {
        throw new Error('The AWS account record does not contain a deployable account configuration.');
      }
      const accountClient = clientRecordForAccountConfiguration(client, account);
      openEditForm(accountClient, account);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load AWS account configuration.');
    }
  }

  function openClientUsers(client: ClientRecord) {
    setSelectedClient(client);
    setUsersClient(client);
  }

  async function loadClientPackages(client: ClientRecord, criteria: PackageSearchState = packageSearch, pageNumber = packagePage) {
    const clientId = String(client.clientId || '').trim();
    const accountId = clientAccountIdForRequest(client);
    if (!clientId) {
      setClientPackages([]);
      setPackageMetadata(null);
      setClientPackagesError('Organization slug is required to load packages.');
      return;
    }

    setClientPackagesLoading(true);
    setClientPackagesError(null);
    try {
      const response = await apiGet<AdminPackagesResponse>(`/admin/clients/${encodeURIComponent(clientId)}/packages`, {
        query: {
          packageType: criteria.packageType || undefined,
          status: criteria.status || undefined,
          search: criteria.search || undefined,
          searchMode: criteria.searchMode,
          limit: criteria.limit,
          page: pageNumber,
          ...accountRequestOptions(accountId).query,
        },
      });
      const items = response.packages ?? response.items ?? [];
      setClientPackages(items);
      setPackageMetadata({
        count: response.count,
        matchedCount: response.matchedCount,
        limit: response.limit,
        page: response.page,
        pageCount: response.pageCount,
        hasPreviousPage: response.hasPreviousPage,
        hasNextPage: response.hasNextPage,
      });
      setPackagePage(response.page ?? pageNumber);
    } catch (err) {
      setClientPackages([]);
      setPackageMetadata(null);
      setClientPackagesError(err instanceof Error ? err.message : 'Unable to load packages for the selected AWS account.');
    } finally {
      setClientPackagesLoading(false);
    }
  }

  function openClientPackages(client: ClientRecord) {
    setSelectedClient(client);
    setPackagesClient(client);
    setClientPackagesError(null);
    setPackageSearch(DEFAULT_PACKAGE_SEARCH);
    setPackagePage(1);
    void loadClientPackages(client, DEFAULT_PACKAGE_SEARCH, 1);
  }

  function handlePackageSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!packagesClient) return;
    setPackagePage(1);
    void loadClientPackages(packagesClient, packageSearch, 1);
  }

  function handlePackageClear() {
    if (!packagesClient) return;
    setPackageSearch(DEFAULT_PACKAGE_SEARCH);
    setPackagePage(1);
    void loadClientPackages(packagesClient, DEFAULT_PACKAGE_SEARCH, 1);
  }

  function handlePackagePageChange(nextPage: number) {
    if (!packagesClient) return;
    setPackagePage(nextPage);
    void loadClientPackages(packagesClient, packageSearch, nextPage);
  }

  function handleDuoAuthproxyConfigChange(authproxyConfig: string) {
    const profileIds = form.duoProxyInputs.map((item) => item.profileId);
    const freshInstall = duoDeploymentPlan?.freshInstall === true
      || duoDeploymentPlan?.identityStackExists === false;
    const requiresFreshCommands = (
      form.duoProxyOnboardingConfigured
      && !freshInstall
      && profileIds.length > 0
    );
    setForm((current) => ({
      ...current,
      duoProxyInputs: current.duoProxyInputs.map((item) => ({ ...item, authproxyConfig })),
      duoDeploymentPlanReady: requiresFreshCommands ? false : current.duoDeploymentPlanReady,
      duoDeploymentPlanMissingCommands: requiresFreshCommands
        ? profileIds.join(', ')
        : current.duoDeploymentPlanMissingCommands,
    }));
    if (requiresFreshCommands) {
      setDuoDeploymentPlan({
        ...duoDeploymentPlan,
        operation: 'PROFILE_REPLACEMENT',
        summary: `The shared authproxy.cfg changed. ${profileIds.length} proxy server${profileIds.length === 1 ? '' : 's'} must be replaced, and each replacement requires a fresh Duo-generated command.`,
        requiresFreshCommands: true,
        freshCommandRequiredProfileIds: profileIds,
        readyForDeployment: false,
        readyForApproval: false,
        atLeastOneProxyRemainsOperational: duoDeploymentPlan?.identityStackExists !== false && duoDeploymentPlan?.freshInstall !== true,
        stepCount: profileIds.length,
      });
    }
  }

  function handleDuoInfrastructureChange(updates: Partial<ClientFormState>) {
    const profileIds = form.duoProxyInputs.map((item) => item.profileId);
    const requiresCommands = form.duoProxyOnboardingConfigured && profileIds.length > 0;
    setForm((current) => ({
      ...current,
      ...updates,
      duoDeploymentPlanReady: !requiresCommands,
      duoDeploymentPlanMissingCommands: requiresCommands ? profileIds.join(', ') : '',
    }));
    if (requiresCommands) {
      const isFreshInstall = duoDeploymentPlan?.freshInstall === true
        || duoDeploymentPlan?.identityStackExists === false;
      setDuoDeploymentPlan({
        ...duoDeploymentPlan,
        operation: isFreshInstall ? 'NEW_INSTALL' : 'ROLLING_INFRASTRUCTURE_REPLACEMENT',
        summary: isFreshInstall
          ? `New identity installation: create ${profileIds.length} Duo proxy profile${profileIds.length === 1 ? '' : 's'} and supporting client infrastructure.`
          : `Rolling infrastructure replacement: ${profileIds.length} proxies replaced sequentially. CloudFormation preserves the Admin Portal Auto Scaling capacity; a fresh command is required for each profile.`,
        minimumCapacity: Number(form.duoProxyMinimumCapacity),
        desiredCapacity: Number(form.duoProxyDesiredCapacity),
        maximumCapacity: Number(form.duoProxyMaximumCapacity),
        concurrentProfileUpdates: 1,
        requiresFreshCommands: true,
        freshCommandRequiredProfileIds: profileIds,
        readyForDeployment: false,
        readyForApproval: false,
        atLeastOneProxyRemainsOperational: !isFreshInstall,
        stepCount: profileIds.length,
        freshInstall: isFreshInstall,
        identityStackExists: isFreshInstall ? false : duoDeploymentPlan?.identityStackExists,
      });
    }
  }

  async function handleDuoProxyOnboardingSave(profileId: string) {
    if (mode !== 'edit' || !form.clientId.trim()) {
      setError('Save the client before storing Duo deployment input.');
      return;
    }
    const draft = form.duoProxyInputs.find((item) => item.profileId === profileId);
    if (!draft) {
      setError('The selected Duo proxy configuration could not be found.');
      return;
    }
    const authproxyConfig = draft.authproxyConfig;
    setDuoProxyOnboardingSaving(true);
    setDuoProxyOnboardingMessage(null);
    setError(null);
    try {
      const response = await apiPost<DuoProxyOnboardingResponse>(
        `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspaces-identity/duo-proxy-onboarding`,
        {
          profileId,
          portalProxyName: draft.portalProxyName,
          authproxyConfig,
          generatedCommand: draft.generatedCommand,
          serverImageSelection: form.duoServerImage === 'BASE' || form.duoServerImage === 'DEFAULT' ? { mode: form.duoServerImage } : form.duoServerImage ? { mode: 'EXPLICIT', image_id: form.duoServerImage } : undefined,
          proxyInfrastructure: {
            instanceType: form.duoProxyInstanceType,
            windowsAmiSsmParameter: form.duoProxyAmiSsmParameter,
            rootVolumeSizeGiB: Number(form.duoProxyRootVolumeGib || 40),
            rootVolumeType: 'gp3',
            minimumProxyVersion: form.duoMinimumProxyVersion,
            installerSha256: form.duoImageInstallerEmbedded ? form.duoImageInstallerSha256 : form.duoInstallerSha256,
            installerVersionId: form.duoInstallerVersionId,
          },
        },
        accountRequestOptions(form.clientAccountId),
      );
      setForm((current) => {
        const storedCount = Math.max(1, response.proxyProfiles?.length ?? current.duoProxyInputs.length);
        const updated = {
          ...current,
          duoProxyOnboardingConfigured: response.configured === true,
          duoDeploymentPlanReady: (response.deploymentPlan?.readyForDeployment ?? response.deploymentPlan?.readyForApproval) !== false,
          duoDeploymentPlanMissingCommands: (response.deploymentPlan?.freshCommandRequiredProfileIds ?? []).join(', '),
          duoProxyMinimumCapacity: String(storedCount),
          duoProxyDesiredCapacity: String(storedCount),
          duoProxyMaximumCapacity: String(Math.min(4, storedCount + 1)),
          duoProxyInputs: current.duoProxyInputs.map((item) => item.profileId === profileId
            ? { ...item, authproxyConfig: item.authproxyConfig, generatedCommand: '' }
            : item),
        };
        const stage = effectiveWorkSpacesIdentityDeploymentStage(updated);
        return {
          ...updated,
          workspacesIdentityDeploymentStage: stage,
          workspacesIdentityDeploymentEnabled: stage !== 'STATIC_ONLY',
        };
      });
      setDuoProxyProfileStatuses(response.proxyProfiles ?? []);
      setDuoDeploymentPlan(response.deploymentPlan ?? null);
      const storedCount = response.proxyProfiles?.length ?? 1;
      setDuoProxyOnboardingMessage(response.message || `${profileId} stored securely. ${storedCount} proxy configuration${storedCount === 1 ? '' : 's'} ready for deployment.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to store Duo deployment input.');
    } finally {
      setDuoProxyOnboardingSaving(false);
    }
  }

  async function handleDuoProxyOnboardingStatus() {
    if (mode !== 'edit' || !form.clientId.trim()) return;
    setDuoProxyOnboardingSaving(true);
    setDuoProxyOnboardingMessage(null);
    setError(null);
    try {
      const response = await apiGet<DuoProxyOnboardingResponse>(
        `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspaces-identity/duo-proxy-onboarding/status`,
        accountRequestOptions(form.clientAccountId),
      );
      setForm((current) => {
        const storedProfiles = response.proxyProfiles ?? [];
        const storedProfileCount = storedProfiles.reduce((highest, profile) => {
          const match = /^proxy-([1-3])$/i.exec(profile.profileId);
          return Math.max(highest, match ? Number(match[1]) : 0);
        }, 0);
        const requiredDraftCount = Math.max(1, current.duoProxyInputs.length, storedProfileCount);
        const sharedAuthproxyConfig = current.duoProxyInputs[0]?.authproxyConfig ?? DUO_AUTHPROXY_CONFIG_TEMPLATE;
        const duoProxyInputs = Array.from({ length: requiredDraftCount }, (_, index) => {
          const profileId = `proxy-${index + 1}`;
          const stored = storedProfiles.find((profile) => profile.profileId === profileId);
          const draft = current.duoProxyInputs[index] ?? newDuoProxyInputDraft(index + 1, sharedAuthproxyConfig);
          return {
            ...draft,
            portalProxyName: stored?.portalProxyName || draft.portalProxyName,
            authproxyConfig: sharedAuthproxyConfig,
          };
        });
        const updated = {
          ...current,
          // The tooling staging secret is deleted after a verified transfer.
          // A NOT_STAGED response must not erase the persistent client-runtime
          // readiness flag already stored in the client configuration.
          duoProxyOnboardingConfigured:
            response.configured === true || current.duoProxyOnboardingConfigured,
          duoDeploymentPlanReady: (response.deploymentPlan?.readyForDeployment ?? response.deploymentPlan?.readyForApproval) !== false,
          duoDeploymentPlanMissingCommands: (response.deploymentPlan?.freshCommandRequiredProfileIds ?? []).join(', '),
          duoProxyMinimumCapacity: String(Math.max(1, storedProfileCount || current.duoProxyInputs.length)),
          duoProxyDesiredCapacity: String(Math.max(1, storedProfileCount || current.duoProxyInputs.length)),
          duoProxyMaximumCapacity: String(Math.min(4, Math.max(1, storedProfileCount || current.duoProxyInputs.length) + 1)),
          duoProxyInputs,
        };
        const stage = effectiveWorkSpacesIdentityDeploymentStage(updated);
        return {
          ...updated,
          workspacesIdentityDeploymentStage: stage,
          workspacesIdentityDeploymentEnabled: stage !== 'STATIC_ONLY',
        };
      });
      setDuoProxyProfileStatuses(response.proxyProfiles ?? []);
      setDuoDeploymentPlan(response.deploymentPlan ?? null);
      const storedCount = response.proxyProfiles?.length ?? 0;
      setDuoProxyOnboardingMessage(
        response.configured
          ? `${storedCount} proxy configuration${storedCount === 1 ? '' : 's'} staged securely${response.configuredAt ? ` on ${formatDate(response.configuredAt)}` : ''}.`
          : (form.duoProxyOnboardingConfigured
            ? 'No new temporary input is staged. Existing client-runtime proxy profiles remain configured.'
            : 'No protected Duo deployment input is currently staged.'),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to check Duo proxy onboarding status.');
    } finally {
      setDuoProxyOnboardingSaving(false);
    }
  }

  async function handleDuoProxyRetireHighest() {
    if (mode !== 'edit' || !form.clientId.trim() || form.duoProxyInputs.length <= 1) return;
    setDuoProxyOnboardingSaving(true);
    setDuoProxyOnboardingMessage(null);
    setError(null);
    try {
      const response = await apiDelete<DuoProxyOnboardingResponse>(
        `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspaces-identity/duo-proxy-onboarding`,
        accountRequestOptions(form.clientAccountId),
      );
      const targetCount = Math.max(1, response.proxyProfiles?.length ?? form.duoProxyInputs.length - 1);
      setForm((current) => ({
        ...current,
        duoProxyInputs: current.duoProxyInputs.slice(0, targetCount),
        duoProxyMinimumCapacity: String(targetCount),
        duoProxyDesiredCapacity: String(targetCount),
        duoProxyMaximumCapacity: String(Math.min(4, targetCount + 1)),
        duoDeploymentPlanReady: (response.deploymentPlan?.readyForDeployment ?? response.deploymentPlan?.readyForApproval) !== false,
        duoDeploymentPlanMissingCommands: (response.deploymentPlan?.freshCommandRequiredProfileIds ?? []).join(', '),
      }));
      setDuoProxyProfileStatuses(response.proxyProfiles ?? []);
      setDuoDeploymentPlan(response.deploymentPlan ?? null);
      setDuoProxyOnboardingMessage(response.message || 'Highest-numbered proxy staged for retirement.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to stage proxy retirement.');
    } finally {
      setDuoProxyOnboardingSaving(false);
    }
  }

  async function handleDuoMetadataUpload(file: File | null) {
    if (!file) return;
    setDuoMetadataUploadMessage(null);
    setError(null);
    if (mode !== 'edit' || !form.clientId.trim()) {
      setError('Save the client before uploading SAML metadata.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xml')) {
      setError('Select the XML metadata file downloaded from the Duo WorkSpaces application.');
      return;
    }
    if (file.size < 1 || file.size > 100 * 1024) {
      setError('SAML metadata must be between 1 byte and 100 KB.');
      return;
    }
    setDuoMetadataUploading(true);
    try {
      const response = await apiPost<DuoSamlMetadataUploadResponse>(
        `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspaces-identity/saml-metadata`,
        {
          fileName: file.name,
          contentBase64: arrayBufferToBase64(await file.arrayBuffer()),
        },
        accountRequestOptions(form.clientAccountId),
      );
      setForm((current) => {
        const updated = {
          ...current,
          duoMetadataSourceMode: 'managed_upload' as DuoMetadataSourceMode,
          duoMetadataS3Uri: '',
          duoMetadataStagedS3Uri: response.stagedS3Uri || response.s3Uri,
          duoMetadataStagedVersionId: response.versionId || '',
          duoMetadataUrl: '',
          duoMetadataSha256: response.sha256,
          duoMetadataObjectVerified: false,
          duoMetadataObjectStatus: response.metadataObjectStatus || 'STAGED',
        };
        const stage = effectiveWorkSpacesIdentityDeploymentStage(updated);
        return {
          ...updated,
          workspacesIdentityDeploymentStage: stage,
          workspacesIdentityDeploymentEnabled: stage !== 'STATIC_ONLY',
        };
      });
      setDuoMetadataUploadMessage(response.message || `Validated and staged ${file.name} for automatic promotion.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to upload Duo SAML metadata.');
    } finally {
      setDuoMetadataUploading(false);
    }
  }


  function applyDuoInstallerStatus(
    response: DuoInstallerStatusResponse,
    forceInfrastructureReplacement = false,
  ) {
    const clientId = form.clientId.trim();
    const profileIds = form.duoProxyInputs.map((item) => item.profileId);
    const approvedAssetChanged = (
      response.status === 'APPROVED'
      && form.duoProxyOnboardingConfigured
      && profileIds.length > 0
      && (
        forceInfrastructureReplacement
        || form.duoInstallerUploadStatus !== 'APPROVED'
        || form.duoInstallerS3Uri !== (response.s3Uri || '')
        || form.duoInstallerVersionId !== (response.versionId || '')
      )
    );
    setForm((current) => ({
      ...current,
      duoInstallerSourceMode: 'managed_upload',
      duoInstallerUploadStatus: response.status,
      duoInstallerMalwareScanStatus: response.malwareScanStatus || '',
      duoInstallerUploadId: response.uploadId,
      duoInstallerQuarantineS3Uri: response.quarantineS3Uri || current.duoInstallerQuarantineS3Uri,
      duoInstallerS3Uri: response.s3Uri || '',
      duoInstallerSha256: response.sha256 || current.duoInstallerSha256,
      duoInstallerVersionId: response.versionId || '',
      duoInstallerFileName: response.fileName || current.duoInstallerFileName,
      duoInstallerSizeBytes: String(response.sizeBytes || current.duoInstallerSizeBytes || ''),
      duoDeploymentPlanReady: approvedAssetChanged ? false : current.duoDeploymentPlanReady,
      duoDeploymentPlanMissingCommands: approvedAssetChanged
        ? profileIds.join(', ')
        : current.duoDeploymentPlanMissingCommands,
    }));
    if (approvedAssetChanged) {
      const isFreshInstall = duoDeploymentPlan?.freshInstall === true
        || duoDeploymentPlan?.identityStackExists === false;
      setDuoDeploymentPlan({
        ...duoDeploymentPlan,
        operation: isFreshInstall ? 'NEW_INSTALL' : 'ROLLING_INFRASTRUCTURE_REPLACEMENT',
        summary: isFreshInstall
          ? `New identity installation: create ${profileIds.length} Duo proxy profile${profileIds.length === 1 ? '' : 's'} and supporting client infrastructure.`
          : `Rolling infrastructure replacement: ${profileIds.length} proxies replaced sequentially. CloudFormation preserves the Admin Portal Auto Scaling capacity; a fresh command is required for each profile.`,
        minimumCapacity: Number(form.duoProxyMinimumCapacity),
        desiredCapacity: Number(form.duoProxyDesiredCapacity),
        maximumCapacity: Number(form.duoProxyMaximumCapacity),
        concurrentProfileUpdates: 1,
        requiresFreshCommands: true,
        freshCommandRequiredProfileIds: profileIds,
        readyForDeployment: false,
        readyForApproval: false,
        atLeastOneProxyRemainsOperational: !isFreshInstall,
        stepCount: profileIds.length,
        freshInstall: isFreshInstall,
        identityStackExists: isFreshInstall ? false : duoDeploymentPlan?.identityStackExists,
      });
    }
    if (clientId) {
      if (response.status === 'APPROVED' || response.status === 'BLOCKED' || response.status === 'FAILED') {
        clearPendingDuoInstallerUpload(clientId, form.clientAccountId);
      } else {
        storePendingDuoInstallerUpload({
          clientId,
          accountId: form.clientAccountId.trim(),
          uploadId: response.uploadId,
          fileName: response.fileName,
          sizeBytes: response.sizeBytes,
          sha256: response.sha256,
          quarantineS3Uri: response.quarantineS3Uri,
          status: response.status,
          savedAt: new Date().toISOString(),
        });
      }
    }
    setDuoInstallerUploadMessage(response.message || null);
  }

  async function checkDuoInstallerStatus(
    uploadId = form.duoInstallerUploadId,
    quiet = false,
    uploadContext?: { fileName: string; sizeBytes: number; sha256: string },
  ): Promise<DuoInstallerStatusResponse | null> {
    if (!uploadId || !form.clientId.trim()) return null;
    const fileName = uploadContext?.fileName || form.duoInstallerFileName;
    const sizeBytes = uploadContext?.sizeBytes || Number(form.duoInstallerSizeBytes || 0);
    const sha256 = uploadContext?.sha256 || form.duoInstallerSha256;
    if (!fileName || !sizeBytes || !sha256) {
      if (!quiet) setError('Installer upload details are incomplete. Upload the installer again.');
      return null;
    }
    try {
      const response = await apiPost<DuoInstallerStatusResponse>(
        `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspaces-identity/duo-installer/status`,
        { uploadId, fileName, sizeBytes, sha256 },
        accountRequestOptions(form.clientAccountId),
      );
      applyDuoInstallerStatus(response);
      return response;
    } catch (err) {
      if (!quiet) setError(err instanceof Error ? err.message : 'Unable to check Duo installer scan status.');
      return null;
    }
  }

  async function handleDuoInstallerUpload(file: File | null) {
    if (!file) return;
    setDuoInstallerUploadMessage(null);
    setError(null);
    if (mode !== 'edit' || !form.clientId.trim()) {
      setError('Save the client before uploading the Duo installer. No client-side identity resources are required for this managed upload.');
      return;
    }
    setDuoInstallerUploading(true);
    try {
      const complete = await uploadDuoInstaller(file,
        `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspaces-identity/duo-installer`, {
          requestOptions: accountRequestOptions(form.clientAccountId),
          onProgress: setDuoInstallerUploadMessage,
          onPrepared: upload => {
            setForm(current => ({ ...current, duoInstallerSourceMode: 'managed_upload',
              duoInstallerUploadStatus: 'UPLOADING', duoInstallerMalwareScanStatus: '',
              duoInstallerUploadId: upload.uploadId, duoInstallerQuarantineS3Uri: upload.quarantineS3Uri,
              duoInstallerS3Uri: '', duoInstallerSha256: upload.sha256, duoInstallerVersionId: '',
              duoInstallerFileName: upload.fileName, duoInstallerSizeBytes: String(upload.sizeBytes) }));
            storePendingDuoInstallerUpload({ clientId: form.clientId.trim(), accountId: form.clientAccountId.trim(),
              uploadId: upload.uploadId, fileName: upload.fileName, sizeBytes: upload.sizeBytes,
              sha256: upload.sha256, quarantineS3Uri: upload.quarantineS3Uri,
              status: 'UPLOADING', savedAt: new Date().toISOString() });
          },
        });
      applyDuoInstallerStatus(complete, true);
    } catch (err) {
      clearPendingDuoInstallerUpload(form.clientId.trim(), form.clientAccountId);
      setForm((current) => ({ ...current, duoInstallerUploadStatus: 'FAILED' }));
      setError(err instanceof Error ? err.message : 'Unable to upload the Duo Authentication Proxy installer.');
    } finally {
      setDuoInstallerUploading(false);
    }
  }

  useEffect(() => {
    if (!isClientFormOpen || mode !== 'edit') return;
    if (form.duoInstallerUploadStatus !== 'SCANNING') return;
    if (!form.clientId.trim() || !form.duoInstallerUploadId || !form.duoInstallerFileName || !form.duoInstallerSha256) return;
    const sizeBytes = Number(form.duoInstallerSizeBytes || 0);
    if (!sizeBytes) return;

    let cancelled = false;
    async function pollInstallerScan() {
      for (let attempt = 0; attempt < DUO_INSTALLER_SCAN_POLL_ATTEMPTS; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, DUO_INSTALLER_SCAN_POLL_INTERVAL_MS));
        if (cancelled) return;
        const latest = await checkDuoInstallerStatus(form.duoInstallerUploadId, true, {
          fileName: form.duoInstallerFileName,
          sizeBytes,
          sha256: form.duoInstallerSha256,
        });
        if (cancelled || !latest) continue;
        if (latest.status === 'APPROVED' || latest.status === 'BLOCKED' || latest.status === 'FAILED') return;
      }
      if (!cancelled) {
        setDuoInstallerUploadMessage('GuardDuty is still scanning the installer. You may close this form and use Check status later; upload tracking is retained for this browser session.');
      }
    }

    void pollInstallerScan();
    return () => {
      cancelled = true;
    };
    // Primitive upload fields intentionally define the polling lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isClientFormOpen,
    mode,
    form.clientId,
    form.duoInstallerUploadStatus,
    form.duoInstallerUploadId,
    form.duoInstallerFileName,
    form.duoInstallerSizeBytes,
    form.duoInstallerSha256,
  ]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    const dependencyError = editingAccount ? validateServiceDependencies(form) : '';
    if (dependencyError) {
      setError(dependencyError);
      setSaving(false);
      return;
    }

    try {
      if (mode === 'edit') {
        if (editingAccount) {
          const response = await apiPatch<ManagedAwsAccountResponse>(
            `/admin/clients/${encodeURIComponent(form.clientId)}/accounts/${encodeURIComponent(editingAccount.accountId)}`,
            {
              region: form.region.trim() || 'us-east-1',
              cmmcLevel: form.cmmcLevel,
              managementAccountId: form.managementAccountId.trim(),
              logArchiveAccountId: form.logArchiveAccountId.trim(),
              accountStatus: form.lifecycle.toLowerCase() === 'active' ? 'ACTIVE' : 'DISABLED',
              accountConfig: accountConfigFromForm(form, editingAccount.accountConfig),
            },
          );
          setEditingAccount(response.account);
          setSuccess(`AWS account ${response.account.accountId} configuration was updated. No deployment was started.`);
        } else {
          const response = await apiPatch<ClientResponse>(`/clients/${encodeURIComponent(form.clientId)}`, compactForm(form));
          setSelectedClient(response.client);
          setSuccess(`Organization ${response.client.clientId} was updated. No deployment was started.`);
        }
      } else {
        const response = await apiPost<CreateClientResponse>('/clients', compactForm(form));
        const clientId = response.client?.clientId ?? 'organization';
        if (response.client) setSelectedClient(response.client);
        setSuccess(welcomeDeliveryMessage(response, `Organization ${clientId} was created. Cognito temporary-password invitation sent.`));
        setForm(emptyForm);
        setWorkspacesUserDraft(emptyWorkSpacesProvisioningUserDraft);
      }
      setIsClientFormOpen(false);
      await loadClients(search, page);
    } catch (err) {
      setError(err instanceof Error ? err.message : mode === 'edit' ? 'Unable to update organization.' : 'Unable to create organization.');
    } finally {
      setSaving(false);
    }
  }


  async function handleRefreshWorkSpacesBundles() {
    setWorkspacesBundleCatalog((current) => ({ ...current, loading: true, error: null, warning: null }));
    try {
      const response = await apiGet<WorkSpacesBundleOptionsResponse>('/admin/workspaces-bundles', {
        query: {
          clientId: mode === 'edit' ? form.clientId.trim() || undefined : undefined,
          accountId: mode === 'edit' ? form.clientAccountId.trim() || undefined : undefined,
          region: form.region.trim() || 'us-east-1',
          includeAdvanced: false,
          refresh: true,
        },
      });
      setWorkspacesBundleCatalog({
        recommendedOptions: normalizeWorkSpacesBundleOptions(response.recommendedOptions ?? response.options),
        advancedOptions: normalizeWorkSpacesBundleOptions(response.advancedOptions),
        cached: response.cached,
        lastRefreshedAt: response.lastRefreshedAt,
        expiresAt: response.expiresAt,
        loading: false,
        error: null,
        warning: response.warning || response.message || null,
      });
    } catch (err) {
      setWorkspacesBundleCatalog((current) => ({
        ...current,
        loading: false,
        error: err instanceof Error ? err.message : 'Unable to refresh WorkSpaces bundle options.',
      }));
    }
  }

  function handleWorkSpacesBundleSelection(nextValue: string) {
    setError(null);
    const selectedOption = workspacesBundleCatalog.recommendedOptions
      .find((option) => workSpacesBundleOptionKey(option) === nextValue);
    if (selectedOption?.bundleId) {
      setWorkspacesUserDraft((current) => ({ ...current, bundleId: selectedOption.bundleId || '' }));
      return;
    }
    if (nextValue.startsWith('bundle:')) {
      setWorkspacesUserDraft((current) => ({ ...current, bundleId: nextValue.slice('bundle:'.length) }));
    }
  }

  function handleAddWorkSpacesProvisioningUser() {
    setError(null);
    try {
      const nextJson = addWorkSpacesProvisioningUserToJson(form.workspacesProvisioningUsers, workspacesUserDraft);
      const nextUser = buildWorkSpacesProvisioningUser(workspacesUserDraft);
      if (mode === 'edit' && form.clientId.trim()) {
        void apiPost<UpsertWorkSpacesUserResponse>(
          `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspace-users`,
          nextUser,
          accountRequestOptions(form.clientAccountId),
        )
          .then((response) => {
            const apiUser = response.user ?? (nextUser as WorkSpacesUserRecord);
            setSelectedWorkSpacesUsers((current) => {
              const existing = current[form.clientId] ?? [];
              const apiUserName = String(apiUser.user_name ?? apiUser.userName ?? apiUser.username ?? '').trim().toLowerCase();
              return {
                ...current,
                [form.clientId]: [
                  ...existing.filter((entry) => String(entry.user_name ?? entry.userName ?? entry.username ?? '').trim().toLowerCase() !== apiUserName),
                  apiUser,
                ],
              };
            });
          })
          .catch(() => undefined);
      }
      setForm((current) => ({ ...current, workspacesProvisioningUsers: nextJson }));
      setWorkspacesUserDraft(emptyWorkSpacesProvisioningUserDraft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to add WorkSpaces provisioning user.');
    }
  }

  function handleEditWorkSpacesProvisioningUser(user: Record<string, unknown>) {
    setError(null);
    setWorkspacesUserDraft({
      userName: String(user.user_name || ''),
      givenName: String(user.given_name || ''),
      surname: String(user.surname || ''),
      email: String(user.email || ''),
      bundleId: workSpacesUserBundleId(user),
      passwordSecretArn: String(user.password_secret_arn || ''),
    });
  }

  function handleDeleteWorkSpacesProvisioningUser(user: Record<string, unknown>) {
    setError(null);
    try {
      const userName = String(user.user_name || '').trim().toLowerCase();
      if (!userName) throw new Error('Unable to delete WorkSpaces provisioning user without a user name.');
      const users = workSpacesProvisioningUsersForForm(form).filter((existingUser) => String(existingUser.user_name || '').trim().toLowerCase() !== userName);
      if (mode === 'edit' && form.clientId.trim()) {
        void apiDelete<DeleteWorkSpacesUserResponse>(
          `/admin/clients/${encodeURIComponent(form.clientId.trim())}/workspace-users/${encodeURIComponent(userName)}`,
          accountRequestOptions(form.clientAccountId),
        )
          .then(() => {
            setSelectedWorkSpacesUsers((current) => ({
              ...current,
              [form.clientId]: (current[form.clientId] ?? []).filter((entry) => String(entry.user_name ?? entry.userName ?? entry.username ?? '').trim().toLowerCase() !== userName),
            }));
          })
          .catch(() => undefined);
      }
      setForm((current) => ({ ...current, workspacesProvisioningUsers: formatWorkSpacesProvisioningUsers(users) }));
      setWorkspacesUserDraft((current) => String(current.userName || '').trim().toLowerCase() === userName ? emptyWorkSpacesProvisioningUserDraft : current);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete WorkSpaces provisioning user.');
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    void loadClients(search, 1);
  }

  function handleClear() {
    setSearch(DEFAULT_SEARCH);
    setPage(1);
    void loadClients(DEFAULT_SEARCH, 1);
  }

  async function runAccountWorkflow(row: ClientAccountRegistryRow, kind: OrchestrationRunKind, disableRollback = false) {
    if (!row.account) {
      setError('Register an AWS account before queueing a workflow.');
      return;
    }
    const clientId = String(row.client.clientId || '').trim();
    const accountId = String(row.account.accountId || '').trim();
    const option = ORCHESTRATION_RUN_OPTIONS[kind];
    const actionLabel = disableRollback ? 'deployment with CloudFormation rollback disabled' : kind === 'full' ? 'full workflow' : kind === 'software' ? 'software deployment' : 'evidence collection';
    const accountName = row.account.displayName || accountId;
    if (!clientId || !/^\d{12}$/.test(accountId)) {
      setError('A valid organization and AWS account are required to queue this workflow.');
      return;
    }
    if (!isActiveClient(row.client) || String(row.account.accountStatus || '').toUpperCase() !== 'ACTIVE') {
      setError('Both the organization and the selected AWS account must be active before a workflow can be queued.');
      return;
    }
    const rollbackWarning = disableRollback
      ? '\n\nThis admin troubleshooting job starts without client approval. CloudFormation rollback is disabled for this job only. If deployment fails, resources remain available for inspection where supported and may remain running and billable until you recover or clean them up. Resource replacements are not supported in this mode. Duo identity recovery remains enabled.'
      : '';
    if (!window.confirm(`Queue ${actionLabel} for ${accountName} (${accountId})?${rollbackWarning}`)) return;

    const actionKey = `${clientAccountScopeKey(clientId, accountId)}:${kind}${disableRollback ? ':no-rollback' : ''}`;
    setRunningAccountActionKey(actionKey);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiPost<RunClientsResponse>('/orchestration/run-client', {
        clientId,
        accountId,
        requestedAction: option.requestedAction,
        workflowType: option.workflowType,
        disableRollback,
        reason: `Admin requested ${actionLabel} from the organization and AWS account registry.`,
      });
      setSuccess(response.message || `${actionLabel} queued for AWS account ${accountId}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to queue ${actionLabel}.`);
    } finally {
      setRunningAccountActionKey(null);
    }
  }

  async function handleRunAllActiveClients(kind: OrchestrationRunKind = 'full') {
    const option = ORCHESTRATION_RUN_OPTIONS[kind];
    const activeCount = accountRows.filter(({ client, account }) => (
      Boolean(account) && isActiveClient(client) && String(account?.accountStatus || '').toUpperCase() === 'ACTIVE'
    )).length;
    const confirmed = window.confirm(option.confirmAll(activeCount));
    if (!confirmed) return;

    setQueueingAll(kind);
    setError(null);
    setSuccess(null);
    setLastRunResult(null);
    try {
      const response = await apiPost<RunClientsResponse>('/orchestration/run-active-clients', {
        requestedAction: option.requestedAction,
        workflowType: option.workflowType,
        reason: option.reasonAll,
      });
      setLastRunResult(response);
      setSuccess(formatRunSummary(response));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to queue active organization account workflows.');
    } finally {
      setQueueingAll(null);
    }
  }

  async function handleSetClientStatus(client: ClientRecord) {
    const clientId = String(client.clientId || '').trim();
    if (!clientId) {
      setError('Organization slug is required to update organization status.');
      return;
    }

    const targetStatus = nextClientStatus(client);
    const clientName = String(client.companyName || client.clientName || clientId);
    const defaultReason = targetStatus === 'DISABLED'
      ? 'Customer requested offboarding or access was disabled by SURELINC admin.'
      : 'Organization access re-enabled by SURELINC admin.';
    const reason = window.prompt(
      targetStatus === 'DISABLED'
        ? `Disable ${clientName}? Organization users will be signed out and blocked from signing back in. Enter a reason:`
        : `Re-enable ${clientName}? Enter a reason:`,
      String(client.statusReason || defaultReason),
    );
    if (reason === null) return;

    const confirmed = window.confirm(
      targetStatus === 'DISABLED'
        ? `Disable ${clientName}? This terminates sessions, blocks future sign-in, and denies client-facing backend access.`
        : `Re-enable ${clientName}? Users disabled by the tenant status action will be enabled again.`,
    );
    if (!confirmed) return;

    setStatusUpdatingClientId(clientId);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiPatch<ClientStatusUpdateResponse>(`/admin/clients/${encodeURIComponent(clientId)}/status`, {
        clientStatus: targetStatus,
        statusReason: reason.trim(),
      });
      setSelectedClient(response.client);
      setSuccess(response.message || `Client ${clientId} status updated to ${targetStatus}.`);
      await loadClients(search, page);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to update status for ${clientId}.`);
    } finally {
      setStatusUpdatingClientId(null);
    }
  }

  async function handlePackageDownload(pkg: AdminPackageRecord) {
    const clientId = String(pkg.clientId || '').trim();
    const accountId = String(
      pkg.accountId
        || clientAccountIdForRequest(packagesClient)
        || clientAccountIdForRequest(selectedClient)
        || '',
    ).trim();
    const packageId = String(pkg.packageId || pkg.latestPackageId || pkg.packageName || '').trim();
    if (!clientId || !accountId || !packageId) {
      setError('Organization slug, AWS account ID, and package ID are required to download a package.');
      return;
    }

    const packageKey = `${clientId}#${packageId}`;
    setDownloadingPackageKey(packageKey);
    setError(null);
    try {
      const response = await apiPost<PackageDownloadUrlResponse>(
        `/admin/clients/${encodeURIComponent(clientId)}/packages/${encodeURIComponent(packageId)}/download-url`,
        {},
        accountRequestOptions(accountId),
      );
      window.open(response.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to prepare package download.';
      setError(message);
      if (packagesClient) setClientPackagesError(message);
    } finally {
      setDownloadingPackageKey(null);
    }
  }

  return (
    <div className="page-stack">
      <div className="page-heading page-heading-with-actions">
        <div>
          <h2>Organizations</h2>
          <p>Create organizations independently, then register and operate each authorized AWS account as its own configuration and workflow boundary.</p>
        </div>
        <button className="primary-button" type="button" onClick={openCreateClient}>New Organization</button>
      </div>
      {success && <p className="success-text page-status-message">{success}</p>}

      {isClientFormOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeClientForm();
        }}>
          <article className="card modal-card client-form-modal" role="dialog" aria-modal="true" aria-labelledby="client-form-heading" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header grid-header-row">
              <div>
                <h3 id="client-form-heading">{formTitle}</h3>
                {editingAccount && (
                  <p className="muted-text">
                    {editingAccount.displayName || form.companyName} · {form.clientId}. Organization identity and contact fields remain managed on the parent organization record.
                  </p>
                )}
              </div>
              <div className="button-row modal-header-actions">
                {mode === 'edit' && !editingAccount && <button className="secondary-button" type="button" onClick={openCreateClient} disabled={saving}>New Organization</button>}
                <button className="secondary-button" type="button" onClick={closeClientForm} disabled={saving}>Close</button>
              </div>
            </div>

        <form className="form-grid jobs-search-grid client-edit-form" onSubmit={handleSubmit}>

          <fieldset className="client-profile-section form-wide">
            <legend>{editingAccount ? 'AWS Account Identity and Boundaries' : 'Organization Profile'}</legend>
            <div className="client-profile-grid">
              <label className="client-profile-field">
                <span className="field-label-title">Company name <span className="required-asterisk" aria-label="required">*</span></span>
                <input required disabled={Boolean(editingAccount)} value={form.companyName} onChange={(event) => setForm((current) => ({ ...current, companyName: event.target.value, clientName: event.target.value }))} placeholder="Example Company LLC" />
                <span className="field-help">Displayed throughout the portal.</span>
              </label>
              <label className="client-profile-field">
                <span className="field-label-title">Organization invitation email <span className="required-asterisk" aria-label="required">*</span></span>
                <input type="email" required disabled={Boolean(editingAccount)} value={form.email || form.contactEmail} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value, contactEmail: event.target.value }))} placeholder="client@example.com" />
                <span className="field-help">Creates the first organization administrator and sends the Cognito temporary-password invitation.</span>
              </label>
              {editingAccount && (
                <label className="client-profile-field">
                  <span className="field-label-title">CUI AWS account ID <span className="required-asterisk" aria-label="required">*</span></span>
                  <input required pattern="\d{12}" disabled value={form.clientAccountId} readOnly />
                  <span className="field-help">Immutable 12-digit deployment and evidence boundary.</span>
                </label>
              )}

              <label className="client-profile-field">
                <span className="field-label-title">Contact name</span>
                <input disabled={Boolean(editingAccount)} value={form.name ?? ''} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Primary contact" />
              </label>
              <label className="client-profile-field">
                <span className="field-label-title">Phone number</span>
                <input type="tel" disabled={Boolean(editingAccount)} value={form.phone_number ?? ''} onChange={(event) => setForm((current) => ({ ...current, phone_number: event.target.value }))} placeholder="+1 555 555 0100" />
              </label>
              <label className="client-profile-field">
                <span className="field-label-title">Website</span>
                <input type="url" disabled={Boolean(editingAccount)} value={form.website ?? ''} onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))} placeholder="https://example.com" />
              </label>

              <label className="client-profile-field client-profile-span-2">
                <span className="field-label-title">Address</span>
                <textarea disabled={Boolean(editingAccount)} value={form.address ?? ''} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} placeholder="Primary business or mailing address" rows={2} />
              </label>
              <label className="client-profile-field">
                <span className="field-label-title">Notification emails</span>
                <textarea disabled={Boolean(editingAccount)} value={form.notificationEmails} onChange={(event) => setForm((current) => ({ ...current, notificationEmails: event.target.value }))} placeholder="email1@example.com, email2@example.com" rows={2} />
                <span className="field-help">Comma, semicolon, or line separated; defaults to the invitation email.</span>
              </label>

              {editingAccount && (
                <>
                  <label className="client-profile-field">
                    <span className="field-label-title">Region <span className="required-asterisk" aria-label="required">*</span></span>
                    <select required value={form.region || 'us-east-1'} onChange={(event) => setForm((current) => ({ ...current, region: event.target.value }))}>
                      {US_REGION_OPTIONS.map((region) => <option key={region} value={region}>{region}</option>)}
                    </select>
                    <span className="field-help">Deployment and evidence region for this AWS account.</span>
                  </label>
                  <label className="client-profile-field">
                    <span className="field-label-title">CMMC Level <span className="required-asterisk" aria-label="required">*</span></span>
                    <select value={form.cmmcLevel} onChange={(event) => setForm((current) => ({ ...current, cmmcLevel: event.target.value }))}>
                      {CMMC_LEVEL_OPTIONS.map((level) => <option key={level} value={level}>{level}</option>)}
                    </select>
                    <span className="field-help">Assessment level for this AWS account.</span>
                  </label>
                </>
              )}
              <label className="client-profile-field">
                <span className="field-label-title">{editingAccount ? 'AWS Account Status' : 'Organization Status'} <span className="required-asterisk" aria-label="required">*</span></span>
                <select value={form.lifecycle} onChange={(event) => setForm((current) => ({ ...current, lifecycle: event.target.value }))}>
                  {LIFECYCLE_OPTIONS.map((lifecycle) => <option key={lifecycle} value={lifecycle}>{lifecycle}</option>)}
                </select>
                <span className="field-help">{editingAccount ? 'DISABLED blocks this account from organization selection and new workflows.' : 'DISABLED blocks organization portal access; use the registry action to enforce sign-out.'}</span>
              </label>

              {!editingAccount && (
                <label className="client-profile-field">
                  <span className="field-label-title">Status reason</span>
                  <textarea value={form.statusReason} onChange={(event) => setForm((current) => ({ ...current, statusReason: event.target.value }))} placeholder="Reason for the latest status change" rows={2} />
                  <span className="field-help">Admin-only organization status note.</span>
                </label>
              )}
              {!editingAccount && (
                <>
                  <label className="client-profile-field">
                    <span className="field-label-title">Organization routing slug</span>
                    <input value={mode === 'edit' ? form.clientId : generatedClientId} disabled readOnly />
                    <span className="field-help">Generated from the company name on create and locked. The canonical organization identity is a UUID.</span>
                  </label>
                  <label className="client-profile-field">
                    <span className="field-label-title">Initial organization portal role</span>
                    <input value={`Client Administrator (${INITIAL_CLIENT_USER_ROLE})`} disabled readOnly />
                    <span className="field-help">The first invited user receives CLIENT_ADMIN. Add others from the registry.</span>
                  </label>

                  <label className="client-profile-field">
                    <span className="field-label-title">Organization Users page</span>
                    <select disabled={Boolean(editingAccount)} value={displayedChoice(form.clientUsersManagement, true)} onChange={(event) => setForm((current) => ({ ...current, clientUsersManagement: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Controls whether organization admins can manage portal users. AWS account membership remains account-specific.</span>
                  </label>
                  <label className="client-profile-field">
                    <span className="field-label-title">Organization UUID</span>
                    <input value={mode === 'edit' ? String(selectedClient?.organizationId || '') : 'Generated on create'} disabled readOnly />
                    <span className="field-help">Canonical immutable organization identity used by account ownership records.</span>
                  </label>
                </>
              )}
              {editingAccount && (
                <>
                  <label className="client-profile-field">
                    <span className="field-label-title">Management account ID</span>
                    <input pattern="\d{12}" value={form.managementAccountId} onChange={(event) => setForm((current) => ({ ...current, managementAccountId: event.target.value }))} placeholder="123456789012" />
                    <span className="field-help">AWS Organizations management account, when available.</span>
                  </label>
                  <label className="client-profile-field">
                    <span className="field-label-title">Log archive account ID</span>
                    <input pattern="\d{12}" value={form.logArchiveAccountId} onChange={(event) => setForm((current) => ({ ...current, logArchiveAccountId: event.target.value }))} placeholder="123456789012" />
                    <span className="field-help">Control Tower log archive account, when available.</span>
                  </label>
                </>
              )}
            </div>
          </fieldset>

          {editingAccount && (
          <fieldset className="form-wide override-fieldset override-fieldset-enhanced">
            <legend>AWS Account Deployment Settings</legend>
            <div className="override-intro-card">
              <div>
                <p className="override-eyebrow">Account-specific settings</p>
                <p className="field-help override-help">
                  {`These settings apply only to AWS account ${editingAccount.accountId}. Review each service and choose Enabled or Disabled.`}
                </p>
              </div>
              <div className="override-dependency-chain" aria-label="Deployment dependency order">
                <span>VPC / Enclave</span>
                <span aria-hidden="true">→</span>
                <span>Directory</span>
                <span aria-hidden="true">→</span>
                <span>WorkSpaces</span>
                <span aria-hidden="true">→</span>
                <span>Identity Provider</span>
                <span aria-hidden="true">→</span>
                <span>CUI Files</span>
              </div>
            </div>

            <div className="override-section-grid">
              <section className="override-section override-section-reporting">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">Assessment workflow</p>
                    <h4>Reporting</h4>
                  </div>
                  <span className="override-section-badge">Reports</span>
                </div>
                <p className="field-help override-section-description">Controls whether this AWS account generates readiness reports during the evidence workflow.</p>
                <div className="override-card-grid">
                  <label className="override-control">
                    <span className="field-label-title">Report Generation</span>
                    <select value={displayedChoice(form.reportGeneration, true)} onChange={(event) => setForm((current) => ({ ...current, reportGeneration: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Client-started Evidence Collection</span>
                    <select value={displayedChoice(form.clientEvidenceWorkflow, false)} onChange={(event) => setForm((current) => ({ ...current, clientEvidenceWorkflow: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Paid account feature. Active account administrators may request evidence collection from Approvals, but the request must still be approved or declined before collection starts.</span>
                  </label>
                </div>
              </section>

              <section className="override-section override-section-network override-section-network-wide">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">Foundation</p>
                    <h4>Enclave Network & Private Endpoints</h4>
                  </div>
                  <span className="override-section-badge">Cost controlled</span>
                </div>
                <p className="field-help override-section-description">Enable the VPC first. Gateway endpoints have no endpoint hourly charge; interface endpoints are paid and remain opt-in unless this account has a private workload dependency.</p>
                <div className="override-card-grid endpoint-vpc-grid">
                  <label className="override-control">
                    <span className="field-label-title">VPC / Enclave Network</span>
                    <select value={displayedDeploymentChoice(form, 'vpc', form.vpc)} onChange={(event) => setForm((current) => ({ ...current, vpc: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Endpoint settings are saved even when the VPC is disabled, but they are deployed only with the VPC.</span>
                  </label>
                  <div className="endpoint-cost-guidance" role="note">
                    <strong>Recommended baseline</strong>
                    <span>Keep S3 and DynamoDB gateway endpoints enabled. Leave interface endpoints disabled until a private workload requires one. Amazon Inspector telemetry is required when the Identity Protection Package is enabled.</span>
                  </div>
                </div>

                <details className="endpoint-override-details" open>
                  <summary className="endpoint-override-summary">
                    <span>
                      <strong>VPC endpoint settings</strong>
                      <small>Choose Enabled or Disabled for each endpoint in this AWS account.</small>
                    </span>
                    <span className="endpoint-cost-badge">2 gateway · 14 interface</span>
                  </summary>
                  <div className="endpoint-override-content">
                    <section className="endpoint-group endpoint-group-gateway">
                      <div className="endpoint-group-header">
                        <div>
                          <h5>Gateway endpoints</h5>
                          <p>No endpoint hourly charge. Route-table attachment is controlled by the deployment profile.</p>
                        </div>
                        <span>Recommended on</span>
                      </div>
                      <div className="endpoint-control-grid endpoint-control-grid-gateway">
                        {GATEWAY_ENDPOINT_DEFINITIONS.map((endpoint) => (
                          <label className="endpoint-control" key={endpoint.field}>
                            <span className="field-label-title">{endpoint.label}</span>
                            <select
                              value={displayedDeploymentChoice(form, endpoint.defaultKey, form[endpoint.field])}
                              onChange={(event) => setForm((current) => ({ ...current, [endpoint.field]: event.target.value as OverrideChoice }))}
                            >
                              <option value="enabled">Enabled</option>
                              <option value="disabled">Disabled</option>
                            </select>
                            <span className="field-help">{endpoint.help}</span>
                          </label>
                        ))}
                      </div>
                    </section>

                    <section className="endpoint-group endpoint-group-interface">
                      <div className="endpoint-group-header">
                        <div>
                          <h5>Interface endpoints</h5>
                          <p>Paid per endpoint and Availability Zone. Enable only for a documented private-connectivity requirement. Inspector telemetry is required for Identity Protection proxy deployments.</p>
                        </div>
                        <span>Recommended off</span>
                      </div>
                      <div className="endpoint-control-grid endpoint-control-grid-interface">
                        {INTERFACE_ENDPOINT_DEFINITIONS.map((endpoint) => (
                          <label className="endpoint-control endpoint-control-paid" key={endpoint.field}>
                            <span className="field-label-title">{endpoint.label}</span>
                            <select
                              value={endpoint.requiredByIdentityProtection && workspacesIdentitySelected ? 'enabled' : displayedDeploymentChoice(form, endpoint.defaultKey, form[endpoint.field])}
                              onChange={(event) => setForm((current) => ({ ...current, [endpoint.field]: event.target.value as OverrideChoice }))}
                              disabled={endpoint.requiredByIdentityProtection && workspacesIdentitySelected}
                            >
                              <option value="enabled">Enabled</option>
                              <option value="disabled">Disabled</option>
                            </select>
                            <span className="field-help">{endpoint.help}{endpoint.requiredByIdentityProtection && workspacesIdentitySelected ? ' Enabled automatically by the selected package.' : ''}</span>
                          </label>
                        ))}
                      </div>
                    </section>
                  </div>
                </details>
              </section>

              <section className="override-section override-section-security">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">Security tooling</p>
                    <h4>Monitoring & Findings</h4>
                  </div>
                  <span className="override-section-badge">Detections</span>
                </div>
                <p className="field-help override-section-description">Security services and notification settings used for monitoring, findings, and vulnerability evidence.</p>
                <div className="override-card-grid">
                  <label className="override-control">
                    <span className="field-label-title">GuardDuty</span>
                    <select value={displayedDeploymentChoice(form, 'guardDuty', form.guardDuty)} onChange={(event) => setForm((current) => ({ ...current, guardDuty: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">GuardDuty Runtime Monitoring</span>
                    <select value={displayedDeploymentChoice(form, 'guardDutyRuntimeMonitoring', form.guardDutyRuntimeMonitoring)} onChange={(event) => setForm((current) => ({ ...current, guardDutyRuntimeMonitoring: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Security Hub</span>
                    <select value={displayedDeploymentChoice(form, 'securityHub', form.securityHub)} onChange={(event) => setForm((current) => ({ ...current, securityHub: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Amazon Inspector</span>
                    <select value={displayedDeploymentChoice(form, 'inspector', form.inspector)} onChange={(event) => setForm((current) => ({ ...current, inspector: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Client Security Alerts</span>
                    <select value={displayedDeploymentChoice(form, 'securityHubNotifications', form.securityHubNotifications)} onChange={(event) => setForm((current) => ({ ...current, securityHubNotifications: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Paid add-on. Sends tenant-isolated digests for Security Hub ACTIVE + NEW + HIGH/CRITICAL findings only.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Client Alert Email</span>
                    <input type="email" value={form.securityHubNotificationEmail} onChange={(event) => setForm((current) => ({ ...current, securityHubNotificationEmail: event.target.value }))} placeholder="security@example.com" />
                    <span className="field-help">If blank, the client's Notification Emails are used. Client alerts are never mixed with operator alerts.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Delivery Channel</span>
                    <select value={form.securityHubNotificationDeliveryChannel} onChange={(event) => setForm((current) => ({ ...current, securityHubNotificationDeliveryChannel: event.target.value as ClientSecurityAlertDeliveryChannel }))}>
                      <option value="email">Email only</option>
                      <option value="webhook">Teams/Slack webhook only</option>
                      <option value="email_and_webhook">Email + Teams/Slack webhook</option>
                    </select>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Webhook Secret ARN</span>
                    <input value={form.securityHubNotificationWebhookSecretArn} onChange={(event) => setForm((current) => ({ ...current, securityHubNotificationWebhookSecretArn: event.target.value }))} placeholder="arn:aws:secretsmanager:...:secret:surelinc/cmmc/client-alert-webhooks/..." />
                    <span className="field-help">Optional Teams, Slack, or compatible webhook URL stored in the platform account Secrets Manager.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Minimum Severity</span>
                    <select value={form.securityHubNotificationMinimumSeverity} onChange={(event) => setForm((current) => ({ ...current, securityHubNotificationMinimumSeverity: event.target.value as ClientSecurityAlertMinimumSeverity }))}>
                      <option value="HIGH">High and Critical</option>
                      <option value="CRITICAL">Critical only</option>
                    </select>
                    <span className="field-help">Workflow status is fixed to NEW to reduce notification fatigue.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">SURELINC Copy</span>
                    <select value={form.securityHubNotificationNotifySurelinc ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, securityHubNotificationNotifySurelinc: event.target.value === 'true' }))}>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                    <span className="field-help">Sends a separate SURELINC copy; it is not copied on the client email.</span>
                  </label>
                </div>
              </section>

              <div className="override-stacked-column">
                <section className="override-section override-section-cui">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">CUI data boundary</p>
                    <h4>CUI Storage</h4>
                  </div>
                  <span className="override-section-badge">Self-contained</span>
                </div>
                <p className="field-help override-section-description">Encrypted S3 storage and optional Windows file sharing for CUI workloads.</p>
                <div className="override-card-grid">
                  <label className="override-control">
                    <span className="field-label-title">CUI Storage</span>
                    <select value={displayedDeploymentChoice(form, 'cuiStorage', form.cuiStorage)} onChange={(event) => setForm((current) => ({ ...current, cuiStorage: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Deploys encrypted S3 buckets for CUI files and access logs.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">CUI File Share</span>
                    <select value={displayedDeploymentChoice(form, 'cuiFileShare', form.cuiFileShare)} onChange={(event) => setForm((current) => ({ ...current, cuiFileShare: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Creates a mapped Windows file share for WorkSpaces users.</span>
                    <span className="field-help">{FSX_CUI_FILE_SHARE_COST_WARNING}</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">FSx Storage Size (GiB)</span>
                    <input type="number" min="32" step="32" value={form.cuiFileShareStorageCapacityGib} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareStorageCapacityGib: event.target.value }))} />
                    <span className="field-help">{existingCuiFileShareEnabled ? 'Applied during the next approved deployment. FSx storage can only increase.' : 'SSD capacity. 32 GiB is the minimum.'}</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">FSx Throughput (MB/s)</span>
                    <input type="number" min="8" step="8" value={form.cuiFileShareThroughputCapacityMbps} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareThroughputCapacityMbps: event.target.value }))} />
                    <span className="field-help">{existingCuiFileShareEnabled ? 'Applied during the next approved deployment.' : 'File system throughput. 8 MB/s is the minimum.'}</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Backup Retention Days</span>
                    <input type="number" min="1" max="90" value={form.cuiFileShareBackupRetentionDays} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareBackupRetentionDays: event.target.value }))} />
                    <span className="field-help">Keeps automatic FSx backups for 1 to 90 days.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Drive Letter</span>
                    <input value={form.cuiFileShareDriveLetter} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareDriveLetter: event.target.value.toUpperCase() }))} placeholder="Z:" />
                    <span className="field-help">Maps the shared folder to this drive in each WorkSpace.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Share Name</span>
                    <input value={form.cuiFileShareShareName} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareShareName: event.target.value }))} placeholder="CUI" />
                    <span className="field-help">Names the shared folder created on the FSx file system.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Create Shared Folder</span>
                    <select value={form.cuiFileShareCreateSharedFolder ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareCreateSharedFolder: event.target.value === 'true' }))}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    <span className="field-help">Creates the common CUI folder during provisioning.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Create Per-user Folders</span>
                    <select value={form.cuiFileShareCreateUserFolders ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareCreateUserFolders: event.target.value === 'true' }))}>
                      <option value="true">Yes</option>
                      <option value="false">No - MVP shared folder only</option>
                    </select>
                    <span className="field-help">Creates a private folder for each provisioned user.</span>
                  </label>
                  <label className="override-control override-control-warning">
                    <span className="field-label-title">Retain FSx on Delete</span>
                    <select value={form.cuiFileShareRetainOnDelete ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, cuiFileShareRetainOnDelete: event.target.value === 'true' }))}>
                      <option value="true">Yes</option>
                      <option value="false">No - blocked unless allow_destroy is approved separately</option>
                    </select>
                    <span className="field-help">Recommended. Destructive deletion still requires an approved allow_destroy setting.</span>
                  </label>
                </div>
                </section>

              </div>

              <section className="override-section override-section-directory">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">Identity foundation</p>
                    <h4>Directory Services</h4>
                  </div>
                  <span className="override-section-badge">Depends on VPC</span>
                </div>
                <p className="field-help override-section-description">Directory Service settings for identity-backed enclave access and WorkSpaces registration.</p>
                <div className="override-card-grid">
                  <label className="override-control">
                    <span className="field-label-title">Directory Service</span>
                    <select value={displayedDeploymentChoice(form, 'directoryService', form.directoryService)} onChange={(event) => setForm((current) => ({ ...current, directoryService: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Provides the directory used by WorkSpaces and domain services.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Directory Type</span>
                    <select value={form.directoryType} onChange={(event) => setForm((current) => ({ ...current, directoryType: event.target.value as DirectoryTypeChoice, workspacesDirectoryTypeRequired: event.target.value }))}>
                      <option value="aws_managed_microsoft_ad">AWS Managed Microsoft AD</option>
                      <option value="simple_ad">Simple AD</option>
                    </select>
                    <span className="field-help">Selects the directory platform used for domain authentication.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Managed AD Edition</span>
                    <select value={form.directoryEdition} onChange={(event) => setForm((current) => ({ ...current, directoryEdition: event.target.value as DirectoryEditionChoice }))} disabled={form.directoryType !== 'aws_managed_microsoft_ad'}>
                      <option value="standard">Standard</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                    <span className="field-help">Sets the capacity tier for AWS Managed Microsoft AD.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Directory DNS Name</span>
                    <input value={form.directoryName} onChange={(event) => setForm((current) => ({ ...current, directoryName: event.target.value }))} placeholder="cmmc.local" />
                    <span className="field-help">Fully qualified directory domain name, such as cmmc.local.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Directory Short Name</span>
                    <input value={form.directoryShortName} onChange={(event) => setForm((current) => ({ ...current, directoryShortName: event.target.value }))} placeholder="CMMC" />
                    <span className="field-help">NetBIOS name used by Windows clients and domain logons.</span>
                  </label>
                  <label className="override-control override-control-warning">
                    <span className="field-label-title">Directory Removal Policy</span>
                    <select value={form.directoryRemovalPolicy} onChange={(event) => setForm((current) => ({ ...current, directoryRemovalPolicy: event.target.value as RemovalPolicyChoice }))}>
                      <option value="retain">Retain</option>
                      <option value="destroy">Destroy</option>
                    </select>
                    <span className="field-help">Deletion intent. Destroy should be used only during planned teardown.</span>
                  </label>
                  <label className="override-control override-control-warning">
                    <span className="field-label-title">Allow Directory Destroy</span>
                    <select value={form.directoryAllowDestroy ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, directoryAllowDestroy: event.target.value === 'true' }))}>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                    <span className="field-help">Explicitly permits deletion when the removal policy is Destroy.</span>
                  </label>
                  <ServerImageSelectors profile="windows-management" value={form.managementServerImage}
                    onChange={value => setForm(current => ({ ...current, managementServerImage: value }))} />
                </div>
              </section>

              <div className="override-stacked-column override-operations-stack">
              <section className="override-section override-section-operations">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">Operations</p>
                    <h4>SSM Management</h4>
                  </div>
                  <span className="override-section-badge">Inventory</span>
                </div>
                <p className="field-help override-section-description">Systems Manager controls for inventory and patch-management evidence.</p>
                <div className="override-card-grid">
                  <label className="override-control">
                    <span className="field-label-title">SSM</span>
                    <select value={displayedDeploymentChoice(form, 'ssm', form.ssm)} onChange={(event) => setForm((current) => ({ ...current, ssm: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Enables AWS Systems Manager management for supported resources in this AWS account and operational evidence collection.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">SSM Inventory</span>
                    <select value={displayedDeploymentChoice(form, 'ssmInventory', form.ssmInventory)} onChange={(event) => setForm((current) => ({ ...current, ssmInventory: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Collects software, application, and system metadata from SSM-managed nodes for inventory and evidence.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">SSM Patch Manager</span>
                    <select value={displayedDeploymentChoice(form, 'ssmPatchManager', form.ssmPatchManager)} onChange={(event) => setForm((current) => ({ ...current, ssmPatchManager: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Scans and installs approved operating-system patches on SSM-managed nodes according to configured maintenance schedules.</span>
                  </label>
                </div>
              </section>

              <section className="override-section override-section-resilience">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">Recovery</p>
                    <h4>Backup</h4>
                  </div>
                  <span className="override-section-badge">Resilience</span>
                </div>
                <p className="field-help override-section-description">Controls the AWS account backup baseline used for recovery evidence.</p>
                <div className="override-card-grid">
                  <label className="override-control">
                    <span className="field-label-title">Backup</span>
                    <select value={displayedDeploymentChoice(form, 'backup', form.backup)} onChange={(event) => setForm((current) => ({ ...current, backup: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Enabled schedules protected resources. Disabled creates nothing for new accounts or suspends future scheduled backups after enablement.</span>
                  </label>
                </div>
              </section>
              </div>

              <section className="override-section override-section-workspaces override-section-workspaces-wide">
                <div className="override-section-header">
                  <div>
                    <p className="override-section-kicker">Managed desktops</p>
                    <h4>WorkSpaces</h4>
                  </div>
                  <span className="override-section-badge">Depends on Directory</span>
                </div>
                <p className="field-help override-section-description">WorkSpaces settings for controlled desktop access into the enclave.</p>
                <div className="override-card-grid workspaces-settings-grid">
                  <label className="override-control">
                    <span className="field-label-title">WorkSpaces</span>
                    <select value={displayedDeploymentChoice(form, 'workspaces', form.workspaces)} onChange={(event) => setForm((current) => ({ ...current, workspaces: event.target.value as OverrideChoice }))}>
                      <option value="enabled">Enabled</option>
                      <option value="disabled">Disabled</option>
                    </select>
                    <span className="field-help">Enables the managed Amazon WorkSpaces desktop service for this AWS account enclave.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Directory Registration</span>
                    <select value={form.workspacesRegistrationEnabled ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, workspacesRegistrationEnabled: event.target.value === 'true' }))}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                    <span className="field-help">Registers the directory for WorkSpaces without creating desktops.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Desktop Provisioning</span>
                    <select value={displayedDeploymentChoice(form, 'workspacesProvisioning', form.workspacesProvisioning)} onChange={(event) => setForm((current) => ({ ...current, workspacesProvisioning: event.target.value as OverrideChoice }))}>
                      <option value="disabled">Disabled</option>
                      <option value="enabled">Enabled</option>
                    </select>
                    <span className="field-help">Creates AD users and WorkSpaces only when explicitly enabled.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Running Mode</span>
                    <select value={form.workspacesRunningMode} onChange={(event) => setForm((current) => ({ ...current, workspacesRunningMode: event.target.value as RunningModeChoice }))}>
                      <option value="AUTO_STOP">AUTO_STOP</option>
                      <option value="ALWAYS_ON">ALWAYS_ON</option>
                    </select>
                    <span className="field-help">AUTO_STOP reduces idle cost; ALWAYS_ON keeps desktops available.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Bundle Type</span>
                    <input value={form.workspacesBundleType} onChange={(event) => setForm((current) => ({ ...current, workspacesBundleType: event.target.value }))} placeholder="value" />
                    <span className="field-help">Display/classification only. Each provisioning user below must have a concrete regional bundle ID.</span>
                  </label>
                  <label className="override-control override-control-warning">
                    <span className="field-label-title">Removal Policy</span>
                    <select value={form.workspacesRemovalPolicy} onChange={(event) => setForm((current) => ({ ...current, workspacesRemovalPolicy: event.target.value as RemovalPolicyChoice }))}>
                      <option value="retain">Retain</option>
                      <option value="destroy">Destroy</option>
                    </select>
                    <span className="field-help">Deletion intent. Required before intentionally destroying Directory Service.</span>
                  </label>
                  <label className="override-control override-control-warning">
                    <span className="field-label-title">Allow WorkSpaces Destroy</span>
                    <select value={form.workspacesAllowDestroy ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, workspacesAllowDestroy: event.target.value === 'true' }))}>
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                    <span className="field-help">Permits destructive WorkSpaces deletion only when the removal policy is also set to Destroy. Leave No for normal operations.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">Root Volume Encryption</span>
                    <select value={form.workspacesRootVolumeEncryption ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, workspacesRootVolumeEncryption: event.target.value === 'true' }))}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                    <span className="field-help">Encrypts each WorkSpace operating-system volume at rest.</span>
                  </label>
                  <label className="override-control">
                    <span className="field-label-title">User Volume Encryption</span>
                    <select value={form.workspacesUserVolumeEncryption ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, workspacesUserVolumeEncryption: event.target.value === 'true' }))}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                    <span className="field-help">Encrypts each WorkSpace user data volume at rest.</span>
                  </label>
                  <div className="override-control form-wide workspaces-user-builder">
                    <div className="workspaces-user-builder-header">
                      <div>
                        <span className="field-label-title">Add Provisioning User</span>
                        <span className="field-help">Adds or updates one WorkSpaces user. Each user can have a unique regional bundle ID. Plaintext passwords are not accepted.</span>
                      </div>
                      <button className="secondary-button" type="button" onClick={handleAddWorkSpacesProvisioningUser}>Add user</button>
                    </div>
                    <div className="workspaces-user-form-grid">
                      <label className="workspace-user-name-field">
                        <span className="field-label-title">User name</span>
                        <input value={workspacesUserDraft.userName} onChange={(event) => setWorkspacesUserDraft((current) => ({ ...current, userName: event.target.value }))} placeholder="Username" />
                      </label>
                      <label className="workspace-bundle-select-field">
                        <span className="field-label-row">
                          <span className="field-label-title">Desktop profile</span>
                          <button className="text-button" type="button" onClick={handleRefreshWorkSpacesBundles} disabled={workspacesBundleCatalog.loading}>{workspacesBundleCatalog.loading ? 'Loading...' : 'Refresh bundles'}</button>
                        </span>
                        <select value={workspacesBundleSelectValue} onChange={(event) => handleWorkSpacesBundleSelection(event.target.value)}>
                          <option value="">Select a desktop profile</option>
                          {selectedWorkSpacesBundleOption && !workspacesBundleCatalog.recommendedOptions.some((option) => String(option.bundleId || '').trim() === String(selectedWorkSpacesBundleOption.bundleId || '').trim()) ? (
                            <option value={workSpacesBundleOptionKey(selectedWorkSpacesBundleOption)}>
                              {workSpacesBundleProfileLabel(selectedWorkSpacesBundleOption)}
                            </option>
                          ) : null}
                          {workspacesBundleCatalog.recommendedOptions.map((option) => (
                            <option key={workSpacesBundleOptionKey(option)} value={workSpacesBundleOptionKey(option)}>
                              {workSpacesBundleProfileLabel(option)}
                            </option>
                          ))}
                        </select>
                        {selectedWorkSpacesBundleOption ? (
                          <div className="workspace-bundle-spec-card">
                            <strong>{selectedWorkSpacesBundleOption.description || 'Selected desktop profile'}</strong>
                            <dl>
                              {workSpacesBundleSpecs(selectedWorkSpacesBundleOption).map(([label, value]) => (
                                <div key={label}>
                                  <dt>{label}</dt>
                                  <dd>{value}</dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        ) : null}
                        <input
                          className="workspace-bundle-id-input"
                          value={workspacesUserDraft.bundleId}
                          onChange={(event) => setWorkspacesUserDraft((current) => ({ ...current, bundleId: event.target.value }))}
                          placeholder="wsb-xxxxxxxxx"
                          aria-label="Provisioning Bundle ID"
                        />
                        <span className="field-help">Select a desktop profile to populate the bundle ID, or paste an account-specific bundle ID below. Performance is the recommended default; bundle IDs are regional.</span>
                        {workspacesBundleCatalog.error && <span className="field-help field-help-error">{workspacesBundleCatalog.error}</span>}
                        {workspacesBundleCatalog.warning && !workspacesBundleCatalog.error && <span className="field-help">{workspacesBundleCatalog.warning}</span>}
                        {workspacesBundleCatalog.lastRefreshedAt && <span className="field-help">Bundle cache refreshed {formatDate(workspacesBundleCatalog.lastRefreshedAt)}.</span>}
                      </label>
                      <label className="workspace-given-name-field">
                        <span className="field-label-title">First name</span>
                        <input value={workspacesUserDraft.givenName} onChange={(event) => setWorkspacesUserDraft((current) => ({ ...current, givenName: event.target.value }))} placeholder="First Name" />
                      </label>
                      <label className="workspace-surname-field">
                        <span className="field-label-title">Last name</span>
                        <input value={workspacesUserDraft.surname} onChange={(event) => setWorkspacesUserDraft((current) => ({ ...current, surname: event.target.value }))} placeholder="Last Name" />
                      </label>
                      <label className="workspace-email-field">
                        <span className="field-label-title">Email</span>
                        <input type="email" value={workspacesUserDraft.email} onChange={(event) => setWorkspacesUserDraft((current) => ({ ...current, email: event.target.value }))} placeholder="Email" />
                      </label>
                      <label className="workspace-password-secret-field">
                        <span className="field-label-title">Password secret ARN</span>
                        <input value={workspacesUserDraft.passwordSecretArn} onChange={(event) => setWorkspacesUserDraft((current) => ({ ...current, passwordSecretArn: event.target.value }))} placeholder="Optional Secrets Manager ARN" />
                        <span className="field-help">Leave blank to let the stack generate a temporary password secret.</span>
                      </label>
                    </div>
                    <div className="workspaces-user-grid-panel">
                      <div className="workspaces-user-grid-header">
                        <strong>Provisioning Users</strong>
                        <span className="result-count">{workspacesProvisioningUserGrid.users.length} user(s)</span>
                      </div>
                      {workspacesProvisioningUserGrid.error && <p className="error-text">{workspacesProvisioningUserGrid.error}</p>}
                      {!workspacesProvisioningUserGrid.error && workspacesProvisioningUserGrid.users.length === 0 && (
                        <p className="muted-text">No WorkSpaces provisioning users have been added.</p>
                      )}
                      {!workspacesProvisioningUserGrid.error && workspacesProvisioningUserGrid.users.length > 0 && (
                        <div className="table-wrap workspaces-user-table-wrap">
                          <table className="workspaces-user-table">
                            <thead>
                              <tr>
                                <th>User</th>
                                <th>Name</th>
                                <th>Bundle ID</th>
                                <th>Password Secret</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {workspacesProvisioningUserGrid.users.map((user, index) => (
                                <tr key={workSpacesUserRowKey(user, index)}>
                                  <td className="mono-cell">{value(user.user_name)}</td>
                                  <td>{workSpacesUserDisplayName(user)}</td>
                                  <td className="mono-cell">{value(workSpacesUserBundleId(user))}</td>
                                  <td>{user.password_secret_arn ? 'Provided' : 'Generated by stack'}</td>
                                  <td>
                                    <div className="button-row table-action-stack">
                                      <button className="secondary-button table-action-button" type="button" onClick={() => handleEditWorkSpacesProvisioningUser(user)}>Update</button>
                                      <button className="danger-button table-action-button" type="button" onClick={() => handleDeleteWorkSpacesProvisioningUser(user)}>Delete</button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                  <details className="override-control form-wide workspaces-user-json-details">
                    <summary>Advanced provisioning JSON</summary>
                    <label>
                      <span className="field-label-title">Provisioning Users JSON</span>
                      <textarea rows={8} value={form.workspacesProvisioningUsers} onChange={(event) => setForm((current) => ({ ...current, workspacesProvisioningUsers: event.target.value }))} placeholder={'[{\n  "user_name": "Username",\n  "given_name": "First Name",\n  "surname": "Last Name",\n  "email": "Email",\n  "bundle_id": "wsb-xxxxxxxxx"\n}]'} />
                      <span className="field-help">Saved to {editingAccount ? 'this account’s accountConfig' : 'the default client template'} under overrides.workspaces.provisioning.users. You may still edit the JSON directly. Do not include plaintext passwords.</span>
                    </label>
                  </details>
                </div>
              </section>
              <details className="override-section override-section-identity override-section-workspaces-wide identity-section-details">
                <summary className="override-section-header identity-section-summary">
                  <div>
                    <p className="override-section-kicker">Optional identity provider integration</p>
                    <h4>WorkSpaces Identity Protection</h4>
                  </div>
                  <span className="override-section-badge">Depends on WorkSpaces + Directory</span>
                </summary>
                <p className="field-help override-section-description">Cisco Duo SAML identity protection for Amazon WorkSpaces. The provider and integration mode are fixed for this release. Future provider adapters will continue using the same client configuration contract.</p>

                <div className="identity-workflow">
                  <section className="identity-workflow-step">
                    <div className="identity-workflow-step-header">
                      <span className="identity-workflow-step-number">1</span>
                      <div>
                        <strong>Configure Duo</strong>
                        <span className="field-help">Provide the required Duo values. Defaults and overrides are available under Advanced settings.</span>
                      </div>
                    </div>

                    <div className="identity-fixed-summary">
                      <span><strong>Identity provider:</strong> Cisco Duo</span>
                      <span><strong>Integration:</strong> SAML SSO for Amazon WorkSpaces</span>
                    </div>

                    <div className="override-card-grid identity-provider-settings-grid">
                      <label className="override-control">
                        <span className="field-label-title">Identity Protection Package</span>
                        <select
                          value={displayedDeploymentChoice(form, 'workspacesIdentityFederation', form.workspacesIdentityFederation)}
                          onChange={(event) => {
                            const choice = event.target.value as OverrideChoice;
                            setForm((current) => ({
                              ...current,
                              workspacesIdentityFederation: choice,
                              vpcEndpointInterfaceInspector2Telemetry: choice === 'enabled' ? 'enabled' : current.vpcEndpointInterfaceInspector2Telemetry,
                              workspacesIdentityProvider: 'duo',
                              workspacesIdentityIntegrationMode: 'saml_sso',
                              workspacesIdentityDeploymentStage: choice === 'enabled' ? current.workspacesIdentityDeploymentStage : 'STATIC_ONLY',
                              workspacesIdentityDeploymentEnabled: choice === 'enabled' ? current.workspacesIdentityDeploymentStage !== 'STATIC_ONLY' : false,
                              workspacesIdentityClientAuthorizationApproved: choice === 'enabled' ? current.workspacesIdentityClientAuthorizationApproved : false,
                            }));
                          }}
                        >
                          <option value="enabled">Enabled</option>
                          <option value="disabled">Disabled</option>
                        </select>
                        <span className="field-help">Selecting the package saves configuration only. It does not start installation.</span>
                      </label>

                      <label className="override-control">
                        <span className="field-label-title">Unsupported Client Fallback</span>
                        <select value={form.workspacesIdentityAllowDirectoryLoginFallback ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, workspacesIdentityAllowDirectoryLoginFallback: event.target.value === 'true', workspacesIdentityFallbackAcknowledged: event.target.value === 'true' ? current.workspacesIdentityFallbackAcknowledged : false }))} disabled={!workspacesIdentitySelected}>
                          <option value="false">Disabled - enforce SAML provider</option>
                          <option value="true">Enabled - permit directory login fallback</option>
                        </select>
                        <span className="field-help">Fallback may allow unsupported clients to authenticate without the SAML provider. Keep disabled by default.</span>
                      </label>

                      {form.workspacesIdentityAllowDirectoryLoginFallback && (
                        <label className="override-control override-control-warning">
                          <span className="field-label-title">Fallback Security Acknowledgement</span>
                          <select value={form.workspacesIdentityFallbackAcknowledged ? 'true' : 'false'} onChange={(event) => setForm((current) => ({ ...current, workspacesIdentityFallbackAcknowledged: event.target.value === 'true' }))} disabled={!workspacesIdentitySelected}>
                            <option value="false">Not acknowledged</option>
                            <option value="true">Acknowledged - document security exception</option>
                          </select>
                          <span className="field-help">Confirms acceptance of the documented authentication fallback exception.</span>
                        </label>
                      )}
                    </div>

                    {workspacesIdentitySelected && (
                      <>
                        <div className="identity-automation-summary">
                          <span><strong>Automatic:</strong> AWS account, Region, WorkSpaces registration code, SAML provider/role names, encrypted proxy secret container, private metadata bucket, Windows AMI, and production proxy capacity.</span>
                          <span><strong>Stage 2 proxy deployment:</strong> Approved installer plus the complete protected authproxy.cfg and generated enrollment command.</span>
                          <span><strong>Stage 3 SAML activation:</strong> Duo WorkSpaces User Access URL, SAML metadata, tested AD authentication source, approved group access, and verified email domain.</span>
                        </div>

                        <div className="override-card-grid identity-provider-settings-grid">
                          <ServerImageSelectors profile="duo-proxy" value={form.duoServerImage} onResolve={resolveDuoImage} minimumDuoVersion={form.duoMinimumProxyVersion}
                            onChange={(value, image) => setForm(current => ({ ...current, duoServerImage: value,
                              duoProxyAmiSsmParameter: image?.version ? `/surelinc/server-images/duo-proxy/${image.version}` : '/aws/service/ami-windows-latest/Windows_Server-2025-English-Full-Base',
                              duoImageInstallerEmbedded: image?.installerEmbedded === true,
                              duoImageInstallerSha256: image?.installerSha256 || '', duoImageInstallerVersion: image?.installerVersion || '' }))} />
                          <div className="override-control form-wide identity-installer-upload">
                            <span className="field-help"><strong>Step 1.</strong> {form.duoImageInstallerEmbedded ? `The selected image includes verified Duo ${form.duoImageInstallerVersion || ''}. This upload is unused for that image.` : 'Upload an installer when the selected image does not include Duo. Malware scanning and approval are required.'}</span>
                            <span className="field-label-title">Duo Authentication Proxy Installer <span className="identity-requirement identity-requirement-automatic">Managed upload</span></span>
                            <input type="file" accept=".exe,application/vnd.microsoft.portable-executable,application/x-msdownload" onChange={(event) => void handleDuoInstallerUpload(event.target.files?.[0] || null)} disabled={duoInstallerUploading || mode !== 'edit' || form.duoImageInstallerEmbedded} />
                            <span className="field-help">Used only for public bases and custom images without Duo. SURELINC computes and pins SHA-256, uploads to the central private tooling intake bucket, scans the quarantined object with GuardDuty in the tooling account, and promotes only a clean object for deployment.</span>
                            {mode !== 'edit' && <span className="field-help">Save the client before uploading.</span>}
                            {duoInstallerUploading && <span className="field-help">Uploading or waiting for malware scan results...</span>}
                            {duoInstallerUploadMessage && <span className="field-help identity-upload-success">{duoInstallerUploadMessage}</span>}
                            {form.duoInstallerFileName && (
                              <div className="identity-upload-details">
                                <span><strong>File:</strong> {form.duoInstallerFileName}{form.duoInstallerSizeBytes ? ` (${formatFileSize(form.duoInstallerSizeBytes)})` : ''}</span>
                                <span><strong>Status:</strong> {form.duoInstallerUploadStatus || 'Not uploaded'}</span>
                                {form.duoInstallerMalwareScanStatus && <span><strong>Malware scan:</strong> {form.duoInstallerMalwareScanStatus}</span>}
                                {form.duoInstallerSha256 && <span className="mono-input"><strong>SHA-256:</strong> {form.duoInstallerSha256}</span>}
                              </div>
                            )}
                            {form.duoInstallerUploadStatus === 'SCANNING' && (
                              <button className="secondary-button" type="button" onClick={() => void checkDuoInstallerStatus()} disabled={duoInstallerUploading}>Check scan status</button>
                            )}
                          </div>
                        </div>
                          <div className="override-control form-wide identity-installer-upload">
                            <span className="field-help"><strong>Step 2.</strong> Available after the approved installer and protected Duo proxy values are ready.</span>
                            <span className="field-label-title">Duo Deployment Input <span className="identity-requirement identity-requirement-required">Temporary secure storage</span></span>
                            <span className="field-help">The authproxy.cfg content is shared by all proxy servers. Store a separate Duo-generated Windows command for each proxy profile. Up to three protected profiles are transferred to the selected AWS account.</span>
                            <label>
                              <span className="field-label-title">Duo Authentication Proxy Configuration (authproxy.cfg)</span>
                              <textarea
                                className="mono-input authproxy-config-input"
                                rows={18}
                                autoComplete="off"
                                spellCheck={false}
                                value={form.duoProxyInputs[0]?.authproxyConfig ?? ''}
                                onChange={(event) => handleDuoAuthproxyConfigChange(event.target.value)}
                                disabled={duoProxyOnboardingSaving || mode !== 'edit'}
                              />
                              <span className="field-help">This exact shared content creates authproxy.cfg on every proxy. Changing it after deployment replaces every affected server and requires a fresh Duo-generated command for each replacement profile.</span>
                            </label>
                            <div className="duo-proxy-input-list">
                              {form.duoProxyInputs.map((draft, index) => {
                                const profileStatus = duoProxyProfileStatuses.find((item) => item.profileId === draft.profileId);
                                return (
                                  <section className="duo-proxy-input-card" key={draft.profileId}>
                                    <div className="duo-proxy-input-heading">
                                      <span className="field-label-title">Duo Proxy {index + 1}</span>
                                      <span className="identity-requirement identity-requirement-automatic">{draft.profileId}</span>
                                    </div>
                                    <label>
                                      <span className="field-label-title">Duo Portal Proxy Name</span>
                                      <input value={draft.portalProxyName} onChange={(event) => setForm((current) => ({ ...current, duoProxyInputs: current.duoProxyInputs.map((item) => item.profileId === draft.profileId ? { ...item, portalProxyName: event.target.value } : item) }))} placeholder={`SURELINC WorkSpaces Authentication Proxy ${index + 1}`} disabled={duoProxyOnboardingSaving || mode !== 'edit'} />
                                      <span className="field-help">Use the exact Authentication Proxy name shown in the Duo Admin Panel. This value is copied to the EC2 mapping tag.</span>
                                    </label>
                                    <label>
                                      <span className="field-label-title">Duo Generated Windows Command</span>
                                      <textarea className="mono-input" rows={3} autoComplete="off" spellCheck={false} value={draft.generatedCommand} onChange={(event) => setForm((current) => ({ ...current, duoProxyInputs: current.duoProxyInputs.map((item) => item.profileId === draft.profileId ? { ...item, generatedCommand: event.target.value } : item) }))} placeholder="Paste the exact command from Duo's Generate command button" disabled={duoProxyOnboardingSaving || mode !== 'edit'} />
                                      <span className="field-help">Required for a new profile, any EC2 replacement, or any shared authproxy.cfg change. Leave blank only for a portal-name or patch-schedule tag update.</span>
                                    </label>
                                    <div className="button-row">
                                      <button className="secondary-button" type="button" onClick={() => void handleDuoProxyOnboardingSave(draft.profileId)} disabled={duoProxyOnboardingSaving || mode !== 'edit'}>{duoProxyOnboardingSaving ? 'Storing securely...' : `Store ${draft.profileId}`}</button>
                                      {!profileStatus && form.duoProxyInputs.length > 1 && index === form.duoProxyInputs.length - 1 && (
                                        <button className="secondary-button" type="button" onClick={() => setForm((current) => {
                                          const targetCount = Math.max(1, current.duoProxyInputs.length - 1);
                                          return {
                                            ...current,
                                            duoProxyInputs: current.duoProxyInputs.slice(0, -1),
                                            duoProxyMinimumCapacity: String(targetCount),
                                            duoProxyDesiredCapacity: String(targetCount),
                                            duoProxyMaximumCapacity: String(Math.min(4, targetCount + 1)),
                                          };
                                        })} disabled={duoProxyOnboardingSaving || mode !== 'edit'}>Remove</button>
                                      )}
                                      {profileStatus && <span className="identity-upload-success">{profileStatus.status === 'AVAILABLE' ? 'Stored and available' : `${profileStatus.status}${profileStatus.assignedInstanceId ? ` · ${profileStatus.assignedInstanceId}` : ''}`}</span>}
                                    </div>
                                  </section>
                                );
                              })}
                            </div>
                            <div className="button-row duo-proxy-input-actions">
                              {form.duoProxyInputs.length < 3 && (
                                <button className="secondary-button" type="button" onClick={() => setForm((current) => {
                                  const targetCount = Math.min(3, current.duoProxyInputs.length + 1);
                                  return {
                                    ...current,
                                    duoProxyInputs: [...current.duoProxyInputs, newDuoProxyInputDraft(targetCount, current.duoProxyInputs[0]?.authproxyConfig ?? DUO_AUTHPROXY_CONFIG_TEMPLATE)],
                                    duoProxyMinimumCapacity: String(targetCount),
                                    duoProxyDesiredCapacity: String(targetCount),
                                    duoProxyMaximumCapacity: String(Math.min(4, targetCount + 1)),
                                    duoDeploymentPlanReady: false,
                                    duoDeploymentPlanMissingCommands: `proxy-${targetCount}`,
                                  };
                                })} disabled={duoProxyOnboardingSaving || mode !== 'edit'}>Add another proxy configuration</button>
                              )}
                              <button className="secondary-button" type="button" onClick={() => void handleDuoProxyOnboardingStatus()} disabled={duoProxyOnboardingSaving || mode !== 'edit'}>Check stored input</button>
                              {form.duoProxyInputs.length > 1 && (
                                <button className="secondary-button" type="button" onClick={() => void handleDuoProxyRetireHighest()} disabled={duoProxyOnboardingSaving || mode !== 'edit'}>Retire highest proxy</button>
                              )}
                              <span className={form.duoProxyOnboardingConfigured ? 'identity-upload-success' : 'field-help'}>{form.duoProxyOnboardingConfigured ? 'One or more profiles stored and ready' : 'Not stored'}</span>
                            </div>
                            {duoDeploymentPlan && (
                              <div className="identity-automation-summary">
                                <span><strong>Deployment plan:</strong> {duoDeploymentPlan.operation || 'NO_CHANGE'}</span>
                                <span>{duoDeploymentPlan.summary || 'No proxy changes are staged.'}</span>
                                <span><strong>Auto Scaling capacity:</strong> {duoDeploymentPlan.minimumCapacity ?? Number(form.duoProxyMinimumCapacity)} / {duoDeploymentPlan.desiredCapacity ?? Number(form.duoProxyDesiredCapacity)} / {duoDeploymentPlan.maximumCapacity ?? Number(form.duoProxyMaximumCapacity)} (minimum / desired / maximum). CloudFormation is the only capacity owner.</span>
                                {(duoDeploymentPlan.readyForDeployment ?? duoDeploymentPlan.readyForApproval) === false && (
                                  <span className="identity-requirement identity-requirement-required"><strong>Not ready for deployment:</strong> store fresh commands for {(duoDeploymentPlan.freshCommandRequiredProfileIds ?? []).join(', ') || 'the required proxy profiles'}.</span>
                                )}
                              </div>
                            )}
                            {duoProxyOnboardingMessage && <span className="field-help identity-upload-success">{duoProxyOnboardingMessage}</span>}
                            {mode !== 'edit' && <span className="field-help">Save the client and deploy the static identity stack before storing onboarding content.</span>}
                          </div>


                          <div className="form-wide identity-saml-section">
                            <div className="identity-stage-heading">
                              <span className="field-label-title">WorkSpaces SAML Activation <span className="identity-requirement identity-requirement-required">Step 3</span></span>
                              <span className="field-help">Supply the values once. The deployment workflow creates storage, promotes staged metadata, deploys proxies, and advances SAML configuration in dependency order.</span>
                            </div>

                            <div className="identity-saml-grid">
                              <label className="override-control">
                                <span className="field-label-title">WorkSpaces User Access URL <span className="identity-requirement identity-requirement-required">Stage 3</span></span>
                                <input type="url" value={form.duoUserAccessUrl} onChange={(event) => setForm((current) => ({ ...current, duoUserAccessUrl: event.target.value }))} placeholder="https://..." />
                                <span className="field-help">Copy the HTTPS URL from the Duo Amazon WorkSpaces application.</span>
                              </label>

                              <label className="override-control">
                                <span className="field-label-title">SAML Metadata Source <span className="identity-requirement identity-requirement-required">Stage 3</span></span>
                                <select
                                  value={form.duoMetadataSourceMode}
                                  onChange={(event) => {
                                    const mode = event.target.value as DuoMetadataSourceMode;
                                    setForm((current) => ({
                                      ...current,
                                      duoMetadataSourceMode: mode,
                                      duoMetadataUrl: mode === 'https_url' ? current.duoMetadataUrl : '',
                                      duoMetadataS3Uri: mode === 's3_uri' ? current.duoMetadataS3Uri : mode === 'managed_upload' ? current.duoMetadataS3Uri : '',
                                      duoMetadataStagedS3Uri: mode === 'managed_upload' ? current.duoMetadataStagedS3Uri : '',
                                      duoMetadataStagedVersionId: mode === 'managed_upload' ? current.duoMetadataStagedVersionId : '',
                                      duoMetadataObjectVerified: mode === 'https_url',
                                      duoMetadataObjectStatus: mode === 'https_url' ? 'HTTPS_SOURCE' : mode === 'managed_upload' && current.duoMetadataStagedS3Uri ? 'STAGED' : '',
                                    }));
                                  }}
                                >
                                  <option value="managed_upload">Managed upload (recommended)</option>
                                  <option value="s3_uri">Existing private S3 URI</option>
                                  <option value="https_url">Approved Duo HTTPS metadata URL</option>
                                </select>
                                <span className="field-help">Managed upload validates and stores reviewed XML with encryption and versioning.</span>
                              </label>

                              {form.duoMetadataSourceMode === 'managed_upload' && (
                                <label className="override-control">
                                  <span className="field-label-title">Upload Duo SAML Metadata XML <span className="identity-requirement identity-requirement-automatic">Automatic storage</span></span>
                                  <input type="file" accept=".xml,application/xml,text/xml,application/samlmetadata+xml" onChange={(event) => void handleDuoMetadataUpload(event.target.files?.[0] || null)} disabled={duoMetadataUploading || mode !== 'edit'} />
                                  <span className="field-help">Upload the XML from Duo. The workflow creates managed storage first when needed, then resumes automatically.</span>
                                  {duoMetadataUploading && <span className="field-help">Uploading and validating metadata...</span>}
                                  {duoMetadataUploadMessage && <span className="field-help identity-upload-success">{duoMetadataUploadMessage}</span>}
                                  {(form.duoMetadataStagedS3Uri || form.duoMetadataS3Uri) && (
                                    <input
                                      className="mono-input"
                                      value={form.duoMetadataStagedS3Uri || form.duoMetadataS3Uri}
                                      readOnly
                                      aria-label="Managed SAML metadata storage URI"
                                    />
                                  )}
                                  {form.duoMetadataObjectStatus === 'STAGED' && (
                                    <span className="field-help">Staged securely in the tooling account. It will be promoted automatically after the client metadata bucket is created.</span>
                                  )}
                                </label>
                              )}

                              {form.duoMetadataSourceMode === 's3_uri' && (
                                <label className="override-control">
                                  <span className="field-label-title">Existing SAML Metadata S3 URI <span className="identity-requirement identity-requirement-override">Override</span></span>
                                  <input className="mono-input" value={form.duoMetadataS3Uri} onChange={(event) => setForm((current) => ({ ...current, duoMetadataS3Uri: event.target.value, duoMetadataStagedS3Uri: '', duoMetadataStagedVersionId: '', duoMetadataSha256: '', duoMetadataObjectVerified: false, duoMetadataObjectStatus: '' }))} placeholder="s3://private-bucket/path/duo-metadata.xml" />
                                </label>
                              )}

                              {form.duoMetadataSourceMode === 'https_url' && (
                                <label className="override-control">
                                  <span className="field-label-title">SAML Metadata HTTPS URL <span className="identity-requirement identity-requirement-override">Override</span></span>
                                  <input type="url" value={form.duoMetadataUrl} onChange={(event) => setForm((current) => ({ ...current, duoMetadataUrl: event.target.value, duoMetadataSha256: '', duoMetadataObjectVerified: true, duoMetadataObjectStatus: 'HTTPS_SOURCE' }))} placeholder="https://...duosecurity.com/..." />
                                </label>
                              )}

                              {form.duoMetadataSha256 && (
                                <label className="override-control">
                                  <span className="field-label-title">Metadata SHA-256 <span className="identity-requirement identity-requirement-automatic">Automatic</span></span>
                                  <input className="mono-input" value={form.duoMetadataSha256} readOnly />
                                  <span className="field-help">Read-only checksum used to verify the uploaded SAML metadata has not changed before deployment.</span>
                                </label>
                              )}

                              <div className="override-control">
                                <span className="field-label-title">Automatic Duo runtime validation</span>
                                <strong>Calculated by deployment</strong>
                                <span className="field-help">The platform validates proxy service health, configuration, metadata integrity, and WorkSpaces SAML state during the ordered deployment workflow. Manual readiness attestations are not required.</span>
                              </div>
                            </div>
                          </div>


                        <details className="identity-advanced-settings">
                          <summary>Advanced settings (optional and overrides)</summary>
                          <p className="field-help">Defaults are production-ready. Change these values only for a documented client requirement.</p>
                          <div className="override-card-grid identity-provider-settings-grid">
                            <label className="override-control">
                              <span className="field-label-title">Duo Edition</span>
                              <select value={form.duoEdition} onChange={(event) => setForm((current) => ({ ...current, duoEdition: event.target.value as DuoEditionChoice }))}>
                                <option value="ESSENTIALS">Essentials</option>
                                <option value="ADVANTAGE">Advantage - recommended</option>
                                <option value="PREMIER">Premier</option>
                              </select>
                              <span className="field-help">Records the licensed Duo feature tier for this AWS account.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Licensed Duo Users</span>
                              <input type="number" min="0" value={form.duoLicensedUserCount} onChange={(event) => setForm((current) => ({ ...current, duoLicensedUserCount: event.target.value }))} />
                              <span className="field-help">Number of Duo users licensed for this deployment.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Test WorkSpaces User</span>
                              <input value={form.workspacesIdentityTestUserName} onChange={(event) => setForm((current) => ({ ...current, workspacesIdentityTestUserName: event.target.value }))} placeholder="Optional" />
                              <span className="field-help">Optional account used for end-to-end readiness validation.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Duo MSP Subaccount ID</span>
                              <input value={form.duoSubaccountId} onChange={(event) => setForm((current) => ({ ...current, duoSubaccountId: event.target.value }))} placeholder="Optional tracking value" />
                              <span className="field-help">Optional MSP tenant identifier used for client tracking.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Duo API Hostname</span>
                              <input value={form.duoApiHostname} onChange={(event) => setForm((current) => ({ ...current, duoApiHostname: event.target.value }))} placeholder="Only for API-based Duo features" />
                              <span className="field-help">Duo tenant hostname used by API-based integrations, when required.</span>
                            </label>
                            <label className="override-control form-wide">
                              <span className="field-label-title">Permitted Email Domains</span>
                              <input value={form.duoPermittedEmailDomains} onChange={(event) => setForm((current) => ({ ...current, duoPermittedEmailDomains: event.target.value }))} placeholder="Leave blank for automatic detection" />
                              <span className="field-help">Email domains allowed to authenticate through the Duo application.</span>
                              <span className="field-help">Detected: {inferredDuoEmailDomains(form).join(', ') || 'none'}.</span>
                            </label>
                            <label className="override-control form-wide">
                              <span className="field-label-title">Proxy Onboarding Secret ARN Override</span>
                              <input value={form.duoProxyEnrollmentSecretArn} onChange={(event) => setForm((current) => ({ ...current, duoProxyEnrollmentSecretArn: event.target.value }))} placeholder="Leave blank to use the stack-managed secret" />
                              <span className="field-help">Uses an existing client secret instead of the stack-managed proxy secret.</span>
                            </label>
                            <label className="override-control form-wide">
                              <span className="field-label-title">AD Service Account Secret ARN</span>
                              <input value={form.duoServiceAccountSecretArn} onChange={(event) => setForm((current) => ({ ...current, duoServiceAccountSecretArn: event.target.value }))} placeholder="Leave blank for Integrated authentication" />
                              <span className="field-help">Optional directory bind secret; leave blank for integrated authentication.</span>
                            </label>
                            <label className="override-control form-wide">
                              <span className="field-label-title">Installer Source</span>
                              <select value={form.duoInstallerSourceMode} onChange={(event) => {
                                const sourceMode = event.target.value as DuoInstallerSourceMode;
                                setForm((current) => ({
                                  ...current,
                                  duoInstallerSourceMode: sourceMode,
                                  duoInstallerUploadStatus: sourceMode === 'managed_upload' ? current.duoInstallerUploadStatus : '',
                                  duoInstallerMalwareScanStatus: sourceMode === 'managed_upload' ? current.duoInstallerMalwareScanStatus : '',
                                  duoInstallerS3Uri: sourceMode === 'managed_upload' ? current.duoInstallerS3Uri : '',
                                  duoInstallerSha256: sourceMode === 'managed_upload' ? current.duoInstallerSha256 : '',
                                }));
                              }}>
                                <option value="managed_upload">Managed upload (recommended)</option>
                                <option value="s3_uri">Existing approved S3 object</option>
                              </select>
                              <span className="field-help">Choose managed upload or an existing approved S3 object.</span>
                            </label>
                            {form.duoInstallerSourceMode === 's3_uri' && (
                              <>
                                <label className="override-control form-wide">
                                  <span className="field-label-title">Installer S3 URI Override</span>
                                  <input className="mono-input" value={form.duoInstallerS3Uri} onChange={(event) => setForm((current) => ({ ...current, duoInstallerS3Uri: event.target.value }))} placeholder="s3://approved-bucket/duo/duoauthproxy.exe" />
                                  <span className="field-help">The client proxy role must be able to read this exact object. This override does not bypass live access and checksum validation.</span>
                                </label>
                                <label className="override-control form-wide">
                                  <span className="field-label-title">Installer SHA-256 Override</span>
                                  <input className="mono-input" value={form.duoInstallerSha256} onChange={(event) => setForm((current) => ({ ...current, duoInstallerSha256: event.target.value }))} placeholder="64-character SHA-256 digest" />
                                </label>
                              </>
                            )}
                            <label className="override-control">
                              <span className="field-label-title">Minimum Proxy Version</span>
                              <input value={form.duoMinimumProxyVersion} onChange={(event) => handleDuoInfrastructureChange({ duoMinimumProxyVersion: event.target.value })} placeholder="6.5.1" />
                              <span className="field-help">Lowest Duo Authentication Proxy version accepted during validation.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">EC2 Instance Type</span>
                              <select value={form.duoProxyInstanceType} onChange={(event) => handleDuoInfrastructureChange({ duoProxyInstanceType: event.target.value })}>
                                {DUO_PROXY_INSTANCE_TYPES.map((instanceType) => (
                                  <option key={instanceType} value={instanceType}>{instanceType}{instanceType === 't3.small' ? ' - default' : ''}</option>
                                ))}
                              </select>
                              <span className="field-help">x86 burstable compute for each Windows proxy. t3.small is the cost-conscious default; t3.medium or larger is preferred for heavier use.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Root Volume (GiB)</span>
                              <input type="number" min="40" value={form.duoProxyRootVolumeGib} onChange={(event) => handleDuoInfrastructureChange({ duoProxyRootVolumeGib: event.target.value })} />
                              <span className="field-help">Encrypted system disk size for each proxy instance.</span>
                            </label>
                            <label className="override-control form-wide">
                              <span className="field-label-title">Windows AMI SSM Parameter</span>
                              <input className="mono-input" value={form.duoProxyAmiSsmParameter} readOnly />
                              <span className="field-help">Resolved image parameter. Select the image in Server images above; saving pins its immutable version.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Minimum Capacity</span>
                              <input type="number" min="1" max="3" value={form.duoProxyMinimumCapacity} readOnly />
                              <span className="field-help">Matches desired capacity so the persistent fleet remains fixed.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Desired Capacity</span>
                              <input type="number" min="1" max="3" value={form.duoProxyDesiredCapacity} readOnly />
                              <span className="field-help">Matches the number of stored proxy configurations. Add or retire a proxy configuration to change it.</span>
                            </label>
                            <label className="override-control">
                              <span className="field-label-title">Maximum Capacity</span>
                              <input type="number" min="2" max="4" value={form.duoProxyMaximumCapacity} readOnly />
                              <span className="field-help">Automatically remains one higher than desired so exactly one replacement candidate can launch.</span>
                            </label>
                          </div>
                        </details>
                      </>
                    )}
                  </section>

                  <details className="identity-workflow-step identity-readiness-details">
                    <summary className="identity-workflow-step-header identity-collapsible-summary">
                      <span className="identity-workflow-step-number">2</span>
                      <div>
                        <strong>Validate readiness</strong>
                        <span className="field-help">The portal calculates configuration readiness automatically. Live AWS and provider preflight checks still run before deployment.</span>
                      </div>
                    </summary>

                    <div className="identity-readiness-panel" aria-live="polite">
                      <div className="identity-readiness-header">
                        <div>
                          <strong>{workspacesIdentitySelectedStageReady ? 'Selected deployment stage is ready for live validation' : 'Complete the requirements for the selected deployment stage'}</strong>
                          <span className="field-help">Static resources, proxy deployment, and SAML activation are validated independently.</span>
                        </div>
                        <div className="identity-readiness-summary">
                          <span className={workspacesIdentityBlockedCount ? 'status-pill status-pill-error' : 'status-pill status-pill-success'}>{workspacesIdentityBlockedCount} blocked</span>
                          <span className={workspacesIdentityPendingCount ? 'status-pill status-pill-warning' : 'status-pill status-pill-success'}>{workspacesIdentityPendingCount} pending</span>
                          <span className={workspacesIdentityWarningCount ? 'status-pill status-pill-warning' : 'status-pill status-pill-success'}>{workspacesIdentityWarningCount} warning</span>
                        </div>
                      </div>
                      <div className="identity-readiness-grid">
                        {workspacesIdentityReadiness.map((check) => (
                          <div key={check.id} className={`identity-readiness-check identity-readiness-${check.status.toLowerCase()}`}>
                            <span className={`status-pill ${check.status === 'PASS' ? 'status-pill-success' : check.status === 'BLOCKED' ? 'status-pill-error' : 'status-pill-warning'}`}>{check.status}</span>
                            <div>
                              <strong>{check.label}</strong>
                              <p>{check.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </details>

                  <div className="identity-workflow-note">
                    <strong>Deployment authorization is implicit for SURELINC administrators.</strong>
                    <span className="field-help">
                      {editingAccount
                        ? 'Use Update AWS Account Configuration to save this account, then run Deploy from its registry row. The backend computes and executes every currently possible stage without a separate approval control.'
                        : 'Use Update Client to save the complete primary-account configuration, then run Deploy Software Update. The backend computes and executes every currently possible stage without a separate approval control.'}
                    </span>
                  </div>
                </div>
              </details>
            </div>
          </fieldset>
          )}

          <div className="form-actions form-wide button-row search-action-row client-form-actions">
            <button className="primary-button" type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : editingAccount
                  ? 'Update AWS Account Configuration'
                  : mode === 'edit'
                    ? 'Update Organization Profile'
                    : 'Create Organization and Send Invite'}
            </button>
            {!editingAccount && <button className="secondary-button" type="button" onClick={resetForm} disabled={saving}>Clear Form</button>}
          </div>
        </form>
        {success && <p className="success-text">{success}</p>}
        {error && <p className="error-text">{error}</p>}
          </article>
        </div>
      )}

      {packagesClient && (
        <ClientPackageRegistryDialog
          client={packagesClient}
          packages={clientPackages}
          search={packageSearch}
          metadata={packageMetadata}
          loading={clientPackagesLoading}
          error={clientPackagesError}
          downloadingPackageKey={downloadingPackageKey}
          onSearchChange={setPackageSearch}
          onSearch={handlePackageSearch}
          onClear={handlePackageClear}
          onClose={() => setPackagesClient(null)}
          onPageChange={handlePackagePageChange}
          onPackageDownload={handlePackageDownload}
        />
      )}

      {usersClient && (
        <ClientUserManagement
          client={usersClient}
          onClose={() => setUsersClient(null)}
        />
      )}

      <article className="card">
        <h3>Search clients</h3>
        <form className="form-grid jobs-search-grid" onSubmit={handleSearch}>
          <label>
            Organization slug or name
            <input value={search.clientId} onChange={(event) => setSearch({ ...search, clientId: event.target.value })} placeholder="surelinc" />
          </label>
          <label>
            Client account ID
            <input value={search.clientAccountId} onChange={(event) => setSearch({ ...search, clientAccountId: event.target.value })} placeholder="123456789012" />
          </label>
          <label>
            Lifecycle
            <select value={search.lifecycle} onChange={(event) => setSearch({ ...search, lifecycle: event.target.value })}>
              <option value="">All organization statuses</option>
              {LIFECYCLE_OPTIONS.map((lifecycle) => <option key={lifecycle} value={lifecycle}>{lifecycle}</option>)}
            </select>
          </label>
          <label>
            CMMC Level
            <select value={search.cmmcLevel} onChange={(event) => setSearch({ ...search, cmmcLevel: event.target.value })}>
              <option value="">All levels</option>
              {CMMC_LEVEL_OPTIONS.map((level) => <option key={level} value={level}>{level}</option>)}
            </select>
          </label>
          <label>
            Search text
            <input value={search.search} onChange={(event) => setSearch({ ...search, search: event.target.value })} placeholder="email, account, pipeline, region" />
          </label>
          <label>
            Search mode
            <select value={search.searchMode} onChange={(event) => setSearch({ ...search, searchMode: event.target.value as SearchMode })}>
              <option value="contains">Contains</option>
              <option value="exact">Exact word</option>
              <option value="beginsWith">Begins with</option>
            </select>
          </label>
          <label>
            Limit
            <input type="number" min="1" max="200" value={search.limit} onChange={(event) => setSearch({ ...search, limit: Number(event.target.value) || 25 })} />
          </label>
          <div className="form-actions form-wide button-row search-action-row">
            <button className="primary-button" type="submit" disabled={loading}>{loading ? 'Searching...' : 'Search'}</button>
            <button className="secondary-button" type="button" onClick={handleClear} disabled={loading}>Clear</button>
          </div>
        </form>
      </article>

      <article className="card client-registry-card">
        <div className="grid-header-row client-registry-header-row">
          <div>
            <h3>Organization and AWS Account Registry</h3>
            <p className="muted-text">Organizations remain visible even before an AWS account is registered. Each account row is an independent configuration, authorization, evidence, and workflow target.</p>
            {lastRunResult && (
              <div className="muted-text compact-status-line">
                <p>Last run request: queued {lastRunResult.queuedCount}, skipped {lastRunResult.skippedCount}.</p>
                {(lastRunResult.skippedAccounts ?? lastRunResult.skippedClients)?.slice(0, 3).map((item) => (
                  <p key={`${item.clientId || 'client'}-${item.accountId || 'account'}-${item.reason || 'skipped'}`}>
                    Skipped {item.clientId || 'client'}{item.accountId ? ` / ${item.accountId}` : ''}: {item.reason || 'Not queued.'}
                  </p>
                ))}
              </div>
            )}
          </div>
          <div className="registry-header-actions">
            {metadata && <span className="result-count">{accountRows.length} AWS account row{accountRows.length === 1 ? '' : 's'} from {metadata.count ?? clients.length} organization{(metadata.count ?? clients.length) === 1 ? '' : 's'} · Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>}
            <button className="secondary-button" type="button" onClick={openCreateClient}>New Organization</button>
            <button className="secondary-button software-update-button" type="button" onClick={() => void handleRunAllActiveClients('software')} disabled={queueingAll !== null || loading || clientAccountsLoading}>
              {queueingAll === 'software' ? ORCHESTRATION_RUN_OPTIONS.software.queueingLabel : ORCHESTRATION_RUN_OPTIONS.software.allLabel}
            </button>
            <button className="success-button evidence-only-button" type="button" onClick={() => void handleRunAllActiveClients('evidence')} disabled={queueingAll !== null || loading || clientAccountsLoading}>
              {queueingAll === 'evidence' ? ORCHESTRATION_RUN_OPTIONS.evidence.queueingLabel : ORCHESTRATION_RUN_OPTIONS.evidence.allLabel}
            </button>
          </div>
        </div>
        {(loading || clientAccountsLoading) && <p>Loading organizations and managed AWS accounts...</p>}
        {error && <p className="error-text">{error}</p>}
        {clientAccountsError && clientAccountsError !== error && <p className="error-text">{clientAccountsError}</p>}
        {!loading && !clientAccountsLoading && clients.length === 0 && <p>No organizations found.</p>}
        {!loading && !clientAccountsLoading && clients.length > 0 && accountRows.length === 0 && <p className="error-text">The listed organizations do not have managed AWS accounts yet.</p>}

        {metadata && !loading && !error && (metadata.hasPreviousPage || metadata.hasNextPage) && (
          <div className="pagination-row">
            <button className="secondary-button" type="button" disabled={!metadata.hasPreviousPage || loading} onClick={() => void loadClients(search, Math.max(1, page - 1))}>Previous</button>
            <span className="muted-text">Page {metadata.page ?? page}{metadata.pageCount ? ` of ${metadata.pageCount}` : ''}</span>
            <button className="secondary-button" type="button" disabled={!metadata.hasNextPage || loading} onClick={() => void loadClients(search, page + 1)}>Next</button>
          </div>
        )}
        {accountRows.length > 0 && (
          <div className="table-wrap client-registry-table-wrap">
            <table className="interactive-table jobs-table client-registry-table client-account-registry-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>AWS Account</th>
                  <th>Account Name</th>
                  <th>Organization Status</th>
                  <th>Account Status</th>
                  <th>Workflow Status</th>
                  <th>CMMC</th>
                  <th>Region</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accountRows.map((row) => {
                  const clientId = String(row.client.clientId || '').trim();
                  if (!row.account) {
                    return (
                      <tr key={`${clientId}:organization`} className={selectedClientId === clientId && !selectedClientAccountId ? 'selected-row' : ''} onClick={() => setSelectedClient(row.client)}>
                        <td><span className="mono-cell">{clientId}</span><br /><span className="muted-text">{row.client.companyName || row.client.clientName || '—'}</span></td>
                        <td><span className="status-pill status-pill-warning">Not registered</span></td>
                        <td>—</td>
                        <td><span className={statusPillClassName(clientStatus(row.client))}>{clientStatus(row.client)}</span></td>
                        <td>—</td>
                        <td>—</td>
                        <td>—</td>
                        <td>—</td>
                        <td>{formatDate(row.client.updatedAt || row.client.createdAt)}</td>
                        <td>
                          <div className="table-action-stack account-registry-actions">
                            <Link className="primary-button table-action-button" to={`/clients/${encodeURIComponent(clientId)}/accounts`} onClick={(event) => event.stopPropagation()}>Register Account</Link>
                            <button className="secondary-button table-action-button" type="button" onClick={(event) => { event.stopPropagation(); editClient(row.client); }}>Organization Profile</button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const accountId = String(row.account.accountId || '').trim();
                  const scopeKey = clientAccountScopeKey(clientId, accountId);
                  const runnable = isActiveClient(row.client) && String(row.account.accountStatus || '').toUpperCase() === 'ACTIVE';
                  const workflowState = accountWorkflowState(row.client, accountId);
                  return (
                    <tr key={scopeKey} className={selectedClientId === clientId && selectedClientAccountId === accountId ? 'selected-row' : ''} onClick={() => setSelectedClient(clientRecordForAccountConfiguration(row.client, row.account!))}>
                      <td><span className="mono-cell">{clientId}</span><br /><span className="muted-text">{row.client.companyName || row.client.clientName || '—'}</span></td>
                      <td className="mono-cell">{accountId}{row.account.isPrimary ? <span className="status-pill status-pill-info account-primary-pill">Primary</span> : null}</td>
                      <td>{row.account.displayName || '—'}</td>
                      <td><span className={statusPillClassName(clientStatus(row.client))}>{clientStatus(row.client)}</span></td>
                      <td><span className={statusPillClassName(row.account.accountStatus)}>{row.account.accountStatus}</span></td>
                      <td><WorkflowStatusBadges client={{ workflowState } as ClientRecord} /></td>
                      <td>{value(row.account.cmmcLevel)}</td>
                      <td>{value(row.account.region)}</td>
                      <td>{formatDate(row.account.updatedAt || row.account.createdAt)}</td>
                      <td>
                        <div className="table-action-stack account-registry-actions">
                          <button className="primary-button table-action-button" type="button" onClick={(event) => { event.stopPropagation(); void configureAccount(row.client, accountId); }}>Configure</button>
                          <button className="secondary-button table-action-button" type="button" disabled={!runnable || runningAccountActionKey !== null} onClick={(event) => { event.stopPropagation(); void runAccountWorkflow(row, 'full'); }}>{runningAccountActionKey === `${scopeKey}:full` ? 'Queueing…' : 'Full'}</button>
                          <button className="secondary-button table-action-button" type="button" disabled={!runnable || runningAccountActionKey !== null} onClick={(event) => { event.stopPropagation(); void runAccountWorkflow(row, 'software'); }}>{runningAccountActionKey === `${scopeKey}:software` ? 'Queueing…' : 'Deploy'}</button>
                          <button className="secondary-button table-action-button" type="button" title="Troubleshoot this account's deployment with CloudFormation rollback disabled. Resources may remain billable after failure." disabled={!runnable || runningAccountActionKey !== null} onClick={(event) => { event.stopPropagation(); void runAccountWorkflow(row, 'software', true); }}>{runningAccountActionKey === `${scopeKey}:software:no-rollback` ? 'Queueing…' : 'Deploy (No Rollback)'}</button>
                          <button className="success-button table-action-button" type="button" disabled={!runnable || runningAccountActionKey !== null} onClick={(event) => { event.stopPropagation(); void runAccountWorkflow(row, 'evidence'); }}>{runningAccountActionKey === `${scopeKey}:evidence` ? 'Queueing…' : 'Evidence'}</button>
                          <Link className="secondary-button table-action-button" to={`/clients/${encodeURIComponent(clientId)}/accounts`} onClick={(event) => event.stopPropagation()}>Accounts</Link>
                          {row.account.isPrimary && <button className="secondary-button table-action-button" type="button" onClick={(event) => { event.stopPropagation(); editClient(row.client); }}>Organization Profile</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </article>

      {selectedClient && (
        <ClientDetails
          client={selectedClient}
          packageSummary={selectedClientAccountId
            ? packageSummaries[clientAccountScopeKey(String(selectedClient.clientId || ''), selectedClientAccountId)]
            : undefined}
          packageSummaryLoading={packageSummaryLoading}
          packageSummaryError={packageSummaryError}
          onEdit={editClient}
          onConfigureAccount={(client, accountId) => { void configureAccount(client, accountId); }}
          onSetStatus={handleSetClientStatus}
          statusUpdating={statusUpdatingClientId === selectedClient.clientId}
        />
      )}
    </div>
  );
}
